/**
 * Quote Builder embed — Phase 2 (script-tag).
 *
 * Lives at /public/embed.js after `npm run build:embed`. Merchants drop:
 *
 *   <div id="quote-builder"></div>
 *   <script
 *     src="https://your-app.example.com/embed.js"
 *     data-shop="my-shop"
 *     data-target="#quote-builder"
 *     data-mode="inline"></script>
 *
 * Plain DOM, no framework, no external deps. The form is mounted inside a
 * Shadow DOM so the merchant's theme CSS can't bleed in (and our CSS can't
 * bleed out). When the embed runs inside a Shopify storefront it picks up
 * the storefront's cart + product search "for free" — those endpoints
 * (/cart.js, /cart/add.js, /search/suggest.json, /products.json) are same-
 * origin and need no credentials.
 *
 * Sub-phases (per the build spec):
 *   2.1 — inline mode, shop_config branding, /api/quote-requests submit
 *   2.2 — Shopify cart import + export
 *   2.3 — modal + drawer modes
 *   2.4 — JS config object (window.QuoteBuilderConfig), /settings/embed docs
 *
 * All four ship in this build.
 */

// esbuild --define replaces this at build time. Use a literal fallback for
// the dev path so tsc + node both stay happy.
declare const EMBED_VERSION: string;
const VERSION = (typeof EMBED_VERSION !== "undefined" ? EMBED_VERSION : "dev");

// Vercel Blob's client-side direct-upload helper. The function is
// tiny (~10KB minified) and handles multipart uploads, progress
// callbacks, and round-trips to our /api/embed/upload-token handler
// for the auth token. We deliberately don't roll our own — Vercel's
// internals (chunk size, retry behavior, content-type negotiation)
// would be a maintenance burden.
import { uploadPresigned as blobUpload } from "@vercel/blob/client";
import { fetchEmbedShopConfigWithVersion, readCachedShopConfig, writeCachedShopConfig } from "@/lib/embed-shop-config";
import { resolveWizardSteps, type WizardStepId } from "@/lib/embed-step-order";
import type { FormSectionId, DescribeSectionConfig, CustomFieldType, LegacyCustomFieldType } from "@/lib/shop-config-types";
import { canonicalCustomFieldType, DEFAULT_FILE_UPLOAD } from "@/lib/shop-config-types";
import { BUNDLES_ENABLED } from "@/lib/feature-flags";
import { regionConfigForCountry, allowedCountries, postalLabelForCountry } from "@/lib/region-data";
import { isFieldVisible } from "@/lib/form-field-order";
import { pairRowsForStep, resolveFormLayout, validateFormState } from "@/lib/form-render-core";
import type { ValidateBuiltinValues } from "@/lib/form-render-core";
import { isValidEmail, isValidPhone } from "@/lib/contact-validation";
import {
  resolveFormDesignTokens, scaleSpacing,
  type ResolvedFormDesignTokens,
} from "@/lib/form-design-tokens";
import { applyCatalogRules } from "./catalog-rules";
import { cartContentsMatch } from "./cart-sync";
import {
  normalizePreviewOptions,
  type PreviewCartItem,
  type RenderPreviewOptions,
} from "./preview";
import {
  autoPlaceProductButton,
  injectCardAddToQuote,
  resolveProductButtonConfig,
  CARD_ATQ_BTN_CLASS,
  type AddToQuotePayload,
} from "./product-button";
import { resolveSeedProperties } from "./option-capture";
import { renderItemOptionsHtml } from "./render-item-options";
import { itemUnitPriceWithOptions, optionsPriceDelta, cartTotalWithOptions } from "./item-price";
import { readConfiguredCustomer } from "./customer-config";
import { resolveLiveVariantId } from "./variant-resolver";
import { suppressFloatingWidgets, restoreFloatingWidgets } from "./floating-widgets";
import { BasketStore, basketItemsToPrefill, type BasketItem, type BasketLineProperty } from "./basket";
import { parseHexColor, resolveDividerColor } from "./color-contrast";

// ── Types ─────────────────────────────────────────────────────────────────
// Mirror the shapes the embed actually reads — kept loose so the bundle
// doesn't need to import @/lib/* (those imports would pull in pg, redis,
// etc. and blow the size budget). Exception: shop-config-types.ts itself
// has zero imports — it's a pure leaf module — and the embed already pulls
// types AND runtime functions (isFieldVisible, validateFormState, ...) from
// @/lib/*, so the legacy→canonical mapping is imported for real rather than
// hand-rolled a second time. One mapping, every consumer.
type EmbedCustomFieldType = CustomFieldType | LegacyCustomFieldType;

/** `#0f172a` / `#fd0` → `rgba(15,23,42,0.08)`. Used for the selected
 *  choice-card fill.
 *
 *  Deliberately NOT CSS `color-mix()`: the embed runs in whatever browser
 *  the customer brought, and an unsupported color-mix() drops the whole
 *  declaration — the selected card would lose its fill silently. Falls back
 *  to a neutral slate tint for a non-hex accent (a merchant could have
 *  saved `rebeccapurple`), which still reads as "selected" alongside the
 *  accent border + indicator. */
function accentTint(accent: string, alpha = 0.08): string {
  const rgb = parseHexColor(accent);
  if (!rgb) return `rgba(15,23,42,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

type EmbedCustomFieldStep = "about" | "finish";
interface EmbedShowWhenRule {
  field_id: string;
  operator: "equals" | "not_equals" | "in" | "not_in";
  value: string | string[];
}
interface EmbedCustomField {
  id: string;
  label: string;
  helpText?: string;
  type: EmbedCustomFieldType;
  step: EmbedCustomFieldStep;
  required?: boolean;
  options?: { id: string; label: string }[];
  /** `file` only — per-question upload limits. UI AFFORDANCE ONLY: the
   *  server re-derives every one of these from its own config before it
   *  mints a token (resolveUploadRules in src/lib/file-upload.ts). A
   *  tampered bundle buys nothing. */
  max_files?: number;
  max_file_size_mb?: number;
  allowed_types?: string[];
  display_order?: number;
  column_width?: "full" | "half" | "third";
  show_when?: EmbedShowWhenRule;
}
interface EmbedBuiltinFieldConfig {
  required?: boolean;
  helper?: string;
  label?: string;
  step?: "about" | "shipping" | "finish";
  display_order?: number;
  column_width?: "full" | "half" | "third";
  show_when?: EmbedShowWhenRule;
}

// Built-in field rendering metadata — kept for the embed's own field
// renderer (builtinField()). The ordering / pairing / validation logic
// has moved to @/lib/form-render-core (imported above) so these
// constants are no longer duplicating layout logic.
type BuiltinKey =
  | "first_name" | "last_name" | "email" | "phone" | "company"
  | "preferred_contact" | "address" | "street_address_2" | "city" | "country" | "state" | "zip";
const BUILTIN_LABELS: Record<BuiltinKey, string> = {
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  phone: "Phone",
  company: "Company name",
  preferred_contact: "Preferred contact",
  address: "Street address",
  street_address_2: "Apt / Suite / Unit",
  city: "City",
  country: "Country",
  state: "State",
  zip: "ZIP",
};
const BUILTIN_DEFAULTS_REQUIRED: Record<BuiltinKey, boolean> = {
  first_name: true, last_name: true, email: true, phone: true,
  company: false, preferred_contact: false,
  address: true, street_address_2: false, city: true, country: true, state: true, zip: true,
};

type OrderedEmbedField =
  | { kind: "builtin"; key: BuiltinKey; order: number; column_width: "full" | "half" | "third"; show_when?: EmbedShowWhenRule }
  | { kind: "custom"; field: EmbedCustomField; order: number; column_width: "full" | "half" | "third"; show_when?: EmbedShowWhenRule };
interface ShopConfig {
  brand_name?: string;
  brand_short?: string;
  logo_url?: string | null;
  primary_color?: string;
  accent_color?: string;
  // Form body/canvas background (fix #2, form-width-and-contrast). Cards,
  // inputs, header, step bar, and footer stay white regardless — only the
  // body behind them tints. Falls back to FORM_BG_DEFAULT when unset.
  // Mirrors ShopConfig in src/lib/shop-config-types.ts.
  form_bg_color?: string;
  // Form design tokens (Phase 2c). Optional; resolved via
  // resolveFormDesignTokens() from @/lib/form-design-tokens (dep-free).
  // Mirrors ShopConfig in src/lib/shop-config-types.ts.
  form_font_family?: string;
  form_size?: string;
  form_button_radius?: string;
  form_density?: string;
  support_email?: string;
  // "Powered by QuoteSense" badge (tier-gating Lane 3 §4) — server-computed
  // entitlement flag (branding.remove_badge), read verbatim off the public
  // shop-config payload. NO tier logic lives in this bundle: the embed
  // just renders (or doesn't) based on this one boolean.
  show_powered_by?: boolean;
  // Legacy lists — kept for backwards-compat; new shops use custom_fields.
  facility_types?: string[];
  service_options?: { id: string; label: string }[];
  custom_fields?: EmbedCustomField[];
  quote_validity_days?: number;
  bundles?: Bundle[];
  notes_placeholder?: string;
  // Section toggles + copy overrides (all optional; defaults inline).
  require_shipping?: boolean;
  about_you_label?: string;
  shipping_label?: string;
  build_quote_label?: string;
  finish_up_label?: string;
  notes_label?: string;
  submit_button_label?: string;
  // Optional note under the Build-step / quick-form subtotal. Empty →
  // hidden (form-polish-v3 #5c). Mirrors ShopConfig in shop-config-types.ts.
  subtotal_note?: string;
  confirmation_headline?: string;
  confirmation_body?: string;
  // Confirmation "what happens next" block (#6). Heading + three steps;
  // each optional, blank → today's default copy. Step 2's default
  // interpolates response_time_text; a merchant override is rendered
  // verbatim. Mirrors ShopConfig in src/lib/shop-config-types.ts.
  next_steps_heading?: string;
  next_step_1?: string;
  next_step_2?: string;
  next_step_3?: string;
  response_time_text?: string;
  trust_signal_text?: string;
  // Optional consent checkbox on the Finish step. consent_enabled gates the
  // whole feature; consent_required (default true when enabled)
  // makes ticking the box mandatory; consent_text is the label
  // (rendered HTML-escaped). Off everywhere by default.
  consent_enabled?: boolean;
  consent_required?: boolean;
  consent_text?: string;
  /** Per-step settings (label, intro, shipping enable, notes/submit
   *  label). Mirrors FormStepsConfig in src/lib/shop-config-types.ts;
   *  inlined because the embed bundle can't import @/lib. */
  form_steps?: {
    about?:    { label?: string; intro?: string };
    shipping?: { label?: string; intro?: string; enabled?: boolean };
    build?:    { label?: string; search_label?: string; search_placeholder?: string };
    finish?:   { label?: string; intro?: string; notes_label?: string; submit_label?: string };
  };
  /** Merchant overrides for built-in inputs. Mirrors
   *  Partial<Record<BuiltinFieldKey, BuiltinFieldConfig>>. */
  builtin_fields?: Partial<Record<BuiltinKey, EmbedBuiltinFieldConfig>>;
  // Multi-form (docs/MULTI_FORM.md §4.3). The RESOLVED form's ordered
  // section list + Describe-step copy. Forwarded by toPublicShopConfig()
  // on the single resolved form. Absent → today's hardcoded step order.
  // Types imported from Lane 1's schema (consume, don't redefine).
  section_order?: FormSectionId[];
  describe?: DescribeSectionConfig;
  // Customer-side file attachments. Mirrors the FileUploadConfig in
  // src/lib/shop-config-types.ts. When `enabled` is false (or the
  // whole block is absent) the embed hides the upload UI entirely
  // and the public upload-token endpoint rejects requests.
  file_upload?: {
    enabled: boolean;
    max_files: number;
    max_file_size_mb: number;
    allowed_types: string[];
    helper_text?: string;
  };
  // Storefront-drawer quick-submit flow. Mirrors ShopConfig.quick_form
  // in src/lib/shop-config-types.ts. Resolved via resolveQuickForm()
  // (inlined below) before each render so defaults stay in one place.
  quick_form?: {
    enabled?: boolean;
    show_phone?: boolean;
    show_email?: boolean;
    show_qty?: boolean;
    show_message?: boolean;
    submit_label?: string;
    intro?: string;
    allow_multi_product?: boolean;
    phone_required?: boolean;
    email_required?: boolean;
    /** Quick form's OWN consent checkbox — independent from the
     *  shared consent_enabled/required/text above. Unset falls back
     *  to the shared triplet (resolveQuickForm below), mirroring
     *  src/lib/form-steps.ts's resolveQuickForm. */
    consent_enabled?: boolean;
    consent_required?: boolean;
    consent_text?: string;
    /** Quick form's own accent/button color. Unset falls back to
     *  this instance's already-resolved accent (see
     *  resolveQuickAccent()) — no visual change unless set. */
    accent_color?: string;
    /** Copy for the "switch to the full form" escalation link. Unset
     *  falls back to a sensible default. Mirrors
     *  src/lib/shop-config-types.ts's quick_form.escalation_text. */
    escalation_text?: string;
    /** Show/hide the whole escalation line. Default true. */
    show_escalation?: boolean;
    /** @deprecated Read from top-level builder_url. Kept on the
     *  embed-side mirror only so a stale-cache config blob written
     *  before the split still resolves. */
    builder_url?: string;
    /** @deprecated Read from top-level builder_form_style. */
    builder_form_style?: "transparent" | "bordered";
    /** The quick form's one optional binary (Yes/No) question. Mirrors
     *  QuickFormCustomField in src/lib/shop-config-types.ts. */
    custom_field?: {
      enabled?: boolean;
      id: string;
      label: string;
      help_text?: string;
      required?: boolean;
      true_label?: string;
      false_label?: string;
    };
  };
  /** Top-level builder-page settings. Public-safe; flows through
   *  the public shop-config endpoint to the embed. Resolver below
   *  prefers these over the legacy nested quick_form.builder_*
   *  values during the migration window. */
  builder_url?: string;
  builder_form_style?: "transparent" | "bordered";
  /** Inventory awareness. block_out_of_stock gates customer adds
   *  on availableForSale=false variants; block_discontinued gates
   *  product-tagged discontinued items. discontinued_tag is for
   *  display only here (server-computed `discontinued` flag is
   *  what gates rendering); keeping it in the type so admin
   *  preview helpers can echo what the merchant configured.
   *  inventory_strict is the legacy single switch — kept in the
   *  type for one release so older bundle caches that still read
   *  it keep working; new code reads the split pair via
   *  resolvedGates() below. */
  discontinued_tag?: string;
  inventory_strict?: boolean;
  block_out_of_stock?: boolean;
  block_discontinued?: boolean;
  /** Product-page auto-place button. Mirrors ProductButtonConfig in
   *  src/lib/shop-config-types.ts. OFF unless auto_place_enabled ===
   *  true; when on, boot() calls autoPlaceProductButton() (see
   *  src/embed/product-button.ts) to inject the button next to
   *  Add-to-Cart on product pages. */
  product_button?: {
    auto_place_enabled?: boolean;
    label?: string;
    bg_color?: string;
    text_color?: string;
    /** "form" (default) opens the request form; "add_to_quote" adds
     *  the product to the standalone Quote Basket (requires
     *  basket.enabled). See src/embed/product-button.ts. */
    mode?: "form" | "add_to_quote";
    open_mode?: "quick" | "full";
    display_mode?: "drawer" | "modal";
    target_selector?: string;
    position?: "after" | "before";
    style_mode?: "color" | "outlined" | "theme_button" | "theme_inverted" | "custom_class";
    button_class?: string;
    width?: "full" | "auto";
    shape?: "rounded" | "pill" | "square" | "inherit";
    font_mode?: "inherit" | "system";
  };
  /** Quote Basket (P1-core). OFF unless `enabled === true`. When on,
   *  boot() unifies quote items onto the standalone basket store (the
   *  single source of truth across every surface), lets
   *  product_button.mode="add_to_quote" stage items into it, and mounts a
   *  slim side-anchored "View Quote" edge tab as the re-entry into the
   *  embedded multi-step form — the one review/submit surface. There is
   *  no standalone basket drawer, floating bubble, or cart-adjacent
   *  count. Mirror of BasketConfig in src/lib/shop-config-types.ts. */
  basket?: {
    enabled?: boolean;
    /** Label on the "View Quote" edge tab. Default "View Quote". */
    tab_label?: string;
    /** Edge-tab background color. Defaults to the brand accent. */
    tab_color?: string;
    /** Viewport edge the tab affixes to. Default "right". */
    tab_side?: "right" | "left";
    /** Show the edge tab. Default true. */
    show_tab?: boolean;
  };
}

/** Resolved quick-form config — every field defaulted. Mirror of
 *  resolveQuickForm() in src/lib/form-steps.ts; the embed can't
 *  import @/lib without pulling pg/redis into the bundle. */
interface ResolvedQuickForm {
  enabled: boolean;
  show_phone: boolean;
  show_email: boolean;
  show_qty: boolean;
  show_message: boolean;
  submit_label: string;
  intro: string;
  allow_multi_product: boolean;
  phone_required: boolean;
  email_required: boolean;
  builder_url: string;
  builder_form_style: "transparent" | "bordered";
  /** Quick form's OWN consent checkbox. Falls back to the shared
   *  consent_enabled/required/text when the quick-specific field is
   *  unset — mirrors resolveQuickForm() in src/lib/form-steps.ts. */
  consent_enabled: boolean;
  consent_required: boolean;
  consent_text: string;
  /** Copy for the "switch to the full form" escalation link. Falls
   *  back to a sensible default when unset. */
  escalation_text: string;
  /** Show/hide the whole escalation line. Default true. */
  show_escalation: boolean;
  /** The quick form's one optional binary (Yes/No) question, with
   *  defaults applied — null when unconfigured. Mirrors
   *  resolveQuickFormCustomField() in src/lib/form-steps.ts. */
  custom_field: ResolvedQuickFormCustomField | null;
}
interface ResolvedQuickFormCustomField {
  enabled: boolean;
  id: string;
  label: string;
  help_text: string;
  required: boolean;
  true_label: string;
  false_label: string;
}
// Mirrors FORM_SLUG_RE in src/lib/shop-config-types.ts.
const QUICK_FIELD_ID_RE = /^[a-z0-9][a-z0-9-]*$/;
function resolveQuickFormCustomField(config: ShopConfig | undefined): ResolvedQuickFormCustomField | null {
  const raw = config?.quick_form?.custom_field;
  if (!raw || typeof raw !== "object") return null;
  const id = typeof raw.id === "string" ? raw.id.trim().toLowerCase() : "";
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  if (!id || !QUICK_FIELD_ID_RE.test(id) || !label) return null;
  return {
    enabled: raw.enabled === true,
    id,
    label,
    help_text: typeof raw.help_text === "string" ? raw.help_text.trim() : "",
    required: raw.required === true,
    true_label: (typeof raw.true_label === "string" && raw.true_label.trim()) ? raw.true_label.trim() : "Yes",
    false_label: (typeof raw.false_label === "string" && raw.false_label.trim()) ? raw.false_label.trim() : "No",
  };
}
function resolveBuilderUrl(config: ShopConfig | undefined): string {
  // Top-level wins; legacy quick_form.builder_url is the back-compat
  // fallback. Trim + require leading "/" so a half-set value (the
  // input-type validator on /settings/forms/builder enforces this too)
  // doesn't ride through as a no-op.
  const top = typeof config?.builder_url === "string" ? config.builder_url.trim() : "";
  if (top.startsWith("/")) return top;
  const nested = typeof config?.quick_form?.builder_url === "string"
    ? config.quick_form.builder_url.trim() : "";
  return nested.startsWith("/") ? nested : "";
}
function resolveBuilderFormStyle(config: ShopConfig | undefined): "transparent" | "bordered" {
  const top = config?.builder_form_style;
  if (top === "transparent" || top === "bordered") return top;
  const nested = config?.quick_form?.builder_form_style;
  if (nested === "transparent" || nested === "bordered") return nested;
  return "bordered";
}
function resolveQuickForm(qf: ShopConfig["quick_form"] | undefined, config?: ShopConfig): ResolvedQuickForm {
  const q = qf || {};
  const consentEnabled = typeof q.consent_enabled === "boolean"
    ? q.consent_enabled
    : config?.consent_enabled === true;
  const consentRequiredRaw = typeof q.consent_required === "boolean"
    ? q.consent_required
    : config?.consent_required;
  const consentText = (typeof q.consent_text === "string" && q.consent_text.trim())
    ? q.consent_text
    : ((typeof config?.consent_text === "string" && config.consent_text.trim())
        ? config.consent_text
        : "I agree to be contacted about my quote request.");
  return {
    enabled:      q.enabled !== false,
    show_phone:   q.show_phone !== false,
    show_email:   q.show_email !== false,
    show_qty:     q.show_qty !== false,
    show_message: q.show_message !== false,
    submit_label: (typeof q.submit_label === "string" && q.submit_label.trim()) ? q.submit_label : "Request quote",
    intro:        typeof q.intro === "string" ? q.intro : "",
    allow_multi_product: q.allow_multi_product !== false,
    phone_required: q.phone_required === true,
    email_required: q.email_required === true,
    // Top-level-first lookup; falls back to legacy nested copy
    // (q.builder_*) for back-compat with shops whose cached config
    // still has the old shape.
    builder_url: resolveBuilderUrl(config),
    builder_form_style: resolveBuilderFormStyle(config),
    // consent_required defaults true when enabled, mirroring the full
    // form's own `consent_required !== false` semantics.
    consent_enabled: consentEnabled,
    consent_required: typeof consentRequiredRaw === "boolean" ? consentRequiredRaw : true,
    consent_text: consentText,
    escalation_text: (typeof q.escalation_text === "string" && q.escalation_text.trim())
      ? q.escalation_text.trim()
      : "Need shipping or a longer request?",
    show_escalation: q.show_escalation !== false,
    custom_field: resolveQuickFormCustomField(config),
  };
}

/** Form mode passed in from window.QuoteApp.openRequest(). Theme app
 *  extension's product-page button passes either "quick_expandable"
 *  (default) or "full"; the floating CTA never passes one (which
 *  defaults to "full" since there's no prefilled product to gate on). */
type FormMode = "quick_expandable" | "full";

// An attachment in the embed: the bookkeeping for one file the
// customer added. Starts in `uploading`; transitions to `done` once
// the Vercel Blob upload returns a URL, or `error` if anything failed.
// Only `done` files end up in the submit payload.
interface Attachment {
  id: string;            // local-only uuid for keying
  file: File;            // the original File object — kept for retries
  filename: string;
  size_bytes: number;
  content_type: string;
  status: "uploading" | "done" | "error";
  progress: number;      // 0..1
  error?: string;
  blob_url?: string;
  blob_pathname?: string;
  /** The `file` custom-field this upload answers. Absent = the generic
   *  catch-all drop zone on the Finish step. One Attachment pipeline for
   *  both; the tag is the only difference. */
  fieldId?: string;
}
interface Bundle {
  id: string; name: string; description: string;
  imageUrl?: string;
  lineItems: { variantId: string; qty: number }[];
  isActive: boolean;
}
interface CartItem {
  variantId: string;
  productId?: string;
  title: string;
  variantTitle?: string;
  sku: string;
  qty: number;
  price: string;        // dollar string, e.g. "1299.00"
  vendor: string;
  imageUrl?: string | null;
  // ── Inventory awareness (V1) ───────────────────────────────────
  // Populated from /api/public/product/* responses. The embed
  // renders Out of stock / Backorder / Discontinued badges based
  // on these. All optional — older cart entries persisted before
  // V1 just don't render the badge layer.
  availableForSale?: boolean;
  inventoryPolicy?: "DENY" | "CONTINUE";
  inventoryQuantity?: number | null;
  discontinued?: boolean;
  // ── Custom line-item options (Quote Basket) ────────────────────
  // Per-line custom options (e.g. engraving, monogram) carried from
  // the basket into the form's line items and onto the submit
  // payload. Shared JSON contract with the server route lane:
  // Array<{name, value}>. Absent on cart-sync / manual line items.
  properties?: BasketLineProperty[];
}
interface MerchantConfig {
  shop?: string;
  apiOrigin?: string;   // override autodetect (useful for previewing other envs)
  onSubmit?: (quoteRequestId: number) => void;
  customStyles?: { primaryColor?: string; accentColor?: string };
  /** Logged-in customer passed in from the theme via Liquid.
   *  Required on stores using Shopify's new Customer Account API
   *  (the legacy /account.json endpoint redirects to a CORS-blocked
   *  cross-origin host on those stores, so client-side detection
   *  isn't possible without OAuth). The merchant pastes a small
   *  {% if customer %}{% endif %} block in their theme that
   *  populates window.QuoteBuilderConfig.customer with the same
   *  shape /account.json used to return. See /settings/embed docs. */
  customer?: {
    id: string | number;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string | null;
    default_address?: {
      address1?: string;
      city?: string;
      province_code?: string;
      province?: string;
      zip?: string;
    } | null;
  };
}

// ── Init ──────────────────────────────────────────────────────────────────
// `document.currentScript` is null when the embed is loaded async or via
// dynamic import. Capture it synchronously at script eval time.
const SCRIPT_EL: HTMLScriptElement | null =
  (document.currentScript as HTMLScriptElement | null) ||
  // Fallback — find a script tag pointing at this bundle.
  Array.from(document.scripts).find(s => /\/embed\.js(\?|$)/.test(s.src)) ||
  null;

const merchantConfig: MerchantConfig =
  (typeof window !== "undefined" && (window as any).QuoteBuilderConfig) || {};

function attr(name: string, fallback = ""): string {
  return (SCRIPT_EL?.getAttribute(name) || "").trim() || fallback;
}

const SHOP_SLUG = (() => {
  // Theme app extension's app-embed.liquid uses
  // `data-shop="{{ shop.permanent_domain }}"`, which surfaces as
  // `<shop>.myshopify.com`. Our backend tenant key is the bare
  // slug (matches shops.id). Strip the suffix here so every
  // downstream call — /api/public/shop-config, /api/quote-requests,
  // /api/quote-requests/draft, /api/embed/upload-token — receives a
  // value that passes our strict slug regex
  // (/^[a-z0-9][a-z0-9-]*$/). The server-side endpoints normalize
  // too as defense-in-depth, but fixing the source at the embed
  // boundary keeps log lines + diagnostics readable.
  const raw = (merchantConfig.shop || attr("data-shop") || "default").trim().toLowerCase();
  return raw.endsWith(".myshopify.com")
    ? raw.slice(0, -".myshopify.com".length)
    : raw;
})();

// Origin we'll talk to. Autodetect from the script's own URL so the embed
// works whether it's hosted at quotes.app.com or a Vercel preview URL.
const API_ORIGIN: string = (() => {
  if (merchantConfig.apiOrigin) return merchantConfig.apiOrigin.replace(/\/+$/, "");
  if (SCRIPT_EL?.src) {
    try {
      const u = new URL(SCRIPT_EL.src);
      return `${u.protocol}//${u.host}`;
    } catch { /* fall through */ }
  }
  return ""; // same-origin fallback (only useful in local dev)
})();

const MODE = (attr("data-mode") || "inline").toLowerCase() as "inline" | "modal" | "drawer" | "builder";
const TARGET_SELECTOR = attr("data-target") || "#quote-builder";

// `data-preview="1"` — the admin form designer loads this bundle purely to
// call `QuoteApp.renderPreview()`. It is NOT a storefront. Boot still exposes
// the global API (that's the whole point) but skips every storefront side
// effect: the shop-config / catalog-rules network calls, the auto-place
// button, the basket activation, the auto-mount scan, and the DOM observer.
// Without this the designer would fire a config fetch for a shop slug it
// never passed and hang a MutationObserver on the admin's body.
const PREVIEW_ONLY = attr("data-preview") === "1";

// Optional legacy-snippet override: `data-form-mode="quick" | "full"`.
// Mirrors the theme-block setting so merchants on the manual install
// path get the same control. Precedence in openRequest():
//   1. explicit opts.formMode on the call wins
//   2. SCRIPT_FORM_MODE (this attribute) is the next fallback
//   3. default per display kind — drawer → full, modal → quick
//      (matches the theme-block defaults so behavior is consistent
//      across surfaces).
const SCRIPT_FORM_MODE: FormMode | null = (() => {
  const raw = attr("data-form-mode").toLowerCase();
  if (raw === "quick" || raw === "quick_expandable") return "quick_expandable";
  if (raw === "full") return "full";
  return null;
})();
function defaultFormModeFor(kind: "modal" | "drawer"): FormMode {
  return kind === "modal" ? "quick_expandable" : "full";
}

// Optional multi-form launcher id (docs/MULTI_FORM.md §6 / Lane 2):
// `data-form-id="<slug>"` on the embed <script> tag picks which form
// this storefront loads by default. Mirrors data-form-mode — the
// manual-install fallback for the theme-block `data-qa-form-id` setting
// that Phase 2 will wire. Precedence in openRequest():
//   1. explicit opts.formId on the call wins
//   2. SCRIPT_FORM_ID (this attribute) is the next fallback
//   3. unset → the shop's default form (byte-identical to today)
// Only a slug-shaped value passes; anything else falls through to the
// default form (the server's resolveForm() is the final authority).
const SCRIPT_FORM_ID: string | null = (() => {
  const raw = attr("data-form-id").toLowerCase();
  return /^[a-z0-9][a-z0-9-]*$/.test(raw) && raw !== "default" ? raw : null;
})();

const PRIMARY_OVERRIDE =
  merchantConfig.customStyles?.primaryColor ||
  attr("data-primary-color") ||
  "";
const ACCENT_OVERRIDE =
  merchantConfig.customStyles?.accentColor ||
  attr("data-accent-color") ||
  "";

// Form size/scale (#3). The theme block's "form size" setting writes
// data-form-scale on the embed script (manual installs) OR rides a
// per-mount / per-open override (theme blocks — see mountInline /
// openRequest). Bounded so a merchant typo can't blow up the layout;
// default 1.0 means "no scaling" and buildCss emits no zoom rule, so
// the rendered CSS is byte-stable for every shop that never touches it.
const FORM_SCALE_MIN = 0.9;
const FORM_SCALE_MAX = 1.4;
function clampFormScale(raw: number | string | null | undefined): number {
  const n = typeof raw === "number" ? raw : parseFloat(String(raw ?? ""));
  if (!isFinite(n) || n <= 0) return 1;
  return Math.min(FORM_SCALE_MAX, Math.max(FORM_SCALE_MIN, n));
}
const FORM_SCALE = clampFormScale(attr("data-form-scale"));

// ── Helpers ───────────────────────────────────────────────────────────────
// Loosely typed — accepts arbitrary string keys for data-*/aria-* attrs,
// "class"/"style"/"html" shorthands, and on* event handlers. Strict typing
// here costs us more friction than it buys, since the embed manipulates raw
// DOM and the keys are validated by `setAttribute` at runtime.
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, any> = {},
  ...children: (Node | string | null | undefined)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(props as any)) {
    if (v == null) continue;
    if (k === "class") (node as any).className = v;
    else if (k === "style") (node as any).setAttribute("style", v);
    else if (k === "html") (node as any).innerHTML = v;
    else if (k.startsWith("on") && typeof v === "function") (node as any)[k.toLowerCase()] = v;
    else if (k in node) (node as any)[k] = v;
    else (node as any).setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c == null) continue;
    node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

function $(root: ParentNode, sel: string) { return root.querySelector(sel) as HTMLElement | null; }
function $$(root: ParentNode, sel: string) { return Array.from(root.querySelectorAll(sel)) as HTMLElement[]; }

function fmtUSD(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** Default form body/canvas tint when the merchant hasn't set
 *  form_bg_color. Light neutral — distinct enough from white that cards/
 *  inputs (which stay white) visibly pop against it. */
const FORM_BG_DEFAULT = "#f4f5f7";

/** Neutral placeholder glyph for product thumbnails with no image —
 *  a simple line-art box, tinted via `currentColor` on the empty thumb. */
const THUMB_BOX_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8L12 3 3 8v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5M12 13v8"/></svg>';

/** Clean an untrusted line-item properties array (from a prefill payload
 *  or the basket) down to real {name, value} pairs — drops blanks and
 *  malformed entries. Shared shape with the server route lane. */
function normalizeLineProperties(raw: any): BasketLineProperty[] {
  if (!Array.isArray(raw)) return [];
  const out: BasketLineProperty[] = [];
  for (const p of raw) {
    if (!p || typeof p !== "object") continue;
    const name = typeof p.name === "string" ? p.name.trim() : "";
    const value = p.value == null ? "" : String(p.value).trim();
    if (!name || !value) continue;
    out.push({ name, value });
  }
  return out;
}

/** The live storefront `window`/`document` for option-capture.ts's
 *  DOM-facing reads — `undefined` outside a browser (never happens in
 *  practice; boot() already bails when `document` is undefined, but
 *  this keeps the helper safe to call from anywhere). */
function currentWinDoc(): { win: any; doc: any } {
  return {
    win: typeof window !== "undefined" ? window : undefined,
    doc: typeof document !== "undefined" ? document : undefined,
  };
}

/** Item-seeding chokepoint #1: api.openRequest()'s single-item prefill.
 *  `opts.properties` (an explicit caller array, or auto-place's own
 *  click-time capture threaded through) always wins untouched; absent,
 *  and only when a product/variant id is actually being seeded, this
 *  falls through to a fresh page capture (option-capture.ts) — covers
 *  the app-block/manual-snippet doors that never build their own
 *  properties array. No product id → `[]`, no DOM touched. */
function resolveOpenRequestProperties(
  opts: { variantId?: string | number; productId?: string | number; properties?: Array<{ name: string; value: string }> } | undefined,
): BasketLineProperty[] {
  const hasProductContext = !!(opts?.variantId || opts?.productId);
  return normalizeLineProperties(
    resolveSeedProperties({ hasProductContext, existing: opts?.properties, ...currentWinDoc() }),
  );
}

/** Map a basket line → the form's CartItem view. The basket store is the
 *  unified item source of truth; each mounted builder holds state.cartItems
 *  as a VIEW over it (seeded on mount, re-synced on the store's subscribe).
 *  BasketItem is a structural superset — this just fills CartItem's
 *  required-string slots and carries inventory + properties so badges /
 *  submit-gate survive the round-trip. */
function basketItemToCartItem(it: BasketItem): CartItem {
  const out: CartItem = {
    variantId: it.variantId,
    title: it.title || "Product",
    variantTitle: it.variantTitle || "",
    sku: it.sku || "",
    qty: Math.max(1, Math.floor(Number(it.qty)) || 1),
    price: it.price != null ? String(it.price) : "0",
    vendor: it.vendor || "",
    imageUrl: it.imageUrl ?? null,
  };
  if (it.productId != null && String(it.productId)) out.productId = String(it.productId);
  if (typeof it.availableForSale === "boolean") out.availableForSale = it.availableForSale;
  if (it.inventoryPolicy === "DENY" || it.inventoryPolicy === "CONTINUE") out.inventoryPolicy = it.inventoryPolicy;
  if (it.inventoryQuantity != null) out.inventoryQuantity = it.inventoryQuantity;
  if (typeof it.discontinued === "boolean") out.discontinued = it.discontinued;
  const props = normalizeLineProperties(it.properties);
  if (props.length) out.properties = props;
  return out;
}

// (An `embedIsCustomFilled` copy used to live here. It was already dead —
// validate() routes through the shared validateFormState() — and it could
// only ever be wrong about `file`, whose answer lives in the attachment
// list, not the values map. One definition: isCustomFilled() in
// @/lib/form-render-core.)

// Shopify storefront detection. The cart APIs / search/suggest only exist
// when we're embedded inside a Shopify theme; we hide cart-sync UI when
// we're not. Cheap check: `Shopify` global or a Shopify-shaped cart.js.
async function detectShopifyStorefront(): Promise<boolean> {
  if (typeof (window as any).Shopify !== "undefined") return true;
  try {
    const r = await fetch("/cart.js", { method: "GET", credentials: "same-origin" });
    if (!r.ok) return false;
    const d = await r.json().catch(() => null);
    return !!(d && typeof d === "object" && "items" in d);
  } catch { return false; }
}

// ── Shopify cart sync (2.2) ───────────────────────────────────────────────
async function fetchShopifyCart(): Promise<CartItem[]> {
  try {
    const r = await fetch("/cart.js", { credentials: "same-origin" });
    if (!r.ok) return [];
    const d = await r.json();
    return (d.items || []).map((it: any): CartItem => ({
      variantId: String(it.variant_id || it.id || ""),
      productId: String(it.product_id || ""),
      title: it.product_title || it.title || "",
      variantTitle: it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : "",
      sku: it.sku || "",
      qty: it.quantity || 1,
      // Shopify cart prices are in cents
      price: ((it.final_price ?? it.price ?? 0) / 100).toFixed(2),
      vendor: it.vendor || "",
      imageUrl: it.featured_image?.url || it.image || null,
    }));
  } catch { return []; }
}

/** Structured result of a send-to-cart attempt. The caller maps this
 *  to a humanized, restore-aware message (never the raw error). */
type CartPushResult = {
  ok: boolean;
  /** Add failed but the shopper's PRIOR cart contents were put back. */
  restored?: boolean;
  /** The failure looked like an out-of-stock / unavailable line (422). */
  soldOut?: boolean;
  /** Product name(s) Shopify named in the 422 ("…'The Out of Stock
   *  Snowboard' is already sold out."). Empty when unparseable —
   *  the caller falls back to a generic sold-out sentence. */
  soldOutNames?: string[];
  /** Raw error string — for logs/debugging only, NOT shown to the user. */
  error?: string;
};

/** Pull the quoted product name(s) out of a Shopify /cart/add.js 422
 *  body. Shopify phrases it "The product 'NAME' is already sold out.";
 *  the name is single-quoted (JSON uses double quotes, so single-quoted
 *  tokens are unambiguous here). Returns a de-duped list, [] if none. */
function parseSoldOutNames(body: string): string[] {
  if (!body) return [];
  const names = new Set<string>();
  // Strict: a single-quoted name immediately followed by "… sold out".
  const strict = /'([^']+)'\s+is\s+(?:already\s+)?sold\s*out/gi;
  let m: RegExpExecArray | null;
  while ((m = strict.exec(body))) names.add(m[1].trim());
  // Loose fallback: any single-quoted token when the body mentions a
  // sold-out / unavailable condition but the strict phrasing didn't match.
  if (!names.size && /sold\s*out|unavailable|out of stock/i.test(body)) {
    const loose = /'([^']+)'/g;
    while ((m = loose.exec(body))) names.add(m[1].trim());
  }
  return Array.from(names).filter(Boolean);
}

/** Replace the merchant's Shopify cart with exactly the builder's
 *  line items. Two-step: POST /cart/clear.js wipes existing
 *  contents, POST /cart/add.js {items:[...]} adds the builder's
 *  items with their quantities. "Replace" semantics — the
 *  builder is the source of truth, the cart becomes a mirror of
 *  the builder rather than an additive merge.
 *
 *  DATA-LOSS INVARIANT (🔴): a failed add must NEVER leave the
 *  shopper's cart empty. We snapshot the live cart BEFORE clearing;
 *  if the add fails (invalid variant, sold-out 422, network), we
 *  restore the snapshot so the cart returns to its prior contents.
 *  The restore re-adds the snapshot item-by-item (best-effort) so a
 *  single now-unavailable prior line can't sink the whole rollback —
 *  every still-addable item is recovered.
 *
 *  Restore fidelity: items are restored by variant id + quantity.
 *  Bespoke line-item properties / selling plans on the prior cart are
 *  not reconstructed (rare on quote carts) — noted as a known limit.
 *
 *  Empty builder → caller-side guard; we bail here too so a
 *  programmatic call can't accidentally empty the cart. */
async function pushCartToShopify(items: CartItem[]): Promise<CartPushResult> {
  if (!items.length) return { ok: false, error: "empty cart" };

  // Snapshot the CURRENT cart before we touch it, reduced to the
  // add.js shape {id, quantity}. Best-effort: a failed snapshot just
  // means an empty restore set (nothing to put back).
  let snapshot: { id: number; quantity: number }[] = [];
  try {
    const sr = await fetch("/cart.js", { credentials: "same-origin" });
    if (sr.ok) {
      const sd = await sr.json().catch(() => null);
      snapshot = (((sd && sd.items) as any[]) || [])
        .map((it: any) => ({ id: Number(it.variant_id || it.id), quantity: Number(it.quantity) || 1 }))
        .filter((it: { id: number; quantity: number }) => !!it.id);
    }
  } catch { /* snapshot best-effort */ }

  // Roll the cart back to the snapshot. Clears first (in case a partial
  // add somehow landed), then re-adds each prior line independently so
  // one unavailable item doesn't block restoring the rest. Returns true
  // if at least one prior item was put back.
  const restore = async (): Promise<boolean> => {
    if (!snapshot.length) return false;
    try {
      await fetch("/cart/clear.js", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });
    } catch { /* fall through — still try the re-adds */ }
    let restoredAny = false;
    for (const it of snapshot) {
      try {
        const rr = await fetch("/cart/add.js", {
          method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [it] }),
        });
        if (rr.ok) restoredAny = true;
      } catch { /* skip this line, keep restoring the rest */ }
    }
    return restoredAny;
  };

  try {
    // Step 1: clear existing cart contents. /cart/clear.js always
    // returns 200 with an empty-cart payload — no special-casing
    // needed for an already-empty cart. A clear failure means the
    // cart is untouched, so there's nothing to restore.
    const clearResp = await fetch("/cart/clear.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    if (!clearResp.ok) {
      const t = await clearResp.text().catch(() => "");
      return { ok: false, error: `clear: ${clearResp.status}: ${t.slice(0, 200)}` };
    }
    // Step 2: add the builder's items in a single batch call.
    const addResp = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: items.map(it => ({ id: Number(it.variantId), quantity: it.qty })),
      }),
    });
    if (!addResp.ok) {
      const t = await addResp.text().catch(() => "");
      const soldOut = addResp.status === 422 || /sold\s*out|sold_out|not enough|unavailable|out of stock/i.test(t);
      const soldOutNames = parseSoldOutNames(t);
      // The cart is empty right now (clear succeeded, batch add is
      // all-or-nothing so nothing landed). Put the prior cart back.
      const restored = await restore();
      return { ok: false, restored, soldOut, soldOutNames, error: `add: ${addResp.status}: ${t.slice(0, 200)}` };
    }
    return { ok: true };
  } catch (e: any) {
    // Network error mid-flight (possibly after the clear) — attempt a
    // restore so we never strand the shopper with an empty cart.
    const restored = await restore();
    return { ok: false, restored, error: e?.message || "cart replace failed" };
  }
}

// ── Shopify Storefront product search (2.1+) ──────────────────────────────
// Uses /search/suggest.json — public, no API key, returns localized prices,
// honors hidden / unpublished products. Falls back to /products.json on
// older themes where suggest may not be enabled.
type StorefrontProduct = {
  productId: string; title: string; vendor: string; imageUrl: string | null;
  /** Shopify URL handle (e.g. "spr1000"). Captured from suggest.json
   *  responses so the Add-click hydration fetch can hit
   *  /products/{handle}.js — that endpoint ONLY accepts handles,
   *  not numeric ids, and silently 404s on the wrong shape. */
  handle?: string;
  defaultVariantId: string;
  variants: { variantId: string; title: string; sku: string; price: string; available: boolean }[];
};

async function searchStorefront(q: string): Promise<StorefrontProduct[]> {
  if (!q.trim()) return [];
  // Try suggest.json first.
  try {
    const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`;
    const r = await fetch(url, { credentials: "same-origin" });
    if (r.ok) {
      const d = await r.json();
      const products = d?.resources?.results?.products || [];
      if (products.length) {
        return products.map((p: any): StorefrontProduct => ({
          productId: String(p.id || ""),
          title: p.title || "",
          vendor: p.vendor || "",
          imageUrl: p.image || p.featured_image || null,
          // Critical: capture the handle (or derive it from `url`
          // like "/products/spr1000") so the click-to-hydrate fetch
          // can hit /products/{handle}.js — Shopify's .js endpoint
          // only resolves handles, not numeric ids. Without this the
          // Add button silently no-ops on every suggest.json hit.
          handle: String(p.handle || (p.url ? String(p.url).split("?")[0].split("/").pop() : "") || ""),
          defaultVariantId: "",
          variants: [],
        })).filter((p: StorefrontProduct) => p.title);
      }
    }
  } catch { /* fall through */ }

  // Fallback: /products.json — supports a partial-match query via Liquid
  // search syntax. Returns full variant data we can render directly.
  try {
    const r = await fetch(`/products.json?limit=20`, { credentials: "same-origin" });
    if (!r.ok) return [];
    const d = await r.json();
    const q2 = q.toLowerCase();
    const matched = (d.products || []).filter((p: any) =>
      `${p.title} ${p.vendor} ${(p.variants || []).map((v: any) => v.sku || "").join(" ")}`
        .toLowerCase()
        .includes(q2)
    );
    return matched.slice(0, 15).map((p: any): StorefrontProduct => ({
      productId: String(p.id),
      title: p.title,
      vendor: p.vendor || "",
      imageUrl: p.images?.[0]?.src || null,
      defaultVariantId: String(p.variants?.[0]?.id || ""),
      variants: (p.variants || []).map((v: any) => ({
        variantId: String(v.id),
        title: v.title && v.title !== "Default Title" ? v.title : "",
        sku: v.sku || "",
        price: String(parseFloat(v.price) || 0),
        available: v.available !== false,
      })),
    }));
  } catch { return []; }
}

// Storefront /products/{handle}.js endpoint — full variant detail
// for hydrating a suggest.json hit on click. Shopify's .js endpoint
// ONLY resolves handles, not numeric ids; callers must pass a handle
// (e.g. "spr1000") not an id. Returns null if the fetch 404s so the
// caller can degrade gracefully.
async function loadProductDetail(handle: string): Promise<StorefrontProduct | null> {
  if (!handle) return null;
  try {
    const r = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!r.ok) return null;
    const p = await r.json();
    return {
      productId: String(p.id),
      title: p.title,
      vendor: p.vendor || "",
      imageUrl: p.featured_image || p.images?.[0] || null,
      defaultVariantId: String(p.variants?.[0]?.id || ""),
      variants: (p.variants || []).map((v: any) => ({
        variantId: String(v.id),
        title: v.public_title || (v.title && v.title !== "Default Title" ? v.title : ""),
        sku: v.sku || "",
        price: ((v.price || 0) / 100).toFixed(2),
        available: v.available !== false,
      })),
    };
  } catch { return null; }
}

// ── Shop config fetch ─────────────────────────────────────────────────────
// Thin wrapper around src/lib/embed-shop-config — that helper holds
// the three-state return shape (config | null | {}) so vitest can
// exercise it without booting up the embed's currentScript globals.
async function fetchShopConfigWithVersion(formId?: string): Promise<{ config: ShopConfig; version: number } | null> {
  const result = await fetchEmbedShopConfigWithVersion({
    apiOrigin: API_ORIGIN,
    shopSlug: SHOP_SLUG,
    formId,
  });
  if (result === null) return null;
  return { config: result.config as ShopConfig, version: result.version };
}

// ── Storefront customer detection ─────────────────────────────────────────
// Phase 2: when the embed loads on a merchant storefront with a
// logged-in customer (regular or B2B), pull their details so we can
// pre-fill the form + optionally enrich with B2B Company context.
//
// Detection order:
//   1. window.Shopify.customer — set by some themes' customer init
//      blocks. Cheap to read; might be present and might not.
//   2. /account.json — same-origin GET against the storefront.
//      Returns the logged-in customer's record (or 401/404 when
//      anonymous). This is the canonical detection — the response
//      is session-verified by the merchant's cookie, so the embed
//      can trust the id / email / phone / address.
//
// If neither yields a customer, returns null and the embed stays in
// anonymous mode.
//
// Company enrichment (a 3rd call to our own /api/embed/customer-context
// endpoint) is best-effort and silent on failure — a customer with
// no Company association just gets the regular pre-fill flow.
async function detectShopifyCustomer(): Promise<CustomerContext | null> {
  // Probe 1: Liquid-emitted customer block. Set natively by the
  // QuoteSense app embed (theme-extension installs — no merchant code
  // needed since extension v24) or hand-pasted on manual/headless
  // installs (canonical snippet on /settings/embed):
  //   window.QuoteBuilderConfig = window.QuoteBuilderConfig || {};
  //   window.QuoteBuilderConfig.customer = { id, first_name, ... };
  // Read LAZILY at detect-time (not the module-load merchantConfig
  // capture) so manual installs work with any tag order; the
  // module-load capture stays as a fallback for configs that existed
  // before the bundle loaded.
  let raw: any = readConfiguredCustomer(typeof window !== "undefined" ? window : undefined)
    || merchantConfig.customer || null;

  // Probe 2: window.Shopify.customer. Some themes (especially
  // Online Store 2.0 themes still on Classic Customer Accounts)
  // ship the customer object as a global. Accepts both snake_case
  // and camelCase keys.
  if (!raw || !raw.id) {
    const sf: any = (typeof window !== "undefined" && (window as any).Shopify) || {};
    raw = sf.customer || raw;
  }

  // Probe 3: /account.json. Still useful as a last resort on
  // legacy Classic Customer Account stores. On new-accounts stores
  // this redirects cross-origin and the fetch rejects — silent
  // fallback handles that without surfacing the error.
  if (!raw || !raw.id) {
    try {
      const r = await fetch("/account.json", { credentials: "same-origin" });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        // /account.json returns either { customer: {...} } or the
        // customer object inline depending on Shopify version.
        raw = d?.customer || d || null;
      }
    } catch { /* anonymous — silent fallback */ }
  }

  if (!raw) return null;
  const customerId = String(raw.id ?? raw.customerId ?? "").replace(/^gid:\/\/shopify\/Customer\//, "");
  if (!customerId) return null;

  const firstName = String(raw.first_name ?? raw.firstName ?? "");
  const lastName = String(raw.last_name ?? raw.lastName ?? "");
  const email = String(raw.email ?? "");
  const phone = String(raw.phone ?? "") || undefined;
  const da = raw.default_address || raw.defaultAddress || null;
  const defaultAddress = da ? {
    address1: String(da.address1 ?? da.streetLine1 ?? ""),
    city: String(da.city ?? ""),
    // Province codes come through as "NJ" / "CA". Some themes ship
    // the full province name; we accept either since the form
    // accepts free text in the state field.
    province: String(da.province_code ?? da.provinceCode ?? da.province ?? ""),
    zip: String(da.zip ?? da.postalCode ?? ""),
    // Country — kickoffs/address-country-ingestion.md. Without this the
    // Phase-2 seed below left the form's country stuck on its "US"
    // default for every non-US customer.
    countryCode: String(da.country_code ?? da.countryCode ?? ""),
  } : undefined;

  const ctx: CustomerContext = { customerId, firstName, lastName, email, phone, defaultAddress };

  // Probe 3: Company enrichment via our own backend. Anonymous
  // (rate-limited), returns Company info only — never the customer's
  // own PII (we already have that from /account.json). On miss
  // (regular customer with no Company), returns { company: null }.
  try {
    const r = await fetch(`${API_ORIGIN}/api/embed/customer-context`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({ customerId }),
    });
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      if (d?.company) ctx.company = d.company;
    }
  } catch { /* silent — regular customer experience is fine */ }

  return ctx;
}

// ── Submit ────────────────────────────────────────────────────────────────
async function submitQuoteRequest(state: any): Promise<{ ok: true; id: number; shopSeq: number | null } | { ok: false; error: string }> {
  try {
    // Phase 2: pull the Shopify customer + Company linkage from the
    // detected context (set during boot()). Empty/null on anonymous
    // submissions — backend treats every shopify_* field as optional.
    const ctx = state.customerContext as CustomerContext | null;
    const selectedLocation = ctx?.company?.locations.find(l => l.id === state.selectedLocationId)
      ?? ctx?.company?.locations[0]
      ?? null;
    // The customer "linked" to this submission is the Shopify Customer
    // we detected on the storefront — guaranteed to actually be them
    // because /account.json is session-verified, unlike the old
    // window.Shopify.checkout.customer.email opportunistic read it
    // replaces (which could be stale checkout state from a previous
    // session).

    const body: Record<string, any> = {
      shop_slug: SHOP_SLUG,
      first_name: state.firstName, last_name: state.lastName,
      email: state.email,
      phone: state.phone, company: state.company,
      preferred_contact: state.preferred,
      country: state.country, address: state.address, address2: state.address2 || "", city: state.city, state: state.state, zip: state.zip,
      // Separate billing — mirror the hosted form: always send the flag,
      // send the fields only when the customer unchecked "same as
      // shipping" so legacy/same submits stay byte-identical on disk.
      billing_same_as_shipping: state.billingSame,
      ...(state.billingSame ? {} : {
        billing_name: state.billingName, billing_address1: state.billingAddress1,
        billing_address2: state.billingAddress2 || "", billing_company: state.billingCompany,
        billing_city: state.billingCity, billing_state: state.billingState, billing_zip: state.billingZip,
        billing_country: state.billingCountry,
      }),
      line_items: state.cartItems.map((it: CartItem) => ({
        variantId: it.variantId, sku: it.sku, title: it.title,
        variantTitle: it.variantTitle || "",
        qty: it.qty, price: it.price, vendor: it.vendor,
        imageUrl: it.imageUrl || null,
        // Per-line custom options (Quote Basket). Sent whenever present
        // — the server route lane persists them; a server that hasn't
        // merged that lane yet just ignores the extra field.
        ...(Array.isArray(it.properties) && it.properties.length ? { properties: it.properties } : {}),
      })),
      // Generic merchant-defined custom field values. We also mirror the
      // seeded "facility_type" + "services" ids onto the old services
      // shape so existing rep tooling keeps working.
      services: (() => {
        const cvRaw = state.customValues || {};
        const hidden: Set<string> = new Set(Array.isArray(state.hiddenCustomFieldIds) ? state.hiddenCustomFieldIds : []);
        // Drop hidden conditional fields so stale values from
        // now-irrelevant questions don't leak into reporting.
        const cv: Record<string, any> = {};
        for (const [k, v] of Object.entries(cvRaw)) {
          if (!hidden.has(k)) cv[k] = v;
        }
        const out: Record<string, any> = { custom_fields: cv };
        if (typeof cv.facility_type === "string" && cv.facility_type) {
          out.facility_type = cv.facility_type;
        }
        if (Array.isArray(cv.services)) {
          out.services_selected = cv.services;
          out.assembly = cv.services.includes("assembly");
        }
        return out;
      })(),
      notes: state.notes,
      // Free-text description — populated when the customer used
      // describe-mode on Step 2 (toggled via "Not sure what you
      // need? Describe it instead →"). Server-side `description`
      // column, capped at 4000 chars there. Distinct from `notes`
      // — notes rides ON TOP of line items, description is the
      // alternative ask when the customer didn't pick items.
      ...(state.description && state.description.trim() ? { description: state.description.trim() } : {}),
      // Successfully-uploaded attachments. We filter to `done` so
      // half-uploaded / failed entries from the local list don't ride
      // along — the customer already saw the error inline and can
      // remove them. Server re-validates regardless.
      files: (state.attachments as Attachment[])
        .filter(a => a.status === "done" && a.blob_url && a.blob_pathname)
        .map(a => ({
          blob_url: a.blob_url!,
          blob_pathname: a.blob_pathname!,
          filename: a.filename,
          size_bytes: a.size_bytes,
          content_type: a.content_type,
          // Which question this answers. Re-validated server-side against
          // the resolved form before it reaches the INSERT.
          ...(a.fieldId ? { field_id: a.fieldId } : {}),
        })),
      // Server stamps consent_accepted_at on its own clock when this
      // is true; never trust a client-supplied timestamp. Omit when
      // false so the request body stays clean on shops that don't
      // use the feature.
      ...(state.consentAccepted ? { consent_accepted: true } : {}),
      // Analytics-only flag — server stashes on services.form_mode so
      // the dashboard can later filter quick-vs-full submissions
      // without a schema migration. Allow-list on the server side:
      // anything other than "quick" / "full" is silently dropped.
      ...(typeof state.form_mode === "string" ? { form_mode: state.form_mode } : {}),
      // Multi-form attribution (docs/MULTI_FORM.md §4.4). Slug of the
      // named form/placement that produced this submission — set on the
      // QuoteBuilder when opened with a formId. The server validates the
      // slug + snapshots form_name; absent/"default" → both columns stay
      // NULL, byte-identical to a single-form submit.
      ...(typeof state.form_id === "string" && state.form_id.trim() ? { form_id: state.form_id.trim() } : {}),
    };

    // Phase 2: append Shopify linkage when we have it. Backend
    // columns are nullable; anonymous submissions just leave these
    // off and the row stores NULL.
    if (ctx) {
      body.shopify_customer_id = ctx.customerId;
      if (ctx.company && selectedLocation) {
        body.shopify_company_id = ctx.company.id;
        body.shopify_company_location_id = selectedLocation.id;
        body.shopify_company_contact_id = ctx.company.contactId;
        body.shopify_tax_exempt = !!selectedLocation.taxExempt;
      }
    }

    const r = await fetch(`${API_ORIGIN}/api/quote-requests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) return { ok: false, error: d.error || `HTTP ${r.status}` };
    // shop_seq is the per-tenant 1..N counter the confirmation
    // screen displays. We deliberately do NOT fall back to the
    // global id (cross-tenant numbering that shouldn't leak into
    // a customer-facing surface). When the server response omits
    // shop_seq (legacy / unexpected shape), return null and the
    // caller renders confirmation without a QR-N reference.
    return {
      ok: true,
      id: d.request.id,
      shopSeq: typeof d.request.shop_seq === "number" ? d.request.shop_seq : null,
    };
  } catch (e: any) {
    return { ok: false, error: e?.message || "Submission failed" };
  }
}

// ── CSS (lives inside the shadow root, can't leak) ────────────────────────
function buildCss(
  primary: string,
  accent: string,
  scale = 1,
  tokens: ResolvedFormDesignTokens = resolveFormDesignTokens(null),
  bg: string = FORM_BG_DEFAULT,
): string {
  // #3 form size — a single CSS `zoom` on the form root scales every
  // control, label, and gap together (px values included), and because
  // qb-root is an auto-width block its used width still fills the host,
  // so content reflows inside instead of overflowing horizontally — no
  // layout break in inline, builder, modal, or drawer. Emitted only when
  // the merchant set a non-default size, so default output stays byte-
  // stable. clampFormScale already bounded `scale` to [0.9, 1.4].
  const scaleRule = scale !== 1 ? `\n    .qb-root { zoom: ${scale}; }` : "";
  // Design tokens (Phase 2c). At defaults these reproduce today's
  // literals byte-for-byte: fontStack === the Inter stack, btnRadius === 9,
  // and every sp() === its base value (multiplier 1.0). Resolved once here
  // so the embed, request page, and preview share one source of truth.
  const fontStack = tokens.fontStack;
  const btnRadius = tokens.buttonRadius ?? 9;
  const sp = (px: number) => scaleSpacing(px, tokens.spacing);
  // 8% accent wash for the selected choice card, computed here rather than
  // via CSS color-mix() — see accentTint().
  const tint = accentTint(accent);
  // Builder-mode section-divider color. Drawn inside .qb-card, which is
  // transparent — the real backdrop is .qb-shell, #fff by default (the
  // "bordered" builder_form_style; see resolveBuilderFormStyle()). A pale
  // primary against that backdrop would vanish the same way the
  // .qb-toggle-row active-state text nearly did on a pale accent, so this
  // requires WCAG 1.4.11's 3:1 non-text contrast and falls back to slate.
  const dividerColor = resolveDividerColor(primary, "#ffffff", "#0f172a");
  return `
    :host, * { box-sizing: border-box; }${scaleRule}
    .qb-root {
      font-family: ${fontStack};
      color: #0f172a;
      line-height: 1.5;
      font-size: 14px;
      width: 100%;
      /* Form body/canvas — merchant-configurable (form_bg_color, fix #2
         form-width-and-contrast), default a light neutral tint. Cards,
         inputs, header, step bar, and footer stay explicitly white with a
         defined border + faint shadow so they visibly pop against this —
         the fix for the redesign's white-on-white contrast bug. */
      background: ${bg};
    }
    /* Header strip — branded bar (fix, form-polish-header-subtotal): the
       three palette roles are now header = primary_color, body =
       form_bg_color, CTA = accent_color. White shop-name text on the
       merchant's brand primary; refined height/padding (not the old
       heavy slab). Left = logo (or accent initial chip) + shop name;
       right = neutral circular close. */
    .qb-header {
      background: ${primary};
      color: #fff;
      padding: 12px 16px;
      font-weight: 500;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid rgba(255,255,255,.14);
      box-shadow: 0 2px 4px rgba(2,6,23,.15);
    }
    .qb-header-brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
    .qb-header-name {
      font-size: 14px; font-weight: 500; color: #fff;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    /* Logo badge — a real logo renders at its NATURAL aspect ratio,
       contained (never cropped into a fixed square — that was rendering
       wide/tall logos as an unrecognizable zoomed fragment). Sized to
       its content (padding, not a fixed box) on a small white/light
       backing so a colored or dark logo still reads on the brand bar. */
    .qb-logo-badge {
      display: inline-flex; align-items: center; justify-content: center;
      height: 28px; min-width: 28px; padding: 0 6px; border-radius: 7px;
      background: #fff; flex: 0 0 auto;
      box-shadow: 0 1px 2px rgba(2,6,23,.18);
    }
    .qb-logo-badge img {
      display: block; max-height: 20px; max-width: 96px;
      width: auto; height: auto; object-fit: contain;
    }
    /* Fallback (no logo_url) — shop-initial chip. Fixed square is fine
       here since it's a single glyph, not an arbitrary-aspect image. */
    .qb-logo-chip {
      width: 24px; height: 24px; border-radius: 7px; flex: 0 0 auto;
      background: ${accent}; color: #fff; overflow: hidden;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 600; line-height: 1;
      box-shadow: 0 1px 2px rgba(2,6,23,.18);
    }
    .qb-shell { max-width: 680px; margin: 0 auto; padding: ${sp(24)}px ${sp(18)}px ${sp(24)}px; }
    .qb-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: ${sp(24)}px ${sp(26)}px; box-shadow: 0 1px 3px rgba(15,23,42,.04); }
    .qb-title { margin: 0 0 18px; font-size: 20px; font-weight: 500; letter-spacing: -.01em; color: #0f172a; }
    /* Step indicator — refined: thin rails, accent on the active step
       (bar + label), muted elsewhere. Sentence/title-case labels, no
       uppercase shout. White card surface (defined border + faint
       shadow) so it pops off the tinted body — it's a sibling of
       qb-card, not nested inside it, so it needs its own definition. */
    .qb-steps {
      display: flex; gap: 6px; margin-bottom: 22px;
      background: #fff; border: 1px solid rgba(2,6,23,.12); border-radius: 10px;
      padding: 14px 14px 10px; box-shadow: 0 1px 2px rgba(2,6,23,.05);
    }
    /* flex: 1 1 0 (NOT the flex:1 shorthand alone) + min-width: 0 forces
       every column to an equal, rock-steady quarter — flex-basis:0 keeps
       label length from seeding the initial size, and min-width:0
       overrides the flex-item default (min-width:auto, which sizes to
       content) so a longer label wraps INSIDE its column instead of
       claiming extra width from its neighbors. */
    .qb-step { flex: 1 1 0; min-width: 0; }
    /* width:100% ties the rail to its (now-equal) column so all 4 rails
       are always the same length. */
    .qb-step-bar { width: 100%; height: 3px; border-radius: 2px; background: #e2e8f0; margin-bottom: 7px; transition: background .2s; }
    .qb-step-bar.active { background: ${accent}; }
    /* Centered + free to wrap to 2 lines (e.g. "Build Your Quote")
       without affecting the column's width — the column is fixed at
       25% by .qb-step above, so wrapping only changes the row's height,
       never its width. overflow-wrap guards against a merchant-supplied
       step label (form_steps.<step>.label) with no natural break point.
       Font-weight stays constant (500) across active/done/default so the
       active highlight below is color-only and can't itself widen the
       label past its fixed column. */
    .qb-step-label {
      font-size: 11px; font-weight: 500; letter-spacing: 0; color: #94a3b8; text-align: center;
      white-space: normal; overflow-wrap: break-word;
    }
    .qb-step-label.active { color: ${accent}; }
    .qb-field { margin-bottom: ${sp(14)}px; }
    /* Container-relative, NOT viewport-relative (fix, embed-mobile-batch
       #3) — this form renders inside containers of very different widths
       (a 500px drawer on a 1440px desktop viewport, a full-width inline
       mount on a 390px phone), so an '@media (max-width: …)' breakpoint
       on VIEWPORT width was firing (or not firing) independent of how
       much room the row actually had, collapsing a roomy desktop
       drawer's pairs to 1-column while cramming a phone's inline mount
       into fixed 2-up. 'repeat(auto-fit, minmax(140px, 1fr))' sizes off
       the grid's own track width instead: 2-up whenever each column can
       clear 140px, 1-column when it can't — same container width, same
       layout, on any device. Honors a merchant's half-width pairing
       (e.g. First/Last name) on mobile instead of silently ignoring it. */
    .qb-row-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: ${sp(14)}px; margin-bottom: ${sp(14)}px; }
    .qb-row-2 .qb-field { margin-bottom: 0; }
    /* Thirds stay viewport-collapsed (below), NOT container auto-fit —
       an auto-fit floor high enough to keep a third-width input usable
       (~170px) only fits 2-across before 3-across, on the realistic
       phone/drawer container widths this form ships in (see the 320–768px
       sweep in embed-mobile-batch's HANDOFF) — an awkward "2 fields on
       one row + 1 alone on the next" reflow that reads as broken, worse
       than a clean 1-column stack. Kept explicit + full-width instead. */
    .qb-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: ${sp(12)}px; margin-bottom: ${sp(14)}px; }
    .qb-row-3 .qb-field { margin-bottom: 0; }
    @media (max-width: 600px) {
      .qb-row-3 { grid-template-columns: 1fr; gap: 0; } .qb-row-3 .qb-field { margin-bottom: ${sp(14)}px; }
    }
    .qb-label { display: block; font-size: 11.5px; font-weight: 500; color: #475569; letter-spacing: 0; margin-bottom: ${sp(6)}px; }
    /* Inputs — defined border + faint shadow (was a hairline #e2e8f0
       border with no shadow) so a white input visibly pops off the now-
       tinted form body instead of washing out. */
    .qb-input, .qb-textarea, .qb-select {
      width: 100%; padding: 11px 13px; border: 1px solid rgba(2,6,23,.14); border-radius: 9px;
      font-size: 14px; font-family: inherit; outline: none; background: #fff; color: #0f172a;
      box-shadow: 0 1px 2px rgba(2,6,23,.05);
    }
    .qb-input:focus, .qb-textarea:focus, .qb-select:focus { border-color: ${accent}; box-shadow: 0 0 0 3px ${accent}22; }
    .qb-textarea { resize: vertical; min-height: 100px; line-height: 1.5; }
    /* iOS Safari auto-zooms the whole page on focus of any input whose
       font-size is under 16px, and never zooms back out (fix,
       embed-mobile-batch #1 — was a flat 14px everywhere). iOS has been
       observed to key off both readings, so we need to clear 16px under
       either one:
         - COMPUTED style: .qb-root's 'zoom' (the merchant's form_size /
           theme-block form-scale override, ~0.5–2.0 — see 'scale' above)
           does not change computed font-size, so a literal 16px here is
           always safe on that reading regardless of scale.
         - VISUAL (post-zoom) size: zoom multiplies every descendant's
           rendered size, so at scale < 1 a computed 16px still renders
           under 16px. Dividing by 'scale' (16 / scale) fixes that: at
           scale >= 1 it would push computed size below 16 to compensate,
           which is unsafe on the computed-style reading above — so the
           value is floored at 16. Math.ceil to the nearest hundredth so
           rounding can only push the size up, never back under the
           threshold. */
    @media (max-width: 600px) {
      .qb-input, .qb-textarea, .qb-select { font-size: ${Math.ceil(Math.max(16, 16 / scale) * 100) / 100}px; }
    }
    /* Quick-form density overrides — tighten vertical so the 1-item
       case fits without scroll on a 1024x768 viewport. Scoped to
       .qb-quick-form so the full wizard's spacing stays unchanged.
       :has() selectors let us reach UP from the quick-form card to
       prune outer padding (qb-shell, qb-footer-meta) that the full
       wizard still needs at its current generosity. */
    .qb-shell:has(.qb-quick-form) { padding: 14px 16px; }
    .qb-shell:has(.qb-quick-form) > .qb-footer-meta {
      margin-top: 8px; font-size: 10px; line-height: 1.3;
    }
    .qb-quick-form.qb-card { padding: 16px 20px; }
    .qb-quick-form .qb-field { margin-bottom: 10px; }
    .qb-quick-form .qb-textarea { min-height: 56px; }
    .qb-quick-form .qb-input { padding: 9px 12px; }
    .qb-quick-form .qb-label { margin-bottom: 4px; }
    /* Quick-form CTA tray. Mounted into qb-nav-footer (a sibling
       of qb-shell inside qb-root's flex column) by the renderStep
       dispatcher — that slot already pins to the panel bottom
       via the existing nav-footer flex-shrink:0 rule, so the
       tray inherits proper panel-bottom pinning without needing
       its own sticky chain. The full wizard's Back/Next nav uses
       the same slot. We only style the tray's internal stack
       (consent above submit, full width); the qb-nav-footer
       wrapper already supplies background / border-top / padding /
       safe-area-inset. */
    .qb-quick-cta {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
    }
    /* Override qb-nav-footer's default flex layout (Back / center
       / Next is space-between) when it's hosting the quick-form
       CTA — we want the tray to stack vertically and span the
       full width. Slightly tighter vertical padding than the wizard's
       Back/Next row (fix #3, quick-form parity) — the tray stacks
       several blocks (contact/subtotal/submit/link) so a touch less
       breathing room per block reads as polished, not cramped. */
    .qb-nav-footer:has(.qb-quick-cta) {
      flex-direction: column;
      align-items: stretch;
      padding-top: 10px;
      padding-bottom: calc(10px + env(safe-area-inset-bottom));
    }
    @media (max-width: 520px) {
      /* .qb-row-2's own container-relative auto-fit (above) already
         handles this width — a second viewport-keyed collapse here
         would force 1-column even inside a roomy desktop-drawer
         container that happens to load on a narrow viewport, which is
         the exact bug fixed in embed-mobile-batch #3. */
      .qb-shell { padding: 14px 12px 14px; }
      .qb-card { padding: 18px 16px; }
      .qb-steps { display: none; }
      .qb-step-compact { display: block !important; }
    }
    /* Same white-card treatment as .qb-steps (its desktop counterpart —
       only one of the two is ever visible at a given width). */
    .qb-step-compact {
      display: none; font-size: 13px; font-weight: 500; color: #475569; margin-bottom: 16px;
      background: #fff; border: 1px solid rgba(2,6,23,.12); border-radius: 10px;
      padding: 12px 14px; box-shadow: 0 1px 2px rgba(2,6,23,.05);
    }
    .qb-step-compact .qb-progress { height: 3px; background: #e2e8f0; border-radius: 2px; margin-top: 6px; overflow: hidden; }
    .qb-step-compact .qb-progress-fill { height: 100%; background: ${accent}; transition: width .25s; }
    .qb-btn {
      background: ${accent}; color: #fff; border: none; border-radius: ${btnRadius}px;
      padding: 11px 22px; font-size: 14px; font-weight: 500; cursor: pointer;
      min-height: 44px; font-family: inherit;
    }
    .qb-btn[disabled] { opacity: .5; cursor: not-allowed; }
    /* Primary CTA — the single accent action, lifted with a soft
       accent-tinted shadow. */
    .qb-btn-primary { background: ${accent}; box-shadow: 0 6px 16px ${accent}33; }
    .qb-btn-primary[disabled] { box-shadow: none; }
    .qb-btn-ghost { background: #fff; color: #475569; border: 1px solid #e2e8f0; }
    .qb-btn-row { display: flex; justify-content: space-between; gap: 10px; margin-top: 22px; }
    .qb-toggle-row { display: flex; gap: 6px; }
    .qb-toggle-row .qb-btn { flex: 1; }
    .qb-toggle-row .qb-btn { background: #fff; color: #475569; border: 1px solid #e2e8f0; }
    .qb-toggle-row .qb-btn.active { background: ${accent}; color: #fff; border-color: ${accent}; }
    /* ── Choice cards (radio / checkbox custom fields) ─────────────────
       Previously an inline style hardcoded to slate: selected meant a 1px
       border going from light gray to dark gray, and the merchant's brand
       color never touched it (hence "all gray on gray, easy to miss").
       Now real classes, accent-driven, with hover + :focus-visible.

       NOTE the deliberate difference from .qb-toggle-row .qb-btn.active
       directly above: that rule paints a solid accent background with
       #fff text, which silently assumes a DARK accent and goes unreadable
       for a merchant with a pale brand color. These cards instead tint the
       fill to 8% and keep the label slate, so they survive any accent.

       Container-relative only — no viewport media queries. This form
       renders in four container widths and a viewport breakpoint here
       would fire independent of how much room the card actually has. */
    .qb-choice-list { display: flex; flex-direction: column; gap: 8px; }
    .qb-choice {
      position: relative;
      display: flex; align-items: flex-start; gap: 10px;
      padding: 12px 16px; min-height: 48px;
      border: 1px solid #e2e8f0; border-radius: 10px;
      background: #fff;
      /* A whisper of shadow so the card reads as a raised, pressable
         surface rather than a flat outline. */
      box-shadow: 0 1px 2px rgba(15,23,42,.05);
      font-size: 14px; line-height: 1.45; color: #0f172a;
      cursor: pointer;
      transition: border-color .15s, background-color .15s, box-shadow .15s;
    }
    .qb-choice:hover { border-color: #cbd5e1; }
    /* Selected: an INSET ring instead of a thicker border, so the card's
       box model is byte-identical checked and unchecked — nothing shifts a
       pixel on click. Font weight stays constant for the same reason (a
       600 label re-wraps a long option and moves the card's height). */
    .qb-choice-on {
      border-color: ${accent};
      background: ${tint};
      box-shadow: inset 0 0 0 1px ${accent}, 0 1px 2px rgba(15,23,42,.05);
    }
    /* Visually hidden but focusable and still the real control: the label
       wraps it, so a click/tap anywhere on the card activates it and the
       BROWSER enforces radio exclusivity + arrow-key roving. Never
       display:none (that would drop it from the tab order). */
    .qb-choice-input {
      position: absolute; top: 0; left: 0;
      width: 1px; height: 1px; opacity: 0; margin: 0;
      pointer-events: none;
    }
    /* margin-top nudges the indicator onto the first line's optical center
       so a wrapping label ("Yes, more than 1 flight") grows downward
       without the glyph drifting with it. */
    .qb-choice-mark {
      flex: 0 0 18px; width: 18px; height: 18px; margin-top: 1px;
      border: 1.5px solid #cbd5e1; background: #fff;
      display: flex; align-items: center; justify-content: center;
      transition: border-color .15s;
    }
    .qb-choice-radio .qb-choice-mark { border-radius: 50%; }
    .qb-choice-checkbox .qb-choice-mark { border-radius: 5px; }
    .qb-choice-on .qb-choice-mark { border-color: ${accent}; }
    /* min-width:0 lets the flex item shrink below its content width so long
       labels wrap instead of forcing horizontal scroll at 320px. */
    .qb-choice-text { min-width: 0; overflow-wrap: break-word; }
    /* The indicator carries the meaning: a dot says "pick one", a check
       says "pick any". Both are drawn IN the accent on a white mark rather
       than white-on-accent — the pale-accent trap again. */
    .qb-choice-dot {
      width: 8px; height: 8px; border-radius: 50%; background: ${accent};
      transform: scale(0); transition: transform .12s ease-out;
    }
    .qb-choice-on .qb-choice-dot { transform: scale(1); }
    /* Tick drawn from two borders on a rotated box — no SVG namespace to
       thread through el(), no icon font to load cross-origin. */
    .qb-choice-check {
      width: 5px; height: 9px; margin-top: -2px;
      border: solid ${accent}; border-width: 0 2px 2px 0;
      transform: rotate(45deg) scale(0); transform-origin: center;
      transition: transform .12s ease-out;
    }
    .qb-choice-on .qb-choice-check { transform: rotate(45deg) scale(1); }
    /* Focus ring drawn on an inert overlay sibling, not the label, because
       styling the label from the input's focus state would need :has() —
       unsupported in older Safari, exactly the browser we can't choose.
       outline (not border/box-shadow) so it never affects layout or
       triggers horizontal overflow at the narrow container widths. */
    .qb-choice-focus {
      position: absolute; inset: 0; border-radius: 10px; pointer-events: none;
    }
    .qb-choice-input:focus-visible ~ .qb-choice-focus {
      outline: 2px solid ${accent}; outline-offset: 2px;
    }
    .qb-errors { background: #fef2f2; border: 1px solid #fecaca; border-radius: 9px; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; color: #b91c1c; }
    .qb-bundle-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin-bottom: 18px; }
    .qb-bundle { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; flex-direction: column; gap: 8px; }
    .qb-bundle-img { width: 100%; height: 100px; object-fit: cover; border-radius: 8px; background: #f1f5f9; }
    .qb-bundle-img-empty { width: 100%; height: 100px; border-radius: 8px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); }
    .qb-results { border: 1px solid #e2e8f0; border-radius: 10px; margin-top: 8px; max-height: 360px; overflow-y: auto; background: #fff; }
    .qb-result-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid #f1f5f9; }
    .qb-result-row:last-child { border-bottom: none; }
    .qb-thumb { width: 56px; height: 56px; border-radius: 8px; object-fit: cover; background: #f1f5f9; flex: 0 0 56px; }
    .qb-thumb-empty { width: 56px; height: 56px; border-radius: 8px; background: #f1f5f9; color: #cbd5e1; font-size: 9px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex: 0 0 56px; }
    .qb-cart-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f1f5f9; flex-wrap: wrap; }
    .qb-cart-thumb { width: 50px; height: 50px; border-radius: 8px; object-fit: cover; background: #f1f5f9; flex: 0 0 50px; }
    .qb-qty-btn { width: 32px; height: 32px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; cursor: pointer; font-size: 14px; }
    .qb-banner { background: #f8fafc; border: 1px solid rgba(2,6,23,.08); color: #334155; border-radius: 9px; padding: 10px 14px; font-size: 13px; margin-bottom: 14px; display: flex; align-items: center; gap: 10px; }
    .qb-banner-success { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
    .qb-empty { background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 10px; padding: 22px 18px; text-align: center; font-size: 13px; color: #64748b; }
    .qb-variant-modal-overlay {
      /* Desktop: centered card. Mobile: bottom sheet (anchored to
         the keyboard's natural rest zone + thumb reach). Switched
         via the same 600px breakpoint the panel uses. */
      position: fixed; inset: 0; background: rgba(15,23,42,.55); z-index: 2147483600;
      display: flex; align-items: center; justify-content: center;
      padding: 16px;
    }
    .qb-variant-modal {
      width: 100%; max-width: 480px; background: #fff; border-radius: 16px;
      padding: 20px 22px 28px; max-height: 80dvh; overflow-y: auto;
      box-shadow: 0 24px 64px rgba(15,23,42,.25);
    }
    @media (max-width: 600px) {
      .qb-variant-modal-overlay {
        align-items: flex-end;
        padding: 0;
      }
      .qb-variant-modal {
        border-radius: 16px 16px 0 0;
        max-width: 100%;
      }
    }
    .qb-variant-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0;
      border-radius: 9px; margin-bottom: 8px; cursor: pointer; font-size: 14px; font-weight: 600;
    }
    .qb-variant-row.added { background: #f0fdf4; border-color: #86efac; color: #15803d; cursor: default; }
    .qb-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.55); z-index: 2147483600; display: flex; align-items: center; justify-content: center; padding: 16px; }
    .qb-modal-card { background: #f1f5f9; border-radius: 16px; max-width: 720px; width: 100%; max-height: 92vh; overflow-y: auto; }
    .qb-drawer-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.55); z-index: 2147483600; }
    .qb-drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 440px; max-width: 100vw; background: #f1f5f9; z-index: 2147483601; overflow-y: auto; box-shadow: -10px 0 30px rgba(15,23,42,.15); animation: qb-slide .25s ease-out; }
    @keyframes qb-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
    /* Slide-in for the quick-form picker sub-screen. Same .25s
       ease-out rhythm as the drawer's outer animation so the two
       feel consistent. */
    /* qb-screen-slide / qb-quick-screen previously animated the
       separate-screen picker. Picker is now an inline expansion
       inside the quick-form card — no transition needed. */
    .qb-cta-btn {
      background: ${accent}; color: #fff; border: none; border-radius: 10px;
      padding: 14px 28px; font-size: 15px; font-weight: 700; cursor: pointer;
      font-family: inherit; min-height: 48px;
    }
    .qb-close-x {
      background: #fff; border: 1px solid rgba(2,6,23,.10); color: #475569;
      font-size: 16px; line-height: 1; cursor: pointer; border-radius: 50%;
      /* Neutral 28px circle on a comfortable 44px touch envelope (iOS
         HIG min) so it stays tappable without zooming on tiny phones. */
      width: 28px; height: 28px; min-width: 28px; min-height: 28px; padding: 0;
      flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .qb-close-x:hover { background: #f1f5f9; }

    /* ── Layout scaffold (qb-header + qb-shell + qb-nav-footer) ────
       qb-root is always a flex column. In inline mode the height is
       auto so the form takes its natural size and the footer sits
       below the form (regular flow). In overlay mode (.qb-overlay
       class, only present when this builder lives inside an
       openOverlay wrapper) qb-root fills its host and qb-shell
       becomes the scroll container — the footer pins to the bottom
       of the panel viewport via flex-shrink:0.

       This is the correct fix for the mobile "Next button hides
       under the keyboard / scrolls into the middle of the form"
       bug. The nav buttons are no longer inside qb-card (the
       scrollable form card), they're a sibling of qb-shell. */
    :host { display: block; }
    .qb-root {
      display: flex;
      flex-direction: column;
    }
    .qb-overlay {
      /* FIX (form-header-declutter-footer #1 — regression from the prior
         "content-sized panel" pass): qb-root is the sole shadow-tree
         child of qbHost (the light-DOM host div openOverlay sets up as
         display:flex; flex-direction:column; flex:1; min-height:0). qbHost
         itself is capped via its ancestor panel's max-height and
         correctly clamps via flex-grow — but a PERCENTAGE height here
         (height:100%, the old rule) does NOT resolve against that
         clamped size: CSS only treats a flex item's post-flex size as
         "definite" for its OWN children's percentage resolution when the
         flex container's main size was itself definite going in, which an
         auto-height-but-capped column never is. The result: qb-root
         silently fell back to auto (its own content height), completely
         ignoring qbHost's clamp — on a short step this coincidentally
         looked fine (content was smaller than the cap anyway), but on the
         tallest step (Finish Up) qb-root grew past the cap and the
         overflow — including the pinned footer — was clipped off by
         qbHost's overflow:hidden with no internal scroll ever engaging.
         Confirmed via a live shadow-DOM repro before/after.
         FIX: size qb-root via flex-grow (mirroring exactly how qbHost
         itself fills panel) instead of a percentage height. Flex-grow
         sizing of a flex item DOES correctly propagate a definite,
         clamped size that its own children's flex/overflow math can then
         see — this is what lets qb-shell's flex:1 1 auto; min-height:0;
         overflow-y:auto actually compress + scroll once total content
         exceeds the panel's max-height, keeping header + footer
         (flex-shrink:0) pinned and visible on every step. Short steps are
         unaffected: qb-root still just shrinks to content, no forced gap. */
      flex: 1 1 auto;
      min-height: 0;
    }
    .qb-root > .qb-header { flex-shrink: 0; }
    .qb-overlay .qb-shell {
      /* flex-basis:auto (NOT 0) is load-bearing. The earlier
         basis:0 attempt collapsed the Quick Quote form body to 0
         height: the panel sizes to content, but a child with
         basis:0 contributes 0 to the parent's intrinsic size, so
         the panel shrank to just header+footer and shell got 0px
         of flex space to grow into.
         basis:auto = shell first claims its content height, which
         pushes the panel toward its max-height cap. min-height:0
         then lets shell shrink below content when overflow kicks
         in. overflow-y:auto scrolls the excess.
         Manual test checklist on quotesense-demo before changing:
           1. Quick Quote modal w/ 3 line items → form fully visible, no scroll
           2. Quick Quote modal w/ 8+ line items → form scrolls internally,
              Submit reachable at bottom, close X stays at top
           3. Multi-step wizard modal → unchanged
           4. Drawer mode (both above) → unchanged
           5. Mobile (DevTools iPhone) → full-viewport panel still scrolls */
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      /* iOS Safari scroll-bleed defense — keeps touch scrolls
         confined to qb-shell instead of leaking through to the
         merchant's page underneath. */
      overscroll-behavior: contain;
    }

    /* Nav footer — sticky-to-bottom in overlay mode by virtue of
       being the last flex child with flex-shrink:0. In inline mode
       it just sits below the form. Column container: the wizard nav
       (Back / Step N / Next) rides in a .qb-nav-row, and the
       "Questions? <email>" line stacks beneath it. */
    .qb-nav-footer {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      padding: 12px 18px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom));
      background: #fff;
      border-top: 1px solid #e2e8f0;
    }
    /* The Back / Step N of M / Next|Submit triplet — the original
       space-between row, now nested one level down so the footer can
       stack the email line below it. */
    .qb-nav-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }
    /* Inline mode: no border above the footer when it's just sitting
       under the form on a static page — looks cleaner. */
    .qb-root:not(.qb-overlay) > .qb-nav-footer {
      border-top: none;
      background: transparent;
      padding: 0 18px 22px;
    }
    /* Center slot — a column holding "Step N of M" and (beneath it) the
       "Questions? <email>" line (form-polish-v3 #3). flex:1 so it fills
       the space between Back and Submit; contents centered. */
    .qb-nav-center {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      flex: 1 1 auto;
      min-width: 0;
    }
    /* Step indicator — center slot. Drops below 520px so the buttons
       don't crowd; the top progress bar conveys the same info. On mobile
       the "Questions?" line remains, sitting in the Back/Next tier. */
    .qb-step-indicator {
      font-size: 11px;
      font-weight: 600;
      color: #94a3b8;
      letter-spacing: .04em;
      text-align: center;
      flex: 0 0 auto;
    }
    .qb-nav-center .qb-footer-meta { max-width: 100%; }
    .qb-nav-center .qb-footer-meta a { word-break: break-word; }
    @media (max-width: 520px) {
      .qb-step-indicator { display: none; }
      .qb-nav-footer {
        padding: 10px 14px;
        padding-bottom: calc(10px + env(safe-area-inset-bottom));
      }
    }
    /* Left-slot spacer when Back is hidden (step 1). Keeps the Next
       button right-aligned via the space-between layout. Lives inside
       .qb-nav-row now (footer is a column). */
    .qb-nav-row > div:empty { width: 1px; }
    .qb-trust { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 9px; padding: 10px 14px; margin-top: 14px; font-size: 12px; color: #15803d; }
    .qb-footer-meta { text-align: center; margin-top: 14px; font-size: 11px; color: #94a3b8; }
    .qb-footer-meta a { color: ${accent}; text-decoration: none; }
    /* "Powered by QuoteSense" badge (tier-gating Lane 3 §4) — deliberately
       NOT ${accent}-colored like .qb-footer-meta's mailto link: this isn't
       merchant branding, so it stays a flat muted gray in both the label
       and the link, distinct from the "Questions?" line right above it. */
    .qb-badge-meta { text-align: center; margin-top: 6px; font-size: 10px; color: #cbd5e1; }
    .qb-badge-meta a { color: #94a3b8; text-decoration: none; }

    /* ── Builder mode (data-mode="builder") ─────────────────────────
       Document-flow rendering for dedicated quote-builder pages.
       Strips the widget-y panel chrome: no card backgrounds, no
       shadows, no internal scroll, inherits the merchant page's
       fonts. Section headings + form inputs keep their visual
       distinctness so the form is still scannable.
       Background MUST be transparent — qb-root's default
       #f1f5f9 reads as a lavender/grey tint against the merchant's
       white page; the whole point of builder mode is to inherit
       the surrounding page styling. :host transparent for the same
       reason — the shadow host shouldn't paint a background either. */
    .qb-builder { font-family: inherit; color: inherit; background: transparent; }
    .qb-builder .qb-shell {
      max-width: none; padding: 0; margin: 0;
      overflow: visible;            /* no internal scroll — let the page handle it */
      background: transparent;
    }
    /* Bordered wrapper (default) — subtle white card around the
       whole form. Visually contains the form on plain merchant
       pages without competing with the page color. Merchants can
       flip to "transparent" via shop_config.quick_form.builder_form_style
       when their page already provides the container styling. */
    .qb-builder.qb-builder-bordered .qb-shell {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 28px;
    }
    @media (max-width: 600px) {
      .qb-builder.qb-builder-bordered .qb-shell { padding: 18px; }
    }
    .qb-builder .qb-card {
      background: transparent; border: none; border-radius: 0;
      box-shadow: none; padding: 0;
      margin-top: 32px;
    }
    .qb-builder .qb-card:first-of-type { margin-top: 0; }
    /* Section heading — bold sentence-case + primary-color underline.
       Drops the wizard's uppercase/letterspaced treatment in favor
       of a more native-page-content feel. This is the only place
       primary_color paints anything in builder mode (there's no
       header bar here) — see resolveDividerColor() below. Falls
       back to slate when primary is too pale to read against its
       backdrop, or isn't parseable hex. */
    .qb-builder .qb-title {
      font-size: 20px; font-weight: 700;
      margin: 0 0 16px;
      padding-bottom: 8px;
      border-bottom: 2px solid ${dividerColor};
      color: #0f172a;
      text-transform: none;
      letter-spacing: 0;
    }
    /* Field labels — match the section-heading tone: sentence-case,
       no uppercase / letterspacing. Wizard mode keeps the original
       qb-label styling (different visual rhythm). */
    .qb-builder .qb-label {
      font-size: 13px; font-weight: 500;
      color: #0f172a;
      text-transform: none;
      letter-spacing: 0;
      margin-bottom: 6px;
    }
    /* Section divider — top border on every section heading EXCEPT
       the first (Build Your Quote). Removes the qb-card margin-top
       on dividered sections so the line itself defines the gap (a
       margin-top above the line would look like floating chrome).
       Pairs with the heading's existing bottom border: top line
       separates this section from the previous, bottom line
       underlines the heading text. */
    .qb-builder .qb-card:not(:first-of-type) {
      margin-top: 0;
    }
    .qb-builder .qb-card:not(:first-of-type) > .qb-title {
      border-top: 1px solid #e2e8f0;
      padding-top: 32px;
      margin-top: 32px;
    }
    /* Steps strip + compact progress are wizard-only chrome; if they
       leak into builder layout (defensive — renderBuilderLayout
       doesn't add them but renderStep0/1/2/3 might pick something
       up via banner / suggestions), keep them out. */
    .qb-builder .qb-steps, .qb-builder .qb-step-compact { display: none; }
    /* Footer trust copy not used in builder mode (merchant page
       has its own footer) — but the rule is here in case future
       sections reuse the class. */
    .qb-builder .qb-footer-meta { color: inherit; opacity: .7; }
    /* Subtotal summary block — sits below the line items in the
     Build Your Quote section. Updates live as cartItems change.
       Light separator, no card chrome (consistent with the rest
       of builder mode). */
    .qb-builder-summary {
      margin-top: 14px; padding-top: 12px;
      border-top: 1px solid #e2e8f0;
      display: flex; justify-content: space-between; align-items: baseline;
      flex-wrap: wrap; gap: 6px;
    }
    .qb-builder-summary-label {
      font-size: 13px; font-weight: 600; color: inherit; opacity: .75;
    }
    .qb-builder-summary-total {
      font-size: 18px; font-weight: 800; color: inherit; letter-spacing: -.01em;
    }
    .qb-builder-summary-note {
      width: 100%; font-size: 11px; color: inherit; opacity: .55; margin-top: 2px;
    }
    /* Toast — transient confirmation after a cart mutation. Fixed-
       position so it floats over whatever the customer is looking
       at (no layout shift). Fades out via JS-set opacity transition
       after the 3s timer fires. */
    .qb-toast {
      position: fixed; left: 50%; bottom: 24px;
      transform: translateX(-50%);
      display: flex; align-items: center; gap: 12px;
      background: #0f172a; color: #fff;
      padding: 12px 18px; border-radius: 10px;
      font-size: 13px; font-weight: 600; line-height: 1.4;
      box-shadow: 0 10px 25px -6px rgba(15,23,42,.30), 0 4px 10px -4px rgba(15,23,42,.20);
      z-index: 2147483640;
      opacity: 0; transition: opacity .2s ease;
      max-width: calc(100vw - 32px);
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif;
    }
    .qb-toast.qb-toast-visible { opacity: 1; }
    .qb-toast-success { background: #15803d; }
    .qb-toast-error   { background: #b91c1c; }
    .qb-toast-msg { flex: 1 1 auto; }
    .qb-toast-close {
      flex: 0 0 auto; background: transparent; border: 0; color: inherit;
      font-size: 18px; line-height: 1; font-weight: 700; cursor: pointer;
      padding: 0 2px; opacity: .85;
    }
    .qb-toast-close:hover { opacity: 1; }
    .qb-soldout-pill {
      display: inline-block; margin-left: 6px; vertical-align: middle;
      font-size: 10px; font-weight: 500; letter-spacing: .02em;
      padding: 2px 6px; border-radius: 4px;
      background: #fee2e2; color: #b91c1c; white-space: nowrap;
    }

    /* ── Flat panel (overlay) ────────────────────────────────────────
       In modal/drawer the light-DOM panel is now a white surface (see
       openOverlay panelStyle). Inside the shadow we collapse the old
       "window within a window": qb-root fills with the tinted form body
       (bg, set on the base .qb-root rule above), and qb-card sheds its
       floating-card chrome so the form content sits directly on that
       body — individual product rows / subtotal / inputs supply their
       own white-card depth so they pop against the tint (fix #2,
       form-width-and-contrast). Scoped to .qb-overlay so inline +
       builder mounts are untouched. */
    .qb-overlay .qb-shell {
      padding: 16px 16px 20px;
      /* FIX (form-polish-v3 #4 — the multi-step "3 different widths per
         step" bug): the base .qb-shell rule sets max-width:680px +
         margin:0 auto to center the column on the wide hosted /request
         page. But in the overlay, qb-shell is a flex item in qb-root's
         column flex — and an auto cross-axis margin DEFEATS
         align-items:stretch, so the shell stops filling the fixed-width
         panel and instead shrinks to its own CONTENT's min-width. That
         min-width varies per step (item cards / the 3-up preferred-
         contact row / review text all differ), so every step rendered a
         different width. Neutralizing the cap + auto margin here lets the
         shell stretch to the panel's fixed width (min(500px,100vw)),
         identical on all 4 steps, desktop + mobile. Diagnosed +
         verified with a live flex repro (content-hug 74px → full 500px). */
      max-width: none;
      margin: 0;
    }
    .qb-overlay .qb-card,
    .qb-overlay .qb-quick-form.qb-card {
      background: transparent; border: none; border-radius: 0;
      box-shadow: none; padding: 0;
    }
    /* Pinned footer — explicit white (was a light #f8fafc tint) to match
       the header/steps/cards/inputs; still casts a soft shadow UP over
       the scrolling content so the panel reads as framed top + bottom. */
    .qb-overlay .qb-nav-footer {
      background: #fff;
      border-top: 1px solid rgba(2,6,23,.12);
      box-shadow: 0 -8px 18px rgba(2,6,23,.05);
    }

    /* ── Two-line product card (quick form + wizard Build step) ──────
       Line 1: title, full width. Line 2: meta on the left, qty stepper
       + line total + remove on the right. */
    .qb-item-card {
      display: flex; gap: 12px; align-items: flex-start;
      background: #fff; border: 1px solid rgba(2,6,23,.08); border-radius: 10px;
      padding: 11px 12px; box-shadow: 0 1px 3px rgba(2,6,23,.06);
    }
    .qb-item-card + .qb-item-card { margin-top: 8px; }
    .qb-item-thumb {
      width: 44px; height: 44px; border-radius: 8px; flex: 0 0 44px;
      object-fit: cover; background: #f1f5f9;
      border: 1px solid rgba(2,6,23,.08); box-shadow: 0 1px 2px rgba(2,6,23,.05);
    }
    .qb-item-thumb-empty {
      display: flex; align-items: center; justify-content: center; color: #cbd5e1;
    }
    .qb-item-body { flex: 1; min-width: 0; }
    .qb-item-title {
      font-size: 14px; font-weight: 500; line-height: 1.35; color: #0f172a;
      word-break: break-word;
    }
    .qb-item-line2 {
      margin-top: 8px; display: flex; align-items: center;
      justify-content: space-between; gap: 10px; flex-wrap: wrap;
    }
    .qb-item-meta { font-size: 12px; color: #64748b; min-width: 0; }
    /* "(incl. options)" hint next to "each" when the line's price
       includes captured option deltas (kickoffs/option-price-math.md)
       — muted, same tone as the option lines below it. */
    .qb-item-meta-hint { color: #94a3b8; }
    /* Captured options (APO v2 / theme properties[...]) — muted lines
       under the meta/controls row. Same 12px/#94a3b8 tone as the
       embed's other supplementary-info lines. */
    .qb-item-options { margin-top: 6px; }
    .qb-item-option { font-size: 12px; color: #94a3b8; line-height: 1.45; }
    .qb-item-option-more { font-style: italic; }
    .qb-item-controls { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }
    .qb-item-total {
      font-size: 13.5px; font-weight: 500; color: #0f172a;
      text-align: right; min-width: 62px;
    }
    .qb-item-remove {
      background: none; border: none; color: #94a3b8; font-size: 15px;
      line-height: 1; cursor: pointer; padding: 4px; flex: 0 0 auto;
    }
    .qb-item-remove:hover { color: #64748b; }
    /* Compact qty stepper — white pill on a gentle elevation. */
    .qb-stepper {
      display: inline-flex; align-items: center; background: #fff;
      border: 1px solid rgba(2,6,23,.10); border-radius: 8px;
      box-shadow: 0 1px 2px rgba(2,6,23,.05); overflow: hidden;
    }
    .qb-stepper button {
      width: 26px; height: 26px; border: none; background: transparent;
      color: #475569; font-size: 14px; cursor: pointer; font-family: inherit;
      display: flex; align-items: center; justify-content: center; padding: 0;
    }
    .qb-stepper button:hover { background: #f1f5f9; }
    .qb-stepper-val {
      min-width: 26px; text-align: center; font-size: 13px; font-weight: 500; color: #0f172a;
    }

    /* ── Subtotal block (shared quick form + wizard) ─────────────────
       The key number before the CTA — given clear emphasis (fix,
       form-polish-header-subtotal #2): a faint accent wash + accent
       border (ties it visually to the accent submit button below) and
       a bolder, larger total. Still a "card" (white-ish, not a loud
       block) so it stays refined. */
    .qb-subtotal {
      display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
      background: ${accent}0f; border: 1px solid ${accent}40; border-radius: 10px;
      padding: 12px 14px; box-shadow: 0 1px 3px rgba(2,6,23,.06);
    }
    .qb-subtotal-main { flex: 1 1 auto; min-width: 0; }
    .qb-subtotal-label { font-size: 12.5px; color: #475569; }
    .qb-subtotal-note { font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
    .qb-subtotal-total { flex: 0 0 auto; font-size: 21px; font-weight: 700; color: #0f172a; letter-spacing: -.01em; }
    /* Space the wizard's subtotal off the item list it follows. */
    .qb-item-card + .qb-subtotal { margin-top: 12px; }

    /* Subtle full-width dashed "add more" affordance (neutral). */
    .qb-add-more-btn {
      display: block; width: 100%; background: #fff;
      border: 1px dashed rgba(2,6,23,.18); color: #475569;
      font-size: 13px; font-weight: 500; padding: 10px; border-radius: 10px;
      cursor: pointer; font-family: inherit;
    }
    .qb-add-more-btn:hover:not([disabled]) { background: #f8fafc; }
    .qb-add-more-btn[disabled] { cursor: not-allowed; opacity: .55; }

    /* Quick-form pinned-footer contact grid. */
    .qb-quick-contact { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .qb-quick-contact .qb-field { margin-bottom: 0; }
    .qb-quick-contact .qb-span-2 { grid-column: 1 / -1; }
    @media (max-width: 520px) {
      .qb-quick-contact { grid-template-columns: 1fr; }
    }

    /* Collapsible "Your details" (quick form). DESKTOP = default (no
       media query): the toggle summary is hidden and the fields always
       render. MOBILE (<=600px) collapses the fields behind a tappable
       summary so the product list is visible on open — now a clean
       divider + label + chevron, NOT a heavy card/bubble (form-polish-v3
       #6a). Pure-CSS checkbox toggle — no JS, no state. The visually-
       hidden checkbox stays togglable via its <label for>. Lives in the
       scroll region so expanding it scrolls the products (the pinned
       footer stays put). */
    .qb-quick-details-cb { position: absolute; width: 1px; height: 1px; opacity: 0; margin: 0; pointer-events: none; }
    .qb-quick-details-summary { display: none; }
    .qb-quick-details-body { display: block; }
    .qb-quick-details { margin-top: 8px; }
    @media (max-width: 600px) {
      .qb-quick-details-summary {
        display: flex; align-items: center; justify-content: space-between;
        cursor: pointer; user-select: none;
        padding: 12px 2px;
        border-top: 1px solid rgba(2,6,23,.10);
        font-size: 12.5px; font-weight: 500; color: #475569;
      }
      .qb-quick-details-chevron { display: inline-flex; color: #94a3b8; transition: transform .18s ease; }
      .qb-quick-details-cb:checked ~ .qb-quick-details-summary .qb-quick-details-chevron { transform: rotate(180deg); }
      .qb-quick-details-cb:not(:checked) ~ .qb-quick-details-body { display: none; }
      .qb-quick-details-body { margin-top: 6px; }
    }
  `;
}

// ── State ─────────────────────────────────────────────────────────────────
// Wizard step ids. Sourced from the shared embed-step-order helper so
// the renderer + the (unit-tested) order derivation agree on the
// vocabulary. Adds "describe" (the promoted describe-mode step) to the
// legacy about/shipping/quote/finish set.
type StepId = WizardStepId;

/** Storefront-detected customer + optional B2B Company enrichment.
 *  Populated by detectShopifyCustomer() during boot. When null, the
 *  embed renders in anonymous mode (today's behavior — no banner,
 *  no pre-fill). */
type CustomerContext = {
  customerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  defaultAddress?: {
    address1: string;
    city: string;
    province: string;
    zip: string;
    countryCode?: string;
  };
  /** Set only when the customer is also a B2B Company contact. */
  company?: {
    id: string;
    name: string;
    contactId: string;
    locations: Array<{
      id: string;
      name: string;
      taxExempt: boolean;
      address: {
        address1: string;
        address2?: string;
        city: string;
        province: string;
        zip: string;
        countryCode?: string;
      } | null;
    }>;
  };
};

type State = {
  step: number;
  firstName: string; lastName: string; email: string; phone: string; company: string;
  preferred: "email" | "phone" | "sms";
  // Merchant-defined custom fields. Keyed by EmbedCustomField.id. Values
  // are string for select/text/textarea, string[] for multiselect.
  customValues: Record<string, string | string[]>;
  // `country` drives the cascading region control; rides inside the
  // existing `state` (region) slot, not the builtin-field registry.
  country: string;
  address: string; address2: string; city: string; state: string; zip: string;
  // Optional SEPARATE billing address. `billingSame` default true →
  // "same as shipping"; unchecking reveals the fields. Only sent when
  // separate. billingCountry drives the region cascade (UI parity);
  // round-tripped in localStorage but not persisted downstream.
  billingSame: boolean;
  billingName: string; billingCompany: string; billingCountry: string;
  billingAddress1: string; billingAddress2: string; billingCity: string; billingState: string; billingZip: string;
  cartItems: CartItem[];
  notes: string;
  /** Free-text "what are you looking for" populated when the
   *  customer toggles describe-mode on Step 2. Submitted as the
   *  request's `description` field; rendered for the rep as a
   *  callout when the request landed with no picker items. */
  description: string;
  /** When true, Step 2's body renders a textarea instead of the
   *  cart-sync banner + product search + items list. Toggle lives
   *  at the top of Step 2 ("Not sure what you need? Describe it
   *  instead →"). Defaults false (picker is the canonical path). */
  descriptionMode: boolean;
  errors: string[];
  submitted: number | null;
  submitting: boolean;
  shopifyDetected: boolean;
  importableCount: number;
  /** Snapshot of the live Shopify cart fetched at boot (and refreshed
   *  after exportCart()/importCart()). Drives cartInSync — a count alone
   *  can't detect sync (2 cart items vs 2 DIFFERENT quote items is
   *  `2 === 2`). Not persisted; re-fetched every boot. */
  shopifyCartItems: CartItem[];
  // ── Phase 2: storefront customer auto-fill ────────────────────────
  // customerContext is what we detected (or null). selectedLocationId
  // is the picked CompanyLocation when a B2B contact has 2+ locations
  // — defaults to the first location on detection; the customer can
  // change it via the "Shipping to:" dropdown.
  customerContext: CustomerContext | null;
  selectedLocationId: string;
  // Customer-uploaded attachments. Only entries in `done` ship with
  // the submit. Errors stay in the list so the customer sees what
  // happened and can remove + retry.
  attachments: Attachment[];
  // Consent checkbox state. Only meaningful when the merchant has
  // consent_enabled === true on shop_config; shops without the
  // feature carry false and ignore it through to submit.
  consentAccepted: boolean;
  // Quick-mode field-level errors. Keyed by field id ("name", "phone",
  // "email", "qty", "message"); empty when no error. Drives the inline
  // hint rendered under each input on validation failure.
  quickErrors?: Record<string, string>;
  // Quick-mode sub-screen. "form" is the default — the line items +
  // contact fields. "picker" is the product search screen that slides
  // in when the customer clicks "+ Add more products". NOT persisted
  // to localStorage — the drawer always opens on the form screen so a
  // customer who navigated away mid-picker doesn't land on a screen
  // with no context. Only meaningful when shouldRenderQuickForm() is
  // true; the full wizard ignores it.
  /** Inline picker open flag. Replaces the previous "separate
   *  screen" model — picker now expands inline below the line
   *  items list, above the Name field. Not persisted (drawer
   *  always opens with the picker collapsed). */
  quickPickerOpen?: boolean;
  // Search text for the picker. Persisted with the open flag
  // semantics — i.e. NOT to localStorage; reset on each fresh open.
  quickPickerQuery?: string;
  // Cached picker results so re-renders during typing don't refetch.
  // Tied to the current query string; cleared when the customer
  // returns to the form screen.
  quickPickerResults?: QuickPickerProduct[];
  // True while a picker search is in flight; drives the "Searching…"
  // affordance in the results pane.
  quickPickerLoading?: boolean;
  // Product titles Shopify named as sold out on the most recent failed
  // send-to-cart. Drives the red "Sold out" pill on the matching "Your
  // items" rows. Transient (NOT persisted); reset at the start of each
  // send attempt so stale badges don't linger.
  soldOutTitles?: string[];
};

/** Shape returned by GET /api/public/product/search. Mirror of the
 *  ResponseProduct shape in src/app/api/public/product/search/route.ts —
 *  embed bundle can't import the route module. */
interface QuickPickerProduct {
  productId: string;
  title: string;
  image: string | null;
  vendor: string | null;
  /** Server-computed from product.tags + shop_config.discontinued_tag.
   *  Renders the "Discontinued" badge on the product row + every
   *  added line item. Discontinued is product-level so all variants
   *  share the flag. */
  discontinued?: boolean;
  variants: Array<{
    variantId: string;
    title: string;
    sku: string;
    price: string;
    /** Back-compat alias for availableForSale (older bundles read
     *  `available` directly). New code prefers the VariantInventory
     *  fields below. */
    available: boolean;
    availableForSale?: boolean;
    inventoryPolicy?: "DENY" | "CONTINUE";
    inventoryQuantity?: number | null;
  }>;
}

function initialState(): State {
  return {
    step: 0,
    firstName: "", lastName: "", email: "", phone: "", company: "",
    preferred: "email", customValues: {},
    country: "US",
    address: "", address2: "", city: "", state: "", zip: "",
    billingSame: true,
    billingName: "", billingCompany: "", billingCountry: "US",
    billingAddress1: "", billingAddress2: "", billingCity: "", billingState: "", billingZip: "",
    cartItems: [],
    notes: "",
    description: "",
    descriptionMode: false,
    errors: [],
    submitted: null,
    submitting: false,
    shopifyDetected: false,
    importableCount: 0,
    shopifyCartItems: [],
    customerContext: null,
    selectedLocationId: "",
    attachments: [],
    consentAccepted: false,
    quickErrors: {},
    quickPickerOpen: false,
    quickPickerQuery: "",
    quickPickerResults: [],
    quickPickerLoading: false,
  };
}

// ── Render ────────────────────────────────────────────────────────────────
/** Pre-fill instructions handed in from window.QuoteApp.openRequest()
 *  (theme app extension's product-page button). variantId wins over
 *  productId when both are set (more specific). Accepted as either
 *  numeric strings/numbers OR full Shopify GIDs — the public-product
 *  endpoint normalizes both. */
interface PrefillOpts {
  variantId?: string | number;
  productId?: string | number;
  /** Multi-item prefill — used by the cart-page theme block which
   *  reads /cart.js and passes a fully-hydrated list of line items.
   *  When set, the single-product variantId/productId fields are
   *  ignored (the cart paradigm doesn't have a "primary" item). Items
   *  are pushed directly into state.cartItems with no per-item refetch
   *  since the cart payload already carries everything we need. */
  lineItems?: PrefillLineItem[];
  /** Single-item prefill's line-item properties — resolved ONCE in
   *  api.openRequest() via resolveSeedProperties (option-capture.ts)
   *  before this object is built, so applyPrefill just carries it
   *  through onto the seeded CartItem. Absent → no APO/form-input
   *  capture and no explicit properties, byte-identical to before this
   *  field existed. */
  properties?: BasketLineProperty[];
}
interface PrefillLineItem {
  variantId: string | number;
  productId?: string | number;
  qty: number;
  title?: string;
  variantTitle?: string;
  sku?: string;
  /** Accepts string ("19.99") or number (19.99). /cart.js returns
   *  price in cents — callers should divide by 100 before passing. */
  price?: string | number;
  vendor?: string;
  /** Accepts both `imageUrl` (theme block field name) and `image`
   *  (matches /cart.js's own field name, so the manual install
   *  snippet can pass the cart item shape through with a minimal
   *  map). Either resolves to the rendered <img src>. */
  imageUrl?: string | null;
  image?: string | null;
  /** Per-line custom options (Quote Basket). Carried through
   *  applyPrefill into state.cartItems and onto the submit payload. */
  properties?: BasketLineProperty[];
}

class QuoteBuilder {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private state: State = initialState();
  private config: ShopConfig = {};
  // Cache-busting for the logo-proxy URL (kickoffs/quote-ui-clarity.md
  // §6) — same version the cache/network path already tracks in
  // CachedShopConfig, so a merchant's re-uploaded logo shows up on the
  // storefront on the next boot instead of trailing the proxy's
  // 5-minute stale-while-revalidate window. bootPreview() (the
  // designer's live-preview injection) doesn't set this — 0 is a safe
  // default there since that path never hits the real proxy cache.
  private configVersion = 0;
  private prefill: PrefillOpts | null = null;
  /** Form mode for this drawer instance. "quick_expandable" gates on
   *  the quick_form config + a prefilled product before rendering the
   *  single-screen form; "full" always renders the existing 4-step
   *  wizard. Default "full" — preserves prior behavior on callers
   *  (floating CTA, programmatic open()) that don't pass one. */
  private formMode: FormMode = "full";
  /** Wizard = 4-step paged form with Back/Next nav (default; used by
   *  inline/modal/drawer mounts). Builder = single-page scrolling
   *  layout with all sections stacked + one Submit button at bottom;
   *  used by data-mode="builder" mounts for dedicated quote-builder
   *  pages where customers build freely before committing personal
   *  info. The two layouts share state, validation primitives, and
   *  submit pipeline — only the rendering shape differs. */
  private layoutMode: "wizard" | "builder" = "wizard";
  /** Per-instance form appearance (#4 color, #3 size). Set from the
   *  theme block that mounted/opened this instance (mount div data-attrs
   *  for inline/builder, openRequest opts for modal/drawer). Falls back
   *  to the script-tag-level override (ACCENT_OVERRIDE / FORM_SCALE),
   *  then to the shop's brand accent / no scaling. Resolved lazily via
   *  resolveAccent() / formScale because config isn't loaded until boot. */
  private accentOverride = "";
  private formScale = FORM_SCALE;
  /** Multi-form (docs/MULTI_FORM.md): which named form this instance
   *  loads. Threaded into the config fetch (→ `&form=<id>`), the config
   *  cache key, and the submit POST (`form_id` attribution). Empty →
   *  the shop's default form (byte-identical to today). */
  private formId = "";
  /** Optional hook fired once after a SUCCESSFUL final submit (both the
   *  quick and full paths). The basket drawer sets this to
   *  `() => basket.clear()` so a basket-originated quote empties the
   *  basket on success. Unset for every non-basket caller (no-op). */
  private onSubmitSuccess?: () => void;
  /** Live basket subscription (unify). Set on mount when the basket is
   *  the item source of truth (basket.enabled); torn down on close so a
   *  discarded overlay doesn't keep re-rendering. Null when the basket is
   *  off (classic form-draft item ownership). */
  private basketUnsub: (() => void) | null = null;
  /** Which `shopifyDetected` value the DOM currently reflects (fix,
   *  embed-mobile-batch #2). `state.shopifyDetected` starts false and a
   *  warm-cache boot's fast-path paint happens before the async storefront
   *  probe resolves, so that first paint always assumes false. Once the
   *  probe resolves true on a shop with an empty cart, `shouldRender`'s
   *  other conditions can all be false (no cache-version bump, no
   *  customer context, `importableCount === 0`) — nothing would ever
   *  repaint, and "Send to cart" (gated on shopifyDetected) silently
   *  never appears until an unrelated interaction re-renders. Diffing
   *  the resolved value against what was actually painted (not just
   *  `|| shopifyDetected`, which would repaint on every Shopify
   *  storefront and defeat the warm-cache no-flash optimization)
   *  triggers a repaint only on a real flip. */
  private paintedShopifyDetected = false;
  /** Designer preview instance (see renderPreview). An inert render: the
   *  config is INJECTED (never fetched), and every escape hatch to the
   *  outside world is closed — no submit POST, no cart export, no product
   *  search, no localStorage draft, no basket store. Guarded at each site
   *  rather than by a fetch shim so the storefront boot path is byte-
   *  identical for real installs. */
  private preview = false;

  constructor(host: HTMLElement, opts: { prefill?: PrefillOpts; formMode?: FormMode; layoutMode?: "wizard" | "builder"; accentColor?: string; formScale?: number; formId?: string; onSubmitSuccess?: () => void; preview?: boolean } = {}) {
    this.host = host;
    this.shadow = host.attachShadow({ mode: "open" });
    this.preview = opts.preview === true;
    this.prefill = opts.prefill || null;
    this.formMode = opts.formMode || "full";
    this.layoutMode = opts.layoutMode || "wizard";
    this.accentOverride = (opts.accentColor || "").trim();
    if (opts.formScale != null) this.formScale = clampFormScale(opts.formScale);
    this.formId = (opts.formId || "").trim().toLowerCase();
    // Stamp the form id onto the submit payload so the quote-request POST
    // carries `form_id` for attribution (Lane 1 validates + persists it).
    // Empty stays off the body (byte-identical to a single-form submit).
    if (this.formId && this.formId !== "default") (this.state as any).form_id = this.formId;
    this.onSubmitSuccess = opts.onSubmitSuccess;
  }

  /** Resolved accent color for this instance. Per-instance theme-block
   *  override wins, then the script-tag override, then the shop brand
   *  accent, then the slate-blue default. Single source of truth so the
   *  "Import from cart" button, preferred-contact toggle, and every
   *  qb-btn-primary recolor together (#4). */
  private resolveAccent(): string {
    return this.accentOverride || ACCENT_OVERRIDE || this.config.accent_color || "#2563eb";
  }
  private resolvePrimary(): string {
    return PRIMARY_OVERRIDE || this.config.primary_color || "#0f172a";
  }
  /** Resolved form body/canvas background. No script-tag override (this
   *  is a per-shop config field, not a per-theme-block one like accent/
   *  primary) — just the merchant's form_bg_color or the light-neutral
   *  default. Cards/inputs/header/footer stay white regardless; only the
   *  body behind them uses this. */
  private resolveFormBg(): string {
    return this.config.form_bg_color || FORM_BG_DEFAULT;
  }

  /** Quick form's own accent override. Unset falls back to this
   *  instance's already-resolved accent —
   *  resolveAccent() above, which already layers per-instance /
   *  script-tag overrides + the brand default — so a shop that
   *  hasn't touched quick_form.accent_color renders byte-identically
   *  to before this field shipped. Scoped to quick-form-only render
   *  paths (renderQuickForm, the inline picker, the add-more gate) —
   *  the full wizard keeps using resolveAccent() directly. */
  private resolveQuickAccent(): string {
    const own = this.config.quick_form?.accent_color;
    return (typeof own === "string" && own.trim()) ? own.trim() : this.resolveAccent();
  }

  /** Fetch the prefill product/variant from the public endpoint and
   *  push it into state.cartItems as the first line item. Called once
   *  during boot when openRequest was passed { productId, variantId }
   *  from a theme block click. Idempotent — re-mounts won't duplicate
   *  because boot only runs once per QuoteBuilder instance, and the
   *  variantId-uniqueness check in cartItems prevents accidental
   *  doubles. */
  // ── Persistence (localStorage) ─────────────────────────────────────────
  // Within-surface draft persistence. Storefront customers building a
  // quote across product pages used to lose everything on navigation /
  // close. Now serialized to localStorage under
  // `pe:embed:draft:<shop_slug>`, hydrated on open, cleared on
  // successful submit (and on Send-to-cart). 7-day TTL — older drafts
  // get discarded silently so a customer who comes back next month
  // starts fresh.
  //
  // Coverage strategy: an interval-based persist (every 1000ms) checks
  // a snapshot hash and writes when anything changed. Avoids having to
  // thread schedulePersist() through every input/cart mutation. Cheap
  // enough that the merchant won't notice (small JSON, fast write).
  // localStorage failures (Safari private mode, quota exceeded, no
  // window) silently no-op — drawer falls back to today's in-memory
  // behavior.
  private persistInterval: any = null;
  private lastPersistedJson = "";
  private persistKey(): string {
    return `pe:embed:draft:${SHOP_SLUG}`;
  }
  private snapshotForPersist(): Record<string, any> {
    const s = this.state;
    // Unify: when the basket is enabled it owns quote ITEMS (its own
    // key). The form draft then keeps only NON-item state — contact /
    // notes / step / consent — so it never serializes cartItems.
    //
    // Classic (basket off): the draft still owns items. Quick mode is
    // single-product lead capture — its line items come fresh from the
    // openRequest prefill on every drawer open and shouldn't pollute the
    // shared persist key (which builder + wizard read). Preserve whatever
    // cartItems were already in localStorage so quick mode doesn't clobber
    // the builder's accumulated cart. Contact fields still persist.
    const basketBacked = this.isBasketBacked();
    let cartItemsToPersist = s.cartItems;
    if (!basketBacked && this.formMode === "quick_expandable" && typeof window !== "undefined") {
      try {
        const raw = window.localStorage?.getItem(this.persistKey());
        if (raw) {
          const prior = JSON.parse(raw);
          if (Array.isArray(prior?.cartItems)) cartItemsToPersist = prior.cartItems;
          else cartItemsToPersist = [];
        } else {
          cartItemsToPersist = [];
        }
      } catch {
        cartItemsToPersist = [];
      }
    }
    return {
      step: s.step,
      firstName: s.firstName, lastName: s.lastName,
      email: s.email, phone: s.phone, company: s.company,
      preferred: s.preferred,
      country: s.country, address: s.address, address2: s.address2, city: s.city, state: s.state, zip: s.zip,
      billingSame: s.billingSame,
      billingName: s.billingName, billingCompany: s.billingCompany, billingCountry: s.billingCountry,
      billingAddress1: s.billingAddress1, billingAddress2: s.billingAddress2, billingCity: s.billingCity, billingState: s.billingState, billingZip: s.billingZip,
      // Items omitted entirely when the basket owns them.
      ...(basketBacked ? {} : { cartItems: cartItemsToPersist }),
      notes: s.notes,
      // Persist describe-mode payload so a customer who toggled
      // the textarea, typed a few lines, and bounced returns to
      // the same surface. descriptionMode flag persists too —
      // without it the toggle would snap back to the picker on
      // re-open and the typed text would look orphaned.
      description: s.description,
      descriptionMode: !!s.descriptionMode,
      customValues: s.customValues || {},
      consentAccepted: !!s.consentAccepted,
      // quickPickerOpen / quickPickerQuery / quickPickerResults
      // deliberately NOT persisted. Drawer always opens with the
      // picker collapsed so the customer lands on the line items
      // they already built, not on a stale search. cartItems
      // already persist — anything they added through the picker
      // carries over.
      selectedLocationId: s.selectedLocationId || null,
      // Attachments deliberately omitted — file uploads are session-
      // bound to blob URLs that may expire before the customer
      // resumes. Re-upload on resume is the safe path.
      updatedAt: new Date().toISOString(),
    };
  }
  /** True when the embed is booting on the Shopify cart page — `/cart`
   *  or a locale-prefixed `/{locale}/cart`, with or without a trailing
   *  slash. Opening "Request a quote" from the cart page means the LIVE
   *  cart is authoritative, independent of whatever (if anything) the
   *  liquid button passed as lineItems. */
  private isCartPageContext(): boolean {
    try {
      if (typeof window === "undefined") return false;
      return /(^|\/)cart\/?$/.test(window.location.pathname);
    } catch {
      return false;
    }
  }

  /** True when THIS session is seeded from the live Shopify cart — a
   *  cart-page "Request a quote" (prefill.lineItems, the cart page itself,
   *  or a `?import=cart` deep-link). In those sessions the live cart is
   *  authoritative, so the persisted localStorage draft must NOT merge
   *  onto it (that's the stale cross-session bleed). applyPrefill()/
   *  importCart() both replace, so we just skip persisted-cartItems
   *  hydration here and let them win. */
  private isCartOriginatedSession(): boolean {
    if (Array.isArray(this.prefill?.lineItems) && this.prefill!.lineItems!.length) return true;
    if (this.isCartPageContext()) return true;
    try {
      if (typeof window !== "undefined") {
        const u = new URL(window.location.href);
        if (u.searchParams.get("import") === "cart") return true;
      }
    } catch { /* swallow — URL parsing edge */ }
    return false;
  }

  private hydrateFromStorage() {
    // Preview: never read a real customer draft into the designer canvas.
    if (this.preview) return;
    try {
      const raw = window.localStorage?.getItem(this.persistKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, any>;
      const ts = parsed.updatedAt ? new Date(parsed.updatedAt).getTime() : 0;
      // 7-day TTL: silently discard older drafts so the customer
      // doesn't get a surprise re-hydrate from weeks ago.
      if (!ts || Date.now() - ts > 7 * 24 * 60 * 60 * 1000) {
        window.localStorage.removeItem(this.persistKey());
        return;
      }
      // Apply persisted values into state. Skip undefined/null so a
      // prefill set on construction (passed via openRequest) survives.
      const apply = <K extends keyof State>(k: K, v: any) => {
        if (v !== undefined && v !== null) (this.state as any)[k] = v;
      };
      // step deliberately NOT hydrated — it's a raw index into
      // stepDefs, whose order can change (e.g. products-first
      // reorder). Resuming a stale index could land the customer on
      // the wrong section; always reopen on the first step (products).
      // Cart items + field values above/below still restore.
      apply("firstName", parsed.firstName);
      apply("lastName", parsed.lastName);
      apply("email", parsed.email);
      apply("phone", parsed.phone);
      apply("company", parsed.company);
      apply("preferred", parsed.preferred);
      apply("country", parsed.country);
      apply("address", parsed.address);
      apply("address2", parsed.address2);
      apply("city", parsed.city);
      apply("state", parsed.state);
      apply("zip", parsed.zip);
      apply("billingSame", parsed.billingSame);
      apply("billingName", parsed.billingName);
      apply("billingCompany", parsed.billingCompany);
      apply("billingCountry", parsed.billingCountry);
      apply("billingAddress1", parsed.billingAddress1);
      apply("billingAddress2", parsed.billingAddress2);
      apply("billingCity", parsed.billingCity);
      apply("billingState", parsed.billingState);
      apply("billingZip", parsed.billingZip);
      apply("notes", parsed.notes);
      apply("description", parsed.description);
      apply("descriptionMode", parsed.descriptionMode);
      apply("consentAccepted", parsed.consentAccepted);
      // quickPicker* deliberately NOT hydrated — see
      // snapshotForPersist for rationale. Drawer always opens with
      // the picker collapsed.
      apply("selectedLocationId", parsed.selectedLocationId);
      apply("customValues", parsed.customValues);
      // Persisted-cartItems restore is for a NON-cart builder session
      // resumed by the same user. When the session is cart-originated
      // (cart-page seed or ?import=cart), the LIVE cart is authoritative —
      // applyPrefill()/importCart() replace state.cartItems with exactly
      // the cart, so we must NOT merge the stale draft onto it here.
      //
      // For a genuine builder resume we merge (not replace) so a prefilled
      // product from openRequest's opts already pushed isn't evicted;
      // dedupe by variantId. At hydrate time state.cartItems is normally
      // empty, so this restores the draft.
      //
      // Quick mode is single-product lead capture by design — the
      // prefilled product from the button click is the ENTIRE
      // line-items list. Skip cartItems hydration so items the
      // customer accumulated on the builder page don't leak into
      // a fresh product-page quick-form click. Contact info above
      // (name/email/phone/etc) still hydrates — that's useful
      // continuity, not leakage.
      //
      // Unify: when the basket is enabled it owns items (seeded + written
      // through separately), so the draft never restores cartItems — a
      // legacy draft's items are handled once by migrateLegacyDraftItems.
      if (
        !this.isBasketBacked() &&
        !this.isCartOriginatedSession() &&
        this.formMode !== "quick_expandable" &&
        Array.isArray(parsed.cartItems)
      ) {
        const existing = new Set(this.state.cartItems.map((c: CartItem) => c.variantId));
        for (const it of parsed.cartItems) {
          if (it && it.variantId && !existing.has(it.variantId)) {
            this.state.cartItems.push(it);
            existing.add(it.variantId);
          }
        }
      }
      this.lastPersistedJson = raw;
    } catch {
      // Corrupt JSON, no localStorage, quota exceeded — silent.
    }
  }
  private persistTick() {
    try {
      const snap = this.snapshotForPersist();
      const json = JSON.stringify(snap);
      // Hash-skip: don't write when nothing changed since last tick.
      // Cheap shallow comparison via stringified bytes; the snapshot
      // object is small (< 8KB even with a full cart).
      if (json === this.lastPersistedJson) return;
      window.localStorage?.setItem(this.persistKey(), json);
      this.lastPersistedJson = json;
    } catch {
      // No localStorage / quota → drawer keeps working in-memory.
    }
  }
  private startPersistLoop() {
    // Preview: no persistence at all (a designer render must not write a
    // draft, and a re-render every keystroke would leak intervals).
    if (this.preview) return;
    if (this.persistInterval) return;
    if (typeof window === "undefined" || !window.localStorage) return;
    this.persistInterval = setInterval(() => this.persistTick(), 1000);
  }
  private stopPersistLoop() {
    if (this.persistInterval) {
      clearInterval(this.persistInterval);
      this.persistInterval = null;
    }
  }
  /** Full reset — drop the entire persisted draft (incl. contact) and
   *  stop persisting. Used on FINAL submit success (the customer is done
   *  and lands on the confirmation screen). */
  private clearPersistedDraft() {
    this.stopPersistLoop();
    this.lastPersistedJson = "";
    try { window.localStorage?.removeItem(this.persistKey()); } catch { /* swallow */ }
    // Unify: quote ITEMS live in the basket store now (the single source
    // of truth), not the form draft. A successful submit empties it — the
    // quote_request owns those lines. All successful submits clear.
    if (this.isBasketBacked()) {
      try { getBasket().clear(); } catch { /* swallow */ }
    }
  }

  // ── Unify: items on the basket store ───────────────────────────────
  // When the Quote Basket is enabled, the basket store (src/embed/
  // basket.ts, pe:basket:<slug>) is the single source of truth for quote
  // ITEMS across every surface (multi-step overlay, quick form, inline
  // builder, inline embed). This instance's state.cartItems is a live
  // VIEW over it: seeded on mount, written through on every mutation,
  // re-synced on the store's subscribe. The form draft (pe:embed:draft)
  // keeps only NON-item state (contact/notes/step/consent). When the
  // basket is OFF, none of this runs and the form draft owns items
  // exactly as before (classic behavior, byte-identical).

  /** True when quote items unify onto the basket store for this instance
   *  (basket.enabled). Config is loaded before any caller runs (boot sets
   *  this.config before hydrate/seed). */
  private isBasketBacked(): boolean {
    // Preview: the demo items live in this instance's state only. Binding a
    // designer canvas to the shared basket store would let a preview render
    // mutate (and subscribe to) real quote items.
    if (this.preview) return false;
    return resolveBasketConfig(this.config).enabled === true;
  }

  /** state.cartItems := the basket's current lines (mapped to the CartItem
   *  view). The single read path for the unified store. */
  private syncItemsFromBasket(): void {
    this.state.cartItems = getBasket().getItems().map(basketItemToCartItem);
  }

  /** Subscribe this builder to the basket store so a change on ANY
   *  surface (another mounted builder, the Add-to-Quote button, a second
   *  tab) re-syncs + re-renders here. The callback only READS the store
   *  (never writes), so there's no write-back loop; renderStep no-ops
   *  before the shell exists. Idempotent. */
  private subscribeToBasket(): void {
    if (this.basketUnsub) return;
    try {
      this.basketUnsub = getBasket().subscribe(() => {
        // Self-heal: openOverlayShell's re-open guard tears down a stacked
        // overlay by removing its wrapper WITHOUT routing through close()
        // (which is what unsubscribes). If our host is detached, drop the
        // subscription instead of re-rendering a detached shadow tree —
        // otherwise a stacked-open would leave zombie instances all
        // re-rendering on every basket change.
        if (this.host && this.host.isConnected === false) {
          if (this.basketUnsub) { try { this.basketUnsub(); } catch { /* ignore */ } this.basketUnsub = null; }
          return;
        }
        try {
          this.syncItemsFromBasket();
          this.renderStep();
        } catch { /* a store emit must never break the page */ }
      });
    } catch {
      this.basketUnsub = null;
    }
  }

  /** One-time migration: if the basket is empty but a legacy form draft
   *  still carries items (written before unify shipped), seed the basket
   *  from them so a customer mid-build doesn't lose their quote on the
   *  upgrade. Runs once — afterwards the draft no longer serializes items.
   *  Skipped for cart-originated sessions (the live cart is authoritative
   *  and applyPrefill/importCart seed the basket directly). No-op when the
   *  basket is off. */
  private migrateLegacyDraftItems(): void {
    if (!this.isBasketBacked()) return;
    try {
      if (this.isCartOriginatedSession()) return;
      const basket = getBasket();
      if (basket.lineCount() > 0) return;
      const raw = window.localStorage?.getItem(this.persistKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, any>;
      const ts = parsed?.updatedAt ? new Date(parsed.updatedAt).getTime() : 0;
      // Respect the same 7-day TTL the draft itself uses — don't resurrect
      // a stale cart into the basket.
      if (!ts || Date.now() - ts > 7 * 24 * 60 * 60 * 1000) return;
      const items = Array.isArray(parsed.cartItems) ? parsed.cartItems : [];
      if (!items.length) return;
      basket.setItems(items as BasketItem[]);
    } catch {
      // Migration is best-effort — a broken draft just means the customer
      // starts from an empty basket, same as any expired draft.
    }
  }

  /** Seed state.cartItems from the basket + wire the live subscription.
   *  Called from boot() after hydrate/migrate/applyPrefill. No-op when the
   *  basket is off (classic form-draft item ownership stays intact). */
  private seedAndSubscribeBasket(): void {
    if (!this.isBasketBacked()) return;
    this.syncItemsFromBasket();
    this.subscribeToBasket();
  }

  // ── Write-through item mutations ───────────────────────────────────
  // Every quote-item mutation (picker add, qty stepper, remove, cart
  // import, bundle add) routes through these so the basket store stays
  // the source of truth. Basket-backed → mutate the store (its emit
  // re-syncs + re-renders via the subscription); classic → mutate
  // state.cartItems in place and renderStep, exactly as before.

  /** Add a line (dedup-by-variant bumps qty). */
  private cartAdd(item: CartItem): void {
    if (this.isBasketBacked()) {
      try { getBasket().add(item as unknown as BasketItem); } catch { /* swallow */ }
      return;
    }
    const existing = this.state.cartItems.find(c => c.variantId === item.variantId);
    if (existing) existing.qty = Math.min(9999, existing.qty + item.qty);
    else this.state.cartItems.push(item);
    this.renderStep();
  }

  /** Set an absolute qty (<= 0 removes the line). */
  private cartSetQty(variantId: string, n: number): void {
    if (this.isBasketBacked()) {
      try { getBasket().setQty(variantId, n); } catch { /* swallow */ }
      return;
    }
    const it = this.state.cartItems.find(c => c.variantId === variantId);
    if (!it) return;
    if (!isFinite(n) || n <= 0) this.state.cartItems = this.state.cartItems.filter(c => c.variantId !== variantId);
    else it.qty = n;
    this.renderStep();
  }

  /** Remove a line entirely. */
  private cartRemove(variantId: string): void {
    if (this.isBasketBacked()) {
      try { getBasket().remove(variantId); } catch { /* swallow */ }
      return;
    }
    this.state.cartItems = this.state.cartItems.filter(c => c.variantId !== variantId);
    this.renderStep();
  }

  /** Replace the whole item set — the authoritative-seed path (cart
   *  import). Basket-backed → setItems (one emit); classic → assign. */
  private cartReplace(items: CartItem[]): void {
    if (this.isBasketBacked()) {
      try { getBasket().setItems(items as unknown as BasketItem[]); } catch { /* swallow */ }
      return;
    }
    this.state.cartItems = items;
    this.renderStep();
  }

  private async applyPrefill() {
    if (this.preview) return;
    if (!this.prefill) return;
    // Multi-item prefill (cart-page block) wins — when lineItems is
    // present we treat this as a fully-hydrated payload and skip the
    // per-item network fetch. The cart-block onclick already read
    // /cart.js with every field we need (title, variant, price, sku,
    // qty, image), so re-fetching would just waste a round trip.
    if (Array.isArray(this.prefill.lineItems) && this.prefill.lineItems.length) {
      // Two shapes accepted for the same field pair:
      //   imageUrl / image    — theme block uses imageUrl; manual snippet
      //                         can pass /cart.js's own `image` field
      //                         through directly without renaming.
      //   price (string|number) — cartItems.price is a string downstream
      //                         (fmt as $X.XX). /cart.js returns cents
      //                         as an integer; callers divide by 100 to
      //                         get dollars-as-number, which we stringify
      //                         here. Strings ("19.99") pass through.
      // Back-compat: items with only {variantId, qty} still work — they
      // render the "Product" / $0.00 / no-image placeholder, same as
      // before. The cart-page block + the documented manual snippet
      // both pass full data so the placeholder branch shouldn't fire
      // in practice. ID-only callers can opt into a per-item fetch by
      // dropping the lineItems shape and using single-item prefill.
      // Authoritative cart seed: REPLACE staged items with exactly the
      // cart's line items (never merge onto prior/persisted state). Dedup
      // is within the seed payload itself so a doubled cart line can't
      // inflate. Build a fresh array, then assign — keeps re-entry idempotent.
      const seeded: CartItem[] = [];
      for (const li of this.prefill.lineItems) {
        const vid = li.variantId ? String(li.variantId) : "";
        if (!vid) continue;
        if (seeded.some(c => c.variantId === vid)) continue;
        const priceStr = typeof li.price === "number"
          ? li.price.toFixed(2)
          : (typeof li.price === "string" && li.price.trim()) ? li.price : "0";
        const img = (typeof li.imageUrl === "string" && li.imageUrl)
          || (typeof li.image === "string" && li.image)
          || null;
        const props = normalizeLineProperties(li.properties);
        seeded.push({
          variantId: vid,
          productId: li.productId ? String(li.productId) : "",
          title: li.title || "Product",
          variantTitle: li.variantTitle || "",
          sku: li.sku || "",
          qty: Math.max(1, Math.floor(li.qty) || 1),
          price: priceStr,
          vendor: li.vendor || "",
          imageUrl: img,
          ...(props.length ? { properties: props } : {}),
        });
      }
      // Unify: a cart-originated multi-item seed is authoritative — write
      // it straight into the basket store (the item source of truth) so
      // every surface sees the same lines. Classic (basket off) keeps the
      // per-instance assignment.
      if (this.isBasketBacked()) getBasket().setItems(seeded as unknown as BasketItem[]);
      else this.state.cartItems = seeded;
      return;
    }
    const shop = SHOP_SLUG;
    if (!shop) return;
    const variantId = this.prefill.variantId ? String(this.prefill.variantId) : "";
    const productId = this.prefill.productId ? String(this.prefill.productId) : "";
    const id = variantId || productId;
    const type = variantId ? "variant" : "product";
    if (!id) return;
    const url = `${API_ORIGIN}/api/public/product/${encodeURIComponent(id)}?shop=${encodeURIComponent(shop)}&type=${type}`;
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) return;
    const it = await r.json();
    if (!it || !it.variantId) return;
    // Properties were already resolved once, in api.openRequest() (see
    // resolveOpenRequestProperties) — this just carries them onto the
    // seeded line, same as the lineItems branch above does with its own
    // per-line `properties`. Never re-captured here.
    const properties = this.prefill.properties;
    const line: CartItem = {
      variantId: it.variantId,
      productId: it.productId,
      title: it.title,
      variantTitle: it.variantTitle || "",
      sku: it.sku || "",
      qty: 1,
      price: it.price || "0",
      vendor: it.vendor || "",
      imageUrl: it.imageUrl || null,
      // Inventory fields — present on V1+ /api/public/product/[id]
      // responses; absent on older cached blobs (back-compat safe
      // because badge render gates on the optional fields).
      availableForSale: it.availableForSale,
      inventoryPolicy: it.inventoryPolicy,
      inventoryQuantity: it.inventoryQuantity ?? null,
      discontinued: it.discontinued,
      ...(properties && properties.length ? { properties } : {}),
    };
    // Unify: seed the product into the basket store (idempotent — a
    // variant already in the quote is left as-is, mirroring the classic
    // "clicked the button twice" bail rather than bumping qty). Classic
    // (basket off) keeps the per-instance push with the same bail.
    if (this.isBasketBacked()) {
      if (!getBasket().getItems().some(c => c.variantId === it.variantId)) {
        getBasket().add(line as unknown as BasketItem);
      }
      return;
    }
    // Bail if the customer already added this exact variant (e.g.
    // they clicked the button twice or imported the cart first).
    if (this.state.cartItems.some(c => c.variantId === it.variantId)) return;
    this.state.cartItems.push(line);
  }

  async boot() {
    // Fast-path: hydrate config from localStorage cache and render
    // the form before the network revalidation completes. First
    // page-load is still cold (no cached entry yet); repeat clicks
    // within 5 min skip the ~200-400ms config fetch.
    //
    // For prefill cases (single product OR a hydrated lineItems
    // payload), we run applyPrefill before the first paint so the
    // customer doesn't see an empty-cart flash before their product
    // pops in. lineItems is fully hydrated → no fetch; productId/
    // variantId costs one extra fetch but in parallel with the
    // network revalidation below, so still wins overall.
    const cachedEntry = readCachedShopConfig(SHOP_SLUG, this.formId);
    const cachedCfg = cachedEntry?.config || null;
    const cachedVersion = cachedEntry?.version ?? -1;
    const hasPrefill = !!(this.prefill && (this.prefill.variantId || this.prefill.productId || (this.prefill.lineItems && this.prefill.lineItems.length)));
    if (cachedCfg) {
      this.config = cachedCfg as ShopConfig;
      this.configVersion = cachedVersion >= 0 ? cachedVersion : 0;
      this.hydrateFromStorage();
      // Unify: seed the basket from a legacy draft ONCE (before prefill),
      // then applyPrefill writes into the basket, then state.cartItems is
      // synced from it + the live subscription wired. No-ops when the
      // basket is off (classic form-draft item ownership).
      this.migrateLegacyDraftItems();
      if (hasPrefill) {
        await this.applyPrefill().catch(() => { /* swallow — empty form is safe */ });
      }
      this.seedAndSubscribeBasket();
      this.startPersistLoop();
      this.renderShell();
      this.renderStep();
    }

    // Load config, Shopify probe, and customer detection in parallel.
    // Customer detection is best-effort — anonymous browsers and
    // non-Shopify storefronts return null and the form just renders
    // empty as before.
    const [cfgResult, shopifyDetected, customerCtx] = await Promise.all([
      fetchShopConfigWithVersion(this.formId),
      detectShopifyStorefront(),
      detectShopifyCustomer(),
    ]);
    // Unrecoverable shop state (404 — unknown or inactive). Don't
    // render the form; surface a friendly panel mirroring
    // /request/[shopSlug]'s copy so a merchant who's mid-uninstall
    // or who has a typo in their snippet sees consistent UX.
    if (cfgResult === null) {
      this.renderInactiveShop();
      return;
    }
    const cfg = cfgResult.config;
    const fetchedVersion = cfgResult.version;
    this.config = cfg;
    this.configVersion = fetchedVersion;
    writeCachedShopConfig(SHOP_SLUG, cfg, fetchedVersion, this.formId);
    this.state.shopifyDetected = shopifyDetected;
    // If we already painted from a cached config and the network
    // returned a newer version (merchant edited settings since the
    // cache write), force a re-render with the fresh config. Same-
    // version revalidation skips the re-render so we don't reflow
    // for nothing.
    const cacheStale = cachedCfg && fetchedVersion > cachedVersion;

    // Hydrate persisted draft + start the persist loop only if the
    // cached fast-path didn't already do it.
    if (!cachedCfg) {
      this.hydrateFromStorage();
      // Unify: one-time legacy-draft → basket migration (mirrors the
      // cache path). Runs before applyPrefill so a product-button seed
      // lands ON TOP of the migrated quote rather than skipping it.
      this.migrateLegacyDraftItems();
    }

    // Pre-fill with the product the customer was looking at when
    // they clicked the theme app extension's Request Quote button.
    // variantId wins over productId (more specific); the public
    // endpoint resolves either form. Best-effort: a deleted variant
    // / network blip silently no-ops so the form still renders.
    if (!cachedCfg && hasPrefill) {
      // Cache miss path — fast-path hadn't run applyPrefill yet.
      // (Cache hit already awaited applyPrefill above; running it
      // again would be wasted work even though it dedupes.)
      await this.applyPrefill().catch(() => { /* swallow — empty form is the safe fallback */ });
    }

    // Start the persist loop. Writes every 1s when state changed.
    // Cleared on submit success + Send-to-cart. Skip if the cached
    // fast-path already started it.
    if (!cachedCfg) {
      this.startPersistLoop();
    }

    // Pre-count Shopify cart so we know whether to show the Import button.
    // Deliberately NOT auto-imported here (fix/no-cart-autoimport-on-
    // basket-entry): any prefill-less open on /cart — including the View
    // Quote edge tab (openBasketReview(), no prefill) — used to pass this
    // gate and call importCart(), which replaces the PERSISTED basket with
    // the live cart. That silently destroyed a quote accumulated across
    // pages. Importing is now exclusively a customer action: the manual
    // Import affordance (gated on importableCount > 0) or the explicit
    // ?import=cart deep-link below.
    if (shopifyDetected) {
      const cart = await fetchShopifyCart();
      this.state.importableCount = cart.length;
      this.state.shopifyCartItems = cart;
    }

    // URL param handlers — ?import=cart / ?import_product=<id> deep-links
    // (the cart-page block + any external builder_url deep-links) auto-fire
    // the matching action on arrival at the inline builder page. (The quick
    // form no longer generates ?import_product — it escalates in place —
    // but the handler stays for existing links.) Strip the params after
    // firing so a refresh doesn't re-import. Order: cart first (multi-item),
    // then product (single-item) — the existing dedup by variantId in
    // importCart/applyPrefill handles overlap.
    if ((MODE === "inline" || MODE === "builder") && typeof window !== "undefined") {
      try {
        const u = new URL(window.location.href);
        const importCart = u.searchParams.get("import") === "cart";
        const importProduct = u.searchParams.get("import_product") || "";
        let consumed = false;
        if (importCart && shopifyDetected) {
          await this.importCart().catch(() => { /* swallow — empty cart is safe */ });
          consumed = true;
        }
        if (importProduct) {
          const tempPrefill = this.prefill;
          this.prefill = { variantId: importProduct };
          await this.applyPrefill().catch(() => { /* swallow */ });
          this.prefill = tempPrefill;
          consumed = true;
        }
        if (consumed) {
          u.searchParams.delete("import");
          u.searchParams.delete("import_product");
          window.history.replaceState(null, "", u.toString());
        }
      } catch { /* swallow — URL parsing edge */ }
    }

    // Phase 2: seed the form with the detected customer. Every field
    // we set here is also editable downstream — the customer can
    // override anything we pre-filled. We only write to empty slots
    // so a future hot re-mount doesn't clobber in-progress edits.
    if (customerCtx) {
      this.state.customerContext = customerCtx;
      this.state.firstName ||= customerCtx.firstName;
      this.state.lastName  ||= customerCtx.lastName;
      this.state.email     ||= customerCtx.email;
      this.state.phone     ||= customerCtx.phone || "";

      // B2B: stamp company name + pre-select the first location.
      // Single-location companies just inherit that location's
      // address; multi-location renders a dropdown in step 2 that
      // can switch the selection.
      const co = customerCtx.company;
      if (co) {
        this.state.company ||= co.name;
        // Pick the first location with an address as the default
        // selection. (Locations without addresses still render in
        // the dropdown so the customer can pick one and edit
        // address manually.)
        const firstWithAddr = co.locations.find(l => l.address) || co.locations[0];
        if (firstWithAddr) {
          this.state.selectedLocationId = firstWithAddr.id;
          const a = firstWithAddr.address;
          if (a) {
            // Country — kickoffs/address-country-ingestion.md. `country`
            // defaults to "US" in initialState() (unlike address/city/
            // state/zip, which default to ""), so the `||=` pattern the
            // sibling fields use would never fire here — it'd always see
            // a truthy "US" and skip the real value. Guard on
            // `!this.state.address` instead: the same "still untouched"
            // signal the address/city/state/zip assignments below rely on,
            // checked before they run.
            if (a.countryCode && !this.state.address) this.state.country = a.countryCode;
            this.state.address ||= a.address1;
            this.state.city    ||= a.city;
            this.state.state   ||= a.province;
            this.state.zip     ||= a.zip;
          }
        }
      } else if (customerCtx.defaultAddress) {
        // Regular customer — fall back to their default address.
        const a = customerCtx.defaultAddress;
        // Country — see the B2B branch above for why this isn't `||=`.
        if (a.countryCode && !this.state.address) this.state.country = a.countryCode;
        this.state.address ||= a.address1;
        this.state.city    ||= a.city;
        this.state.state   ||= a.province;
        this.state.zip     ||= a.zip;
      }
    }

    // First-paint path (no cached config) always renders here. Fast-
    // path already rendered before this point; only re-render if the
    // server's config version moved (merchant edit since cache write)
    // OR we just learned something the fast path didn't have
    // (customerCtx autofill, importableCount) OR shopifyDetected
    // resolved to a different value than what the fast-path paint
    // assumed (see paintedShopifyDetected — fix, embed-mobile-batch
    // #2; catches the warm-cache + empty-cart case the other
    // conditions all miss, without repainting on every Shopify
    // storefront the way an unconditional `|| shopifyDetected` would).
    // The former always applies on cache miss; the latter cases set
    // fields that the current render call will pick up.
    // Unify: sync state.cartItems from the basket + wire the live
    // subscription now that hydrate/migrate/applyPrefill/cart-import have
    // all settled the store. Idempotent — the cache fast-path already did
    // this, and re-running just picks up any items the network phase added
    // (cart auto-seed, ?import= deep-links). No-op when the basket is off.
    this.seedAndSubscribeBasket();
    const shopifyDetectedChanged = shopifyDetected !== this.paintedShopifyDetected;
    const shouldRender = !cachedCfg || cacheStale || !!customerCtx
      || (shopifyDetected && this.state.importableCount > 0) || shopifyDetectedChanged;
    if (shouldRender) {
      this.renderShell();
      this.renderStep();
      this.paintedShopifyDetected = shopifyDetected;
    }
  }

  /** Preview boot — the designer's entry. Deliberately NOT `boot()`: no
   *  config fetch (the caller injects it), no cache read/write, no storefront
   *  or customer detection, no cart probe, no persist loop, no URL-param
   *  handlers. Paints synchronously off the injected config so a debounced
   *  re-render tracks the merchant's edits without a flash. */
  bootPreview(
    config: ShopConfig,
    items: PreviewCartItem[],
    activeStep: number | null,
    showConfirmation: boolean = false,
  ) {
    this.config = config;
    this.state.cartItems = items.map((i) => ({ ...i }));
    if (showConfirmation) {
      // Seed render state directly — never call submit(). -1 is the same
      // sentinel submit() uses for "submitted, no sequence number to show":
      // it renders the real confirmation branch (renderStep, below) with the
      // merchant's confirmation_headline/body/next_steps and no `#QR-N`.
      // Mutually exclusive with activeStep — confirmation wins.
      this.state.submitted = -1;
    } else {
      // Clamp: the injected form's section order decides how many steps
      // exist, and the caller's carried-over step may be past the end (e.g.
      // Products was just toggled off).
      if (activeStep != null) {
        this.state.step = Math.max(0, Math.min(this.stepDefs.length - 1, activeStep));
      }
    }
    this.renderShell();
    this.renderStep();
  }

  /** Current wizard step index — read by renderPreview to carry the merchant's
   *  position across a live re-render. */
  get previewStep(): number {
    return this.state.step;
  }

  /** Drop every resource this instance holds. Preview re-renders build a fresh
   *  instance per paint, so a missed teardown would leak an interval + a store
   *  subscription per keystroke. (Both are already inert under `preview`, but
   *  destroy() is the honest contract and keeps this safe if a future caller
   *  reuses it outside preview.) */
  destroy() {
    this.stopPersistLoop();
    if (this.basketUnsub) {
      try { this.basketUnsub(); } catch { /* ignore */ }
      this.basketUnsub = null;
    }
  }

  // Rendered when fetchShopConfig() returns null — the shop is
  // unknown or inactive. Neutral chrome (no brand, no per-shop
  // colors) since we explicitly don't know which merchant to
  // represent. Copy mirrors /request/[shopSlug]'s same-named state.
  private renderInactiveShop() {
    const style = el("style", { html: `
      :host, * { box-sizing: border-box; }
      .qb-inactive-root {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif;
        color: #0f172a; line-height: 1.55; font-size: 14px;
        background: #f1f5f9; padding: 32px 16px;
        display: flex; align-items: center; justify-content: center;
        min-height: 240px;
      }
      .qb-inactive-card {
        background: #fff; border: 1px solid #e2e8f0; border-radius: 14px;
        padding: 28px 28px 24px; max-width: 480px; width: 100%;
        box-shadow: 0 1px 3px rgba(15,23,42,.04);
      }
      .qb-inactive-title { font-size: 17px; font-weight: 800; color: #0f172a; margin-bottom: 6px; }
      .qb-inactive-body  { font-size: 14px; color: #64748b; line-height: 1.55; }
    ` });
    const root = el("div", { class: "qb-inactive-root" },
      el("div", { class: "qb-inactive-card" },
        el("div", { class: "qb-inactive-title" }, "This shop isn't available"),
        el("div", { class: "qb-inactive-body" },
          "The link you followed points to a shop that's been removed or isn't accepting requests right now. Double-check the URL or contact the merchant directly.",
        ),
      ),
    );
    this.shadow.replaceChildren(style, root);
  }

  // Pull style + structural scaffold into the shadow root once.
  private renderShell() {
    const primary = this.resolvePrimary();
    const accent = this.resolveAccent();
    const bg = this.resolveFormBg();

    // Resolve the four design tokens from config (single source of truth,
    // shared with the request page + in-app preview). The per-instance
    // formScale override (theme-block data-form-scale) still wins for size
    // when explicitly set; otherwise the config's form_size drives scaling.
    const tokens = resolveFormDesignTokens(this.config);
    const scale = this.formScale !== 1 ? this.formScale : tokens.scale;
    const style = el("style", { html: buildCss(primary, accent, scale, tokens, bg) });

    // Builder mode: skip the merchant-brand header (the merchant's
    // own page already provides chrome) and skip the sticky nav-
    // footer (no Back/Next nav — builder uses a single inline
    // Submit at the bottom of the scroll content). Section cards
    // mount directly into qb-shell as plain document-flow content;
    // no internal scroll, no panel-shape — the merchant page's
    // natural scroll handles everything.
    if (this.layoutMode === "builder") {
      // Resolve wrapper style from merchant config. Default
      // "bordered" so the form has visible containment on plain
      // pages; merchants on already-styled pages can flip to
      // "transparent" in /settings/forms/builder. Resolver prefers
      // top-level config.builder_form_style, falls back to legacy
      // nested quick_form.builder_form_style.
      const wrapperStyle = resolveBuilderFormStyle(this.config);
      const shell = el("div", { class: "qb-shell" });
      const root = el("div", {
        class: `qb-root qb-builder qb-builder-${wrapperStyle}`,
        "data-version": VERSION,
      }, shell);
      this.shadow.replaceChildren(style, root);
      return;
    }

    // Merchant-brand header is suppressed on storefront wizard
    // inline mounts — the merchant's theme already provides their
    // brand chrome, so our bar reads as duplicate branding. The
    // hosted /request/<slug> page (no Shopify context) still gets
    // the header because there the form IS the page. Modal +
    // drawer paths always carry the header (it doubles as the
    // close-X mount).
    // Overlay (modal/drawer floating panel) ALWAYS carries the header
    // strip — it frames the flat panel and identifies the shop. Only a
    // storefront INLINE mount (form embedded in page flow, merchant
    // chrome already present) suppresses it to avoid duplicate branding.
    const isOverlay = !!this.host.closest("[data-qb-wrapper]");
    const skipHeader = MODE === "inline" && this.state.shopifyDetected && !isOverlay;
    const header = skipHeader
      ? null
      : (() => {
          // Header text (fix, form-header-declutter-footer #2) — was the
          // company name (brand_name), which duplicated the logo right
          // next to it (logo IS the brand). Now the action title "Request
          // a Quote", so header + logo together read as "[logo] Request a
          // Quote" instead of "[logo] Strength Warehouse USA". No
          // merchant-configurable form-title/heading field exists in
          // ShopConfig (checked) — static string per the kickoff's
          // fallback instruction.
          const headerTitle = "Request a Quote";
          // brand_name still drives the logo-less fallback initial (the
          // chip needs to represent the SHOP, not the generic action
          // title) — unaffected by the header text change above.
          const brandForInitial = this.config.brand_name || "Request a Quote";
          // Logo — rendered at natural aspect on a white/light badge
          // (contained, never cropped into a fixed square) so a wide or
          // tall logo shows correctly instead of a zoomed fragment. No
          // logo_url falls back to the shop's initial on an accent chip.
          // Routed through our own origin, not this.config.logo_url
          // directly — an uploaded logo now lives in a PRIVATE blob a
          // storefront visitor's browser can't fetch on its own
          // (kickoffs/brand-logo-upload.md §2), and a merchant-pasted
          // external URL may sit behind bot protection the proxy
          // sidesteps too, same as the print page + customer quote page.
          const chip = this.config.logo_url
            ? el("div", { class: "qb-logo-badge", "aria-hidden": "true" },
                el("img", { src: `${API_ORIGIN}/api/public/logo-proxy?shop=${encodeURIComponent(SHOP_SLUG)}&v=${this.configVersion}`, alt: "" }))
            : el("div", { class: "qb-logo-chip", "aria-hidden": "true" },
                document.createTextNode((brandForInitial.trim()[0] || "Q").toUpperCase()));
          const headerLeft = el("div", { class: "qb-header-brand" },
            chip,
            el("span", { class: "qb-header-name" }, headerTitle),
          );
          // In an overlay the light-DOM panel supplies its own circular
          // close (top-right), so the header omits one to avoid a
          // duplicate. Non-overlay modal/drawer keep the header close.
          const headerRight = ((MODE === "modal" || MODE === "drawer") && !isOverlay)
            ? el("button", { class: "qb-close-x", "aria-label": "Close", onclick: () => this.close() }, "✕")
            : null;
          return el("header", { class: "qb-header" }, headerLeft, headerRight || undefined);
        })();

    const shell = el("div", { class: "qb-shell" });
    // Sticky nav footer — lives as a sibling of qb-shell inside
    // qb-root. The form's Back/Next/Submit buttons render here, not
    // inside qb-card, so they stay glued to the bottom of the modal/
    // drawer viewport regardless of how far the form has scrolled.
    // renderStep() updates the contents on every step change.
    const navFooter = el("div", { class: "qb-nav-footer" });
    // Root is marked `qb-overlay` (isOverlay resolved above) when this
    // builder lives inside an openOverlay wrapper (modal/drawer). The CSS
    // gates layout + sticky-nav visuals on this class so inline mode
    // stays untouched.
    const root = el("div", {
      class: isOverlay ? "qb-root qb-overlay" : "qb-root",
      "data-version": VERSION,
    }, header || undefined, shell, navFooter);

    this.shadow.replaceChildren(style, root);
  }

  private close() {
    // Unify: drop the basket subscription so a discarded overlay doesn't
    // keep re-rendering a detached shadow tree on later store changes.
    if (this.basketUnsub) {
      try { this.basketUnsub(); } catch { /* ignore */ }
      this.basketUnsub = null;
    }
    // Tear down any modal/drawer wrapper that this builder may live in.
    // The wrapper carries a __qbBeforeClose hook (set in openOverlay) so
    // body-scroll lock + any other side effects can unwind before the
    // DOM nodes go away.
    const wrapper = this.host.closest("[data-qb-wrapper]") as HTMLElement | null;
    if (wrapper) {
      try { (wrapper as any).__qbBeforeClose?.(); } catch { /* never block teardown */ }
      wrapper.remove();
    }
  }

  /** Resolve a step's label. form_steps.<step>.label wins; legacy
   *  single-keys are the fallback; hardcoded defaults are last. Inlined
   *  here for the embed bundle — mirrors resolveStepLabel() in
   *  src/lib/form-steps.ts. */
  private stepLabel(step: "about" | "shipping" | "build" | "finish"): string {
    const fromNew = this.config.form_steps?.[step]?.label;
    if (typeof fromNew === "string" && fromNew.trim().length > 0) return fromNew;
    const legacy = step === "about" ? this.config.about_you_label
      : step === "shipping" ? this.config.shipping_label
      : step === "build" ? this.config.build_quote_label
      : this.config.finish_up_label;
    if (typeof legacy === "string" && legacy.trim().length > 0) return legacy;
    return step === "about" ? "About You"
      : step === "shipping" ? "Shipping"
      : step === "build" ? "Build Your Quote"
      : "Finish Up";
  }

  private stepIntro(step: "about" | "shipping" | "finish"): string {
    const v = this.config.form_steps?.[step]?.intro;
    return typeof v === "string" ? v : "";
  }

  /** Build-step product-search LABEL. Mirrors resolveBuildSearchLabel()
   *  in src/lib/form-steps.ts — inlined (the bundle can't import @/lib).
   *  New key → form_steps.build.search_label, else the hard default. */
  private buildSearchLabel(): string {
    const v = this.config.form_steps?.build?.search_label;
    return typeof v === "string" && v.trim().length > 0 ? v : "Search products";
  }

  /** Build-step product-search PLACEHOLDER. Mirrors
   *  resolveBuildSearchPlaceholder(). New key →
   *  form_steps.build.search_placeholder, else the hard default. */
  private buildSearchPlaceholder(): string {
    const v = this.config.form_steps?.build?.search_placeholder;
    return typeof v === "string" && v.trim().length > 0 ? v : "Search by product name or SKU…";
  }

  private shippingEnabled(): boolean {
    const newKey = this.config.form_steps?.shipping?.enabled;
    if (typeof newKey === "boolean") return newKey;
    return this.config.require_shipping !== false;
  }

  private resolveNotesLabel(): string {
    const fs = this.config.form_steps?.finish?.notes_label;
    if (typeof fs === "string" && fs.trim().length > 0) return fs;
    if (typeof this.config.notes_label === "string" && this.config.notes_label.trim().length > 0) {
      return this.config.notes_label;
    }
    return "Notes / anything else";
  }

  private resolveSubmitLabel(): string {
    const fs = this.config.form_steps?.finish?.submit_label;
    if (typeof fs === "string" && fs.trim().length > 0) return fs;
    if (typeof this.config.submit_button_label === "string" && this.config.submit_button_label.trim().length > 0) {
      return this.config.submit_button_label;
    }
    // Default "Submit" (form-polish-v3 #2, was "Submit Request →"). Still
    // editable — form_steps.finish.submit_label (Finish-step copy input)
    // takes precedence, then the top-level submit_button_label.
    return "Submit";
  }

  // Step list is config-driven (docs/MULTI_FORM.md §4.3). The resolved
  // form's `section_order` maps section ids → wizard steps via the
  // shared resolveWizardSteps() helper; shippingEnabled() still gates
  // the shipping step (new form_steps.shipping.enabled, legacy
  // require_shipping as fallback). When `section_order` is ABSENT (the
  // default / un-migrated form) the helper returns today's exact order
  // (products → contact → shipping-if → finish) so existing shops render
  // byte-for-byte as before. `finish` is always last; a `consent` entry
  // is a no-op for sequencing (consent renders inside the finish area,
  // gated by consent_enabled — see renderConsent).
  private get stepDefs(): { id: StepId; label: string }[] {
    const ids = resolveWizardSteps(this.config.section_order, this.shippingEnabled());
    return ids.map(id => ({ id, label: this.stepLabelForStep(id) }));
  }

  /** Label for a resolved wizard step id. Bridges the step-order helper's
   *  ids to the existing stepLabel() copy keys (+ the new describe step's
   *  own label). */
  private stepLabelForStep(id: StepId): string {
    switch (id) {
      case "quote":    return this.stepLabel("build");
      case "about":    return this.stepLabel("about");
      case "shipping": return this.stepLabel("shipping");
      case "describe": return this.describeLabel();
      case "finish":   return this.stepLabel("finish");
    }
  }

  /** Whether the resolved form includes a first-class Describe step. When
   *  true, the standalone step owns the free-text ask — the products step
   *  suppresses its inline "Describe it instead" toggle so there's a
   *  single place to describe (avoids two competing entry points). */
  private hasDescribeStep(): boolean {
    return this.stepDefs.some(s => s.id === "describe");
  }

  /** Describe step/tab label. From DescribeSectionConfig.label, else a
   *  neutral default. Per-form overridable (Lane 1 forwards `describe`). */
  private describeLabel(): string {
    const v = this.config.describe?.label;
    return typeof v === "string" && v.trim().length > 0 ? v.trim() : "Describe your project";
  }
  private get currentStepId(): StepId {
    const defs = this.stepDefs;
    return defs[Math.min(this.state.step, defs.length - 1)]?.id || "about";
  }

  /** sid → renderer dispatch. Single source of truth for both the
   *  wizard (one step per screen) and the builder layout (all steps
   *  stacked in one scroll) — extracted so the two layouts render each
   *  section identically and can never diverge again. */
  private renderSectionInto(sid: StepId, card: HTMLElement) {
    if (sid === "about") this.renderStep0(card);
    else if (sid === "shipping") this.renderStep1(card);
    else if (sid === "quote") this.renderStep2(card);
    else if (sid === "describe") this.renderDescribeStep(card);
    else if (sid === "finish") this.renderStep3(card);
  }

  /** Quick-form gate. Renders the self-contained quick form (Screen 1
   *  + Screen 2 picker) INSTEAD of the full 4-step wizard when:
   *    - this drawer instance was opened with formMode "quick_expandable"
   *    - merchant config has quick_form.enabled !== false
   *    - we're not on the confirmation screen
   *  The caller's explicit formMode choice wins — even with no prefilled
   *  product, we render the quick form's empty-cart state (which surfaces
   *  the picker CTA inline). This matches the Floating CTA + manual
   *  install path where the merchant picks quick without product context;
   *  the customer adds items via the picker before submitting.
   *  Failure drops through to the full 4-step wizard. */
  private shouldRenderQuickForm(): boolean {
    if (this.formMode !== "quick_expandable") return false;
    if (this.state.submitted) return false;
    if (this.config.quick_form?.enabled === false) return false;
    return true;
  }

  /** Sticky bit — flipped true the first time shouldRenderQuickForm
   *  observes a non-empty cart (or the picker screen). Used so the
   *  empty-cart fallback (after the customer removes the last line
   *  item) still renders quick mode instead of falling back to the
   *  full wizard with no products. Cleared when the QuoteBuilder
   *  instance tears down. */
  private quickFormSessionStarted = false;

  /** Single repaint seam for custom-field controls that can gate another
   *  field's `show_when` visibility (select, multiselect). renderStep()
   *  already dispatches on `this.layoutMode` internally — wizard mode
   *  rebuilds the current step's card, builder mode rebuilds the whole
   *  single-scroll shell via renderBuilderLayout (which re-derives every
   *  section's visible fields from state.customValues and restores the
   *  document scroll position). Call this instead of this.renderStep()
   *  directly from a field's change handler so there is exactly one named
   *  entry point to reach for — no per-call-site "if (builder) … else …"
   *  branching, and no risk of a future control repainting only one
   *  layout by hand. */
  private repaintActiveSurface() {
    this.renderStep();
  }

  private renderStep() {
    const shell = this.shadow.querySelector(".qb-shell") as HTMLElement;
    const navFooter = this.shadow.querySelector(".qb-nav-footer") as HTMLElement | null;
    if (!shell) return;
    // Builder mode is document-flow (no internal scroll container) —
    // its host page's scrollY is the only scroll position that
    // matters. Custom-field checkbox/select changes wipe + remount
    // the section DOM via renderStep, which (without preservation)
    // resets the document to scrollTop=0. Capture before the
    // teardown and restore after the new tree is in place so
    // toggling a checkbox doesn't yank the customer to the top of
    // the page. Wizard / overlay modes have their own scroll
    // container (qb-shell) so they're unaffected by document scroll.
    const preserveDocScroll = this.layoutMode === "builder" && typeof window !== "undefined";
    const savedScrollY = preserveDocScroll ? window.scrollY : 0;
    if (this.state.submitted) {
      this.renderConfirmation(shell);
      // No nav after submit — clear the sticky footer so the
      // confirmation screen isn't sitting under an empty white bar.
      if (navFooter) navFooter.replaceChildren();
      // Builder mode lives in the merchant page's natural scroll;
      // the scrollY-preservation guard above would leave the
      // customer scrolled at the submit button's old position
      // (typically deep into the page) instead of looking at the
      // confirmation card. Scroll to the embed host so the "request
      // is in" card lands at the top of the viewport.
      if (this.layoutMode === "builder" && typeof window !== "undefined") {
        try {
          const top = this.host.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: Math.max(0, top - 16), behavior: "smooth" });
        } catch { /* swallow — confirmation card is still rendered */ }
      }
      return;
    }
    // Quick-mode short-circuit: skip the step strip but REUSE the
    // existing qb-nav-footer slot for the CTA tray (consent +
    // submit). That slot is a sibling of qb-shell inside qb-root's
    // flex column — already battle-tested by the full wizard's
    // Back / Next nav, so the panel-bottom pinning + iOS safe-area
    // inset Just Work without inventing a new sticky chain inside
    // qb-card. renderQuickForm fills qb-shell with the scrollable
    // form content (line items / picker / contact fields) and
    // returns the CTA tray DOM that the dispatcher mounts into
    // navFooter.
    if (this.shouldRenderQuickForm()) {
      if (this.state.cartItems.length > 0 || this.state.quickPickerOpen) {
        this.quickFormSessionStarted = true;
      }
      shell.replaceChildren();
      const ctaTray = this.renderQuickForm(shell);
      if (navFooter) {
        navFooter.replaceChildren();
        if (ctaTray) navFooter.appendChild(ctaTray);
      }
      return;
    }
    // Builder layout — single scrolling page, all sections stacked,
    // single Submit at the bottom. Replaces the wizard's per-step
    // strip + Back/Next nav. Used by data-mode="builder" mounts on
    // dedicated quote-builder pages.
    if (this.layoutMode === "builder") {
      this.renderBuilderLayout(shell, navFooter);
      // Restore the captured document scroll position synchronously
      // so the customer's vantage point survives a field change. A
      // microtask defer would let the browser paint at scrollY=0
      // first (visible flicker). Use rAF as a fallback if direct
      // scrollTo lands before layout settles.
      if (preserveDocScroll && savedScrollY > 0) {
        try { window.scrollTo({ top: savedScrollY, behavior: "instant" as ScrollBehavior }); } catch {
          window.scrollTo(0, savedScrollY);
        }
      }
      return;
    }
    shell.replaceChildren();

    const defs = this.stepDefs;
    const sid = this.currentStepId;

    // Steps strip (desktop) + compact (mobile). Single-active-only
    // highlighting (fix, form-header-declutter-footer #4) — was
    // cumulative (every step up to + including the current one turned
    // accent, a "progress trail" that grew from 1 colored rail on step 1
    // to all 4 on the last step). That made step 1's row look like the
    // odd one out next to the fuller-looking later steps even though the
    // rails are all the same dimensions. Exactly one rail/label is accent
    // (the current step) on every step-view now — same pattern
    // everywhere, only WHICH one differs.
    const stepsStrip = el("div", { class: "qb-steps" });
    defs.forEach((s, i) => {
      stepsStrip.appendChild(el("div", { class: "qb-step" },
        el("div", { class: `qb-step-bar ${i === this.state.step ? "active" : ""}` }),
        el("div", { class: `qb-step-label ${i === this.state.step ? "active" : ""}` }, s.label),
      ));
    });
    const compactStep = el("div", { class: "qb-step-compact" });
    compactStep.innerHTML = `Step ${this.state.step + 1} of ${defs.length} · <strong style="color:#0f172a">${escapeHtml(defs[this.state.step]?.label || "")}</strong>
      <div class="qb-progress"><div class="qb-progress-fill" style="width:${((this.state.step + 1) / defs.length) * 100}%"></div></div>`;

    // No in-form step-name heading here (fix, form-header-declutter-footer
    // #2) — it duplicated the step tabs above (.qb-steps / .qb-step-compact
    // already show the current step's name) and the branded header already
    // says "Request a Quote". Step INTRO/subtext (renderStepIntro, called
    // from renderStep0/1/3) is unaffected — that's supplementary copy, not
    // a repeat of the step name.
    const card = el("div", { class: "qb-card" });

    if (this.state.errors.length) {
      const errBox = el("div", { class: "qb-errors" });
      this.state.errors.forEach(e => errBox.appendChild(el("div", {}, e)));
      card.appendChild(errBox);
    }

    this.renderSectionInto(sid, card);

    // Phase 2 banner — anchored above the steps strip so it stays
    // visible on every step (the customer doesn't need to remember
    // they were recognized). Anonymous mode renders nothing.
    const banner = this.buildCustomerBanner();
    if (banner) shell.appendChild(banner);

    shell.appendChild(stepsStrip);
    shell.appendChild(compactStep);
    shell.appendChild(card);

    // ── Sticky nav footer ────────────────────────────────────────────
    // Lives outside qb-shell (the scroll container) and outside
    // qb-card (the field container) so it never scrolls with the
    // form. The footer is the third child of qb-root after qb-header
    // + qb-shell; flex-shrink:0 keeps it pinned to the bottom of the
    // panel viewport in overlay mode and sits below the form in
    // inline mode.
    if (navFooter) {
      const isLast = this.state.step >= defs.length - 1;
      // Left slot — Back button on steps 2+, empty spacer on step 1
      // so the right-side action stays right-aligned via the
      // space-between layout.
      const left = this.state.step > 0
        ? el("button", {
            class: "qb-btn qb-btn-ghost",
            onclick: () => { this.state.errors = []; this.state.step--; this.renderStep(); },
          }, "← Back")
        : el("div");
      // Center slot — a column holding "Step N of M" and, beneath it,
      // the "Questions? <email>" line (form-polish-v3 #3). Desktop: both
      // stack centered between Back and Submit. Mobile: the step text is
      // hidden (CSS below ~520px), so "Questions?" alone sits centered in
      // the Back/Next tier — not a full new line stacked below the footer.
      const center = el("div", { class: "qb-nav-center" });
      center.appendChild(el("div", { class: "qb-step-indicator" },
        `Step ${this.state.step + 1} of ${defs.length}`,
      ));
      if (this.config.support_email) {
        // escapeHtml guards a malicious support_email; server PATCH
        // validation also rejects non-emails — defense in depth.
        const qLine = el("div", { class: "qb-footer-meta", style: "margin:0" });
        const safeEmail = escapeHtml(this.config.support_email);
        qLine.innerHTML = `Questions? <a href="mailto:${safeEmail}">${safeEmail}</a>`;
        center.appendChild(qLine);
      }
      // "Powered by QuoteSense" (tier-gating Lane 3 §4) — beneath the
      // "Questions?" line, footer only (never the header — the form is
      // the merchant's conversion surface). Server-computed flag read
      // straight off config; no tier logic in this bundle.
      if (this.config.show_powered_by) {
        const badge = el("div", { class: "qb-badge-meta", style: "margin:0" });
        badge.innerHTML = `<a href="https://quotesense.io" target="_blank" rel="noopener noreferrer">Powered by QuoteSense</a>`;
        center.appendChild(badge);
      }
      // Right slot — Next on intermediate steps, Submit on final.
      // Block submit while any attachment is still uploading — the
      // customer would otherwise miss including a file they're
      // visibly waiting on. Errored entries don't block (the
      // customer can remove + retry or just submit without them).
      const uploadsPending = this.state.attachments.some(a => a.status === "uploading");
      const right = isLast
        ? el("button", {
            class: "qb-btn qb-btn-primary",
            disabled: (this.state.submitting || uploadsPending) ? "true" : undefined as any,
            onclick: () => void this.submit(),
          }, this.state.submitting
              ? "Submitting…"
              : uploadsPending
                ? "Uploading files…"
                : this.resolveSubmitLabel())
        : el("button", {
            class: "qb-btn",
            onclick: () => this.next(),
          }, "Next →");
      // Back / Step N of M (+ Questions beneath) / Next|Submit ride in a
      // row; the nav-footer is a column via .qb-nav-footer:has(.qb-nav-row).
      const navRow = el("div", { class: "qb-nav-row" }, left, center, right);
      const footerChildren: Node[] = [];
      // Build step: the running subtotal is PINNED in the footer (above
      // the nav row), matching the quick form (form-polish-v3 #5a) — it
      // was inline mid-content before. Only on the Build step, only with
      // items. buildSubtotalCard renders the same block the quick form
      // uses (label + total, optional note).
      if (sid === "quote" && this.state.cartItems.length > 0) {
        footerChildren.push(this.buildSubtotalCard());
      }
      footerChildren.push(navRow);
      navFooter.replaceChildren(...footerChildren);
    }
  }

  // ── Quick form (storefront-drawer single-screen flow) ──────────────────
  // Multi-product, fully self-contained. Two sub-screens within the
  // drawer:
  //   Screen 1 ("form"):    line items list (each with qty stepper +
  //                         remove) + "+ Add more products" CTA +
  //                         contact fields + submit.
  //   Screen 2 ("picker"):  search-products screen with results +
  //                         variant pickers, slides in from the right.
  // The "expand to full wizard" affordance is gone — quick mode is
  // self-contained. Inputs reuse .qb-input / .qb-label / .qb-btn from
  // the rest of the embed.
  /** Returns the CTA tray (consent + submit) DOM so the
   *  dispatcher can mount it into qb-nav-footer instead of nesting
   *  it inside the scrolling qb-card. Null when there's no submit
   *  button to render (e.g. submitted state — but the dispatcher
   *  short-circuits before calling us in that case, so callers
   *  always get the tray). */
  private renderQuickForm(shell: HTMLElement): HTMLElement | null {
    const qf = resolveQuickForm(this.config.quick_form, this.config);
    const accent = this.resolveQuickAccent();
    const errs = this.state.quickErrors || {};
    const hasItems = this.state.cartItems.length > 0;

    // ── Scroll region: intro + product cards + add-more ──────────────
    // The pinned footer (returned as `cta`) owns contact + subtotal +
    // submit so a long product list never pushes the CTA off-screen.
    // No "Request a quote" heading here (fix, form-header-declutter-
    // footer #2) — the branded header above already says it.
    const card = el("div", { class: "qb-card qb-quick-form" });

    if (qf.intro) {
      card.appendChild(el("div", {
        style: "font-size:14px;color:#475569;margin:0 0 16px;line-height:1.55",
      }, qf.intro));
    }

    // ── Line items — two-line soft cards (shared with the wizard) ───
    if (hasItems) {
      const list = el("div", { style: "margin-bottom:12px" });
      this.state.cartItems.forEach((it) => {
        const badges = this.renderInventoryBadges({
          discontinued: it.discontinued,
          availableForSale: it.availableForSale,
          inventoryPolicy: it.inventoryPolicy,
        });
        list.appendChild(this.renderItemCard(it, { showQty: qf.show_qty, badges }));
      });
      card.appendChild(list);
    } else {
      // Recovery state — every line item removed. Show a friendly
      // message + an inline "+ Add more products" CTA so the customer
      // can rebuild their selection without closing the drawer. We
      // deliberately don't fall back to the full wizard here.
      const empty = el("div", {
        style: "padding:16px;border:1px dashed #cbd5e1;border-radius:10px;background:#f8fafc;margin-bottom:14px;text-align:center",
      });
      empty.appendChild(el("div", {
        style: "font-size:13px;color:#475569;font-weight:500;margin-bottom:6px",
      }, "Your quote is empty"));
      empty.appendChild(el("div", {
        style: "font-size:12px;color:#64748b;margin-bottom:12px;line-height:1.5",
      }, "Add at least one product to send a request."));
      empty.appendChild(el("button", {
        type: "button",
        class: "qb-btn qb-btn-primary",
        style: `background:${accent};padding:10px 16px;font-size:13px;min-height:0`,
        onclick: () => {
          this.state.quickPickerOpen = true;
          this.state.quickPickerQuery = "";
          this.state.quickPickerResults = [];
          this.renderStep();
        },
      }, "+ Add products"));
      card.appendChild(empty);
    }

    // ── "+ Add more products" — subtle full-width dashed button OR the
    //    expanded inline picker. Picker renders RIGHT HERE (below the
    //    list) when open; collapses back to the button on "Done". The
    //    escalation link in the footer is a DISTINCT action ("switch to
    //    the full form"), so the picker is always available here when
    //    multi-product is allowed.
    if (hasItems && qf.allow_multi_product) {
      if (this.state.quickPickerOpen) {
        this.renderInlinePicker(card);
      } else {
        const gateMsg = this.quickAddGateMessage(qf);
        const disabled = !!gateMsg;
        const addMoreBtn = el("button", {
          type: "button",
          class: "qb-add-more-btn",
          disabled: disabled ? "true" : undefined as any,
          onclick: () => {
            if (this.quickAddGateMessage(qf)) return;
            this.state.quickPickerOpen = true;
            this.state.quickPickerQuery = "";
            this.state.quickPickerResults = [];
            this.renderStep();
          },
        }) as HTMLButtonElement;
        addMoreBtn.appendChild(document.createTextNode("+ Add more products"));
        card.appendChild(addMoreBtn);
        if (disabled) {
          card.appendChild(el("div", {
            class: "qb-add-more-hint",
            style: "font-size:11px;color:#94a3b8;margin-top:8px;text-align:center",
          }, gateMsg!));
        }
      }
    }

    // ── Contact fields — a collapsible "Your details" section that now
    //    lives in the SCROLL region (form-polish-v3 #6b), NOT the pinned
    //    footer. Expanding it scrolls the products up out of view instead
    //    of bloating the footer, so the subtotal + Submit bar below stays
    //    pinned + reachable on mobile (previously expanding it pushed
    //    Submit off-screen). Desktop force-shows the fields (CSS media
    //    query); mobile collapses them behind a lightweight divider +
    //    chevron. Same fields + handlers as before.
    const contact = el("div", { class: "qb-quick-contact" });
    const initialName = [this.state.firstName, this.state.lastName].filter(Boolean).join(" ");
    const nameField = this.renderQuickField({
      id: "name", label: "Your name", required: true,
      value: initialName, error: errs.name,
      onInput: (v) => {
        const trimmed = v;
        const sp = trimmed.indexOf(" ");
        if (sp === -1) {
          this.state.firstName = trimmed;
          this.state.lastName = "";
        } else {
          this.state.firstName = trimmed.slice(0, sp);
          this.state.lastName = trimmed.slice(sp + 1);
        }
        this.refreshQuickAddMoreGate();
      },
    });
    nameField.classList.add("qb-span-2");
    contact.appendChild(nameField);

    // Phone + email share a row when both show; a lone contact field
    // spans full width. The OR-case (both visible, neither required)
    // gets an italic hint since asterisks can't express "at least one".
    const showBothContact = qf.show_phone && qf.show_email;
    const orCaseApplies = showBothContact && !qf.phone_required && !qf.email_required;
    if (qf.show_phone) {
      const f = this.renderQuickField({
        id: "phone", label: "Phone", required: qf.phone_required,
        value: this.state.phone, error: errs.phone, type: "tel",
        onInput: v => { this.state.phone = v; this.refreshQuickAddMoreGate(); },
      });
      if (!showBothContact) f.classList.add("qb-span-2");
      contact.appendChild(f);
    }
    if (qf.show_email) {
      const f = this.renderQuickField({
        id: "email", label: "Email", required: qf.email_required,
        value: this.state.email, error: errs.email, type: "email",
        onInput: v => { this.state.email = v; this.refreshQuickAddMoreGate(); },
      });
      if (!showBothContact) f.classList.add("qb-span-2");
      contact.appendChild(f);
    }
    if (orCaseApplies) {
      contact.appendChild(el("div", {
        class: "qb-span-2",
        style: "font-size:12px;color:#64748b;font-style:italic;margin-top:-2px;line-height:1.4",
      }, "Provide at least one — phone or email."));
    }
    if (qf.show_message) {
      const wrap = el("div", { class: "qb-field qb-span-2" });
      wrap.appendChild(el("label", { class: "qb-label" }, "Notes"));
      const ta = el("textarea", {
        class: "qb-textarea", rows: "2",
        style: "min-height:50px",
        placeholder: "Anything that'll help us serve you better.",
      });
      ta.value = this.state.notes || "";
      ta.addEventListener("input", () => { this.state.notes = ta.value; });
      wrap.appendChild(ta);
      contact.appendChild(wrap);
    }
    // Collapsible "Your details" wrapper (fix, drawer-fullheight-mobile
    // #3): on MOBILE the pinned contact grid otherwise dominates the
    // viewport and crowds the product list down to a sliver — so the
    // fields collapse behind a "Your details" toggle, letting the
    // products show on open. Subtotal + Submit stay pinned below (they
    // are separate cta children). Desktop is UNCHANGED: a pure-CSS
    // media-query collapse (checkbox hack — no JS/state) hides the
    // toggle and always shows the fields above 600px. The hidden
    // checkbox's `checked` seeds the mobile open state: expanded when a
    // contact field has a validation error so its inline error is
    // visible, collapsed otherwise. All fields + input handlers are
    // unchanged — only wrapped.
    const hasContactError = !!(errs.name || errs.phone || errs.email);
    const detailsCb = el("input", {
      type: "checkbox", id: "qb-quick-details-cb", class: "qb-quick-details-cb",
    }) as HTMLInputElement;
    detailsCb.checked = hasContactError;
    const detailsSummary = el("label", {
      class: "qb-quick-details-summary", for: "qb-quick-details-cb",
    },
      el("span", {}, "Your details"),
      el("span", {
        class: "qb-quick-details-chevron", "aria-hidden": "true",
        html: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>',
      }),
    );
    const details = el("div", { class: "qb-quick-details" },
      detailsCb,
      detailsSummary,
      el("div", { class: "qb-quick-details-body" }, contact),
    );
    card.appendChild(details);

    // ── Custom question — the quick form's one optional binary (Yes/No)
    //    field (kickoff: quick-form-binary-field.md). Beneath the contact
    //    fields, above consent/Submit (which live in the pinned footer
    //    below). Reuses the Preferred Contact Method control's toggle-row
    //    markup/classes — no new control. Off by default; renders nothing
    //    when unconfigured or disabled.
    const cf = qf.custom_field;
    if (cf && cf.enabled) {
      const wrap = el("div", { class: "qb-field" });
      wrap.appendChild(el("label", { class: "qb-label" }, cf.label + (cf.required ? " *" : "")));
      const toggleRow = el("div", { class: "qb-toggle-row" });
      (["yes", "no"] as const).forEach(v => {
        const b = el("button", {
          type: "button",
          class: `qb-btn ${this.state.customValues[cf.id] === v ? "active" : ""}`,
          onclick: () => {
            this.state.customValues = { ...this.state.customValues, [cf.id]: v };
            this.renderStep();
          },
        }, v === "yes" ? cf.true_label : cf.false_label);
        toggleRow.appendChild(b);
      });
      wrap.appendChild(toggleRow);
      if (cf.help_text) {
        wrap.appendChild(el("div", { style: "font-size:12px;color:#94a3b8;margin-top:6px;line-height:1.45" }, cf.help_text));
      }
      if (errs.custom_field) {
        wrap.appendChild(el("div", {
          style: "font-size:12px;color:#b91c1c;font-weight:600;margin-top:6px",
        }, errs.custom_field));
      }
      card.appendChild(wrap);
    }

    shell.appendChild(card);

    // ── Pinned footer (sticky bottom bar): subtotal → submit → consent
    //    → escalation → email. FIXED content — it never grows with the
    //    "Your details" reveal above (which is in the scroll region), so
    //    the Submit button is always reachable on mobile.
    const cta = el("div", { class: "qb-quick-cta" });

    // Subtotal block — same component the wizard uses (no Send-to-cart
    // on the quick form). Only when there are items to total.
    if (hasItems) cta.appendChild(this.buildSubtotalCard());

    const submitErr = errs._submit;
    if (submitErr) {
      cta.appendChild(el("div", {
        class: "qb-errors", style: "margin:0",
      }, submitErr));
    }
    // Submit is disabled when the cart is empty (recovery state) or
    // while submitting. Server validates again so this is just UX.
    // Inventory gate: each line item failed-checks against the
    // narrower of the two policies — discontinued items only block
    // when block_discontinued is on; OOS items only block when
    // block_out_of_stock is on. Splitting (was a single
    // inventory_strict) lets merchants run "allow backorder on OOS,
    // never sell discontinued" — the most common policy in practice.
    const gates = this.resolvedGates();
    const hasFlaggedItem = this.state.cartItems.some(it => {
      if (gates.blockDisc && it.discontinued) return true;
      if (gates.blockOos && it.availableForSale === false) return true;
      return false;
    });
    const submitDisabled = this.state.submitting || !hasItems || hasFlaggedItem;
    const submitBtn = el("button", {
      class: "qb-btn qb-btn-primary",
      // Inline background wins over the .qb-btn-primary class rule
      // (which paints the shop's general accent) so the quick form's
      // own accent override — resolveQuickAccent() — actually shows.
      // Height 46 + soft accent shadow (from .qb-btn-primary) per spec.
      style: `width:100%;height:46px;background:${accent};box-shadow:0 6px 16px ${accent}33`,
      disabled: submitDisabled ? "true" : undefined as any,
      onclick: () => void this.submitQuick(),
    }, this.state.submitting ? "Submitting…" : qf.submit_label) as HTMLButtonElement;
    cta.appendChild(submitBtn);
    if (hasFlaggedItem) {
      cta.appendChild(el("div", {
        style: "font-size:11px;color:#b91c1c;text-align:center;line-height:1.4",
      }, "Remove unavailable items above before submitting."));
    }

    // NOTE: the old bottom "Add to Quote" exit is GONE. Under the unified
    // model the quote items already live in the persistent basket (the
    // form is a view over it), so closing the form keeps them — a separate
    // "save to quote" exit would be redundant. The quick-form footer is a
    // single "Request a Quote" submit; "+ Add more products" (top) covers
    // in-place adds and the escalation link (below) switches to the full
    // form.

    // Consent — below Submit, the conventional legal-line-under-CTA
    // placement. Gate is placement-independent (see the consent
    // check above, in submitQuick's validate()) so moving this below
    // the button changes nothing about whether it blocks submit.
    if (qf.consent_enabled) {
      const labelText = qf.consent_text;
      const required = qf.consent_required;
      // Plain consent — checkbox + free text directly under Submit, no
      // card/border/background bubble (form-polish-v3 #1).
      const wrap = el("label", {
        class: "qb-field",
        style: "display:flex;align-items:flex-start;gap:10px;cursor:pointer;margin:0",
      });
      const cb = el("input", {
        type: "checkbox",
        style: "width:18px;height:18px;margin-top:1px;flex-shrink:0;cursor:pointer",
      });
      cb.checked = this.state.consentAccepted;
      cb.addEventListener("change", () => {
        this.state.consentAccepted = (cb as HTMLInputElement).checked;
      });
      wrap.appendChild(cb);
      const text = el("span", {
        style: "font-size:12.5px;color:#475569;line-height:1.5",
      });
      text.appendChild(document.createTextNode(labelText));
      if (required) {
        text.appendChild(el("span", { style: "color:#dc2626;margin-left:4px;font-weight:500" }, "*"));
      }
      wrap.appendChild(text);
      cta.appendChild(wrap);
      if (errs.consent) {
        cta.appendChild(el("div", {
          class: "qb-errors", style: "margin:0",
        }, errs.consent));
      }
    }

    // "Open the full form" escalation (decision A) — the lite quick door
    // switches IN PLACE to the full multi-step form carrying the current
    // items. Sits BELOW the submit as a secondary path so it doesn't
    // compete with the primary CTA. Under the unified model the items
    // already live in the persistent quote, so the full form opens seeded
    // from it — no navigation, nothing lost.
    // Merchant-controlled: qf.escalation_text is the editable prompt copy
    // (sensible default); qf.show_escalation (default true) hides the
    // whole line for single-product / quick-only stores where offering a
    // fuller multi-step form makes no sense.
    if (hasItems && qf.show_escalation) {
      const linkWrap = el("div", {
        style: "padding-top:12px;border-top:1px solid rgba(2,6,23,.08);text-align:center",
      });
      linkWrap.appendChild(el("span", {
        style: "font-size:12px;color:#64748b",
      }, qf.escalation_text + " "));
      linkWrap.appendChild(el("button", {
        type: "button",
        style: `background:none;border:none;padding:0;font-size:13px;font-weight:500;color:${accent};text-decoration:none;cursor:pointer;font-family:inherit`,
        onclick: () => this.escalateToFullForm(),
      }, "Open the full form →"));
      cta.appendChild(linkWrap);
    }

    // "Questions? <email>" — always in the FOOTER (fix,
    // drawer-fullheight-mobile #2), beneath the "Open the full form →"
    // line. Was in the scroll region where it floated mid-form; the
    // pinned CTA is the consistent home across both forms. escapeHtml
    // guards a malicious support_email (server PATCH validation also
    // rejects non-emails — defense in depth). margin:0 so it sits on the
    // tray's own 10px gap, not its class default.
    if (this.config.support_email) {
      const footer = el("div", { class: "qb-footer-meta", style: "margin:0" });
      const safeEmail = escapeHtml(this.config.support_email);
      footer.innerHTML = `Questions? <a href="mailto:${safeEmail}">${safeEmail}</a>`;
      cta.appendChild(footer);
    }
    // "Powered by QuoteSense" (tier-gating Lane 3 §4) — beneath "Questions?",
    // same footer-only rule as the full wizard's nav footer above.
    if (this.config.show_powered_by) {
      const badge = el("div", { class: "qb-badge-meta", style: "margin:0" });
      badge.innerHTML = `<a href="https://quotesense.io" target="_blank" rel="noopener noreferrer">Powered by QuoteSense</a>`;
      cta.appendChild(badge);
    }

    // NOTE: cta is RETURNED (not appended to the scroll card). The
    // renderStep dispatcher mounts it into qb-nav-footer (sibling of
    // qb-shell) so contact + subtotal + submit pin to the panel bottom
    // via the same flex-shrink:0 chain the full wizard's nav uses.
    return cta;
  }

  /** Quick-form "Open the full form" in-place escalation (decision A).
   *  Swaps the current overlay to the full multi-step form carrying the
   *  quick form's current items. openOverlay re-opens in place — its
   *  re-open guard tears down THIS overlay first — so it reads as an
   *  in-place switch with items intact. Never throws out of the click.
   *
   *  Basket-backed: the items already live in the persistent quote (the
   *  quick form is a view over it), so the full form opens seeded from the
   *  basket — no prefill needed, and re-adding would double the qty. Also
   *  clears the basket on submit success (mirrors openBasketReview).
   *
   *  Classic (basket off): carry the items in-memory via the lineItems
   *  prefill, exactly as the prior in-place escalation did. */
  private escalateToFullForm(): void {
    try {
      if (this.isBasketBacked()) {
        openOverlay("drawer", {
          formMode: "full",
          onSubmitSuccess: () => { try { getBasket().clear(); } catch { /* swallow */ } },
        });
        return;
      }
      // CartItem is a structural BasketItem superset; basketItemsToPrefill
      // maps to the form's lineItems shape so the full form seeds the same.
      const lineItems = basketItemsToPrefill(this.state.cartItems as unknown as BasketItem[]) as PrefillLineItem[];
      if (!lineItems.length) return;
      openOverlay("drawer", { prefill: { lineItems }, formMode: "full" });
    } catch {
      /* a broken escalation must never surface as an unhandled error */
    }
  }

  /** Returns null when the "+ Add more products" button is enabled,
   *  or a short helper-line string when it should be greyed out.
   *  Gate is: Name filled, AND at least one of phone/email when both
   *  are visible (or whichever is the only visible contact channel).
   *  Other show_* combinations (e.g. both off) leave the button
   *  enabled — the merchant has chosen to skip contact gates. */
  private quickAddGateMessage(qf: ResolvedQuickForm): string | null {
    const name = [this.state.firstName, this.state.lastName].filter(Boolean).join(" ").trim();
    if (!name) return "Fill in your name and contact info to add more products";
    const phone = (this.state.phone || "").trim();
    const email = (this.state.email || "").trim();
    if (qf.show_phone && qf.show_email) {
      if (!phone && !email) return "Fill in your name and contact info to add more products";
    } else if (qf.show_phone) {
      if (!phone) return "Fill in your name and contact info to add more products";
    } else if (qf.show_email) {
      if (!email) return "Fill in your name and contact info to add more products";
    }
    return null;
  }

  /** Live-update the "+ Add more products" button's disabled state
   *  without a full re-render. Called from the Name/Phone/Email
   *  input handlers so a customer who fills the gating fields sees
   *  the button enable immediately — no need to blur or wait for a
   *  re-render. Resilient if the button isn't in the DOM (e.g.
   *  allow_multi_product disabled). */
  private refreshQuickAddMoreGate() {
    const qf = resolveQuickForm(this.config.quick_form, this.config);
    if (!qf.allow_multi_product) return;
    const root = this.shadow.querySelector(".qb-quick-form");
    if (!root) return;
    const btn = root.querySelector(".qb-add-more-btn") as HTMLButtonElement | null;
    if (!btn) return;
    const gateMsg = this.quickAddGateMessage(qf);
    const disabled = !!gateMsg;
    // Neutral dashed button — enable/disable is conveyed by the
    // .qb-add-more-btn[disabled] CSS rule; no accent recolor (the add-
    // more control stays quiet so it doesn't compete with the CTA).
    btn.disabled = disabled;
    const hint = root.querySelector(".qb-add-more-hint") as HTMLElement | null;
    if (disabled && !hint) {
      const h = el("div", {
        class: "qb-add-more-hint",
        style: "font-size:11px;color:#94a3b8;margin-top:8px;text-align:center",
      }, gateMsg!);
      btn.parentElement?.insertBefore(h, btn.nextSibling);
    } else if (!disabled && hint) {
      hint.remove();
    }
  }

  // ── Quick form picker screen ───────────────────────────────────────────
  // Slides in from the right when the customer clicks "+ Add more
  // products" on Screen 1. Search input → debounced fetch to
  // /api/public/product/search → results list with variant pickers.
  // Adding a result auto-returns to Screen 1.
  private quickPickerDebounce: any = null;
  /** Inline picker panel — renders into the parent quick-form card,
   *  in place where the "+ Add more products" button would be when
   *  collapsed. Visually distinguished by a tinted background +
   *  rounded border so it reads as a sub-panel inside the form
   *  rather than competing with the contact fields below. */
  private renderInlinePicker(card: HTMLElement) {
    const accent = this.resolveQuickAccent();
    const panel = el("div", {
      class: "qb-quick-picker",
      style: "margin-bottom:14px;padding:12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px",
    });

    // Header row — small label + Done collapse link on the right.
    const header = el("div", {
      style: "display:flex;align-items:center;justify-content:space-between;margin-bottom:8px",
    });
    header.appendChild(el("div", {
      style: "font-size:11px;font-weight:700;color:#475569;letter-spacing:.06em;text-transform:uppercase",
    }, "Add products"));
    header.appendChild(el("button", {
      type: "button",
      style: `background:transparent;border:none;color:${accent};font-size:12px;font-weight:700;cursor:pointer;padding:2px 4px;font-family:inherit`,
      onclick: () => {
        this.state.quickPickerOpen = false;
        this.state.quickPickerQuery = "";
        this.state.quickPickerResults = [];
        this.renderStep();
      },
    }, "Done"));
    panel.appendChild(header);

    // Search input — autofocused on expand.
    const searchInput = el("input", {
      class: "qb-input", type: "search",
      placeholder: this.buildSearchPlaceholder(),
      style: "margin-bottom:8px",
    }) as HTMLInputElement;
    searchInput.value = this.state.quickPickerQuery || "";
    searchInput.addEventListener("input", () => {
      this.state.quickPickerQuery = searchInput.value;
      if (this.quickPickerDebounce) clearTimeout(this.quickPickerDebounce);
      const q = searchInput.value.trim();
      if (!q) {
        this.state.quickPickerResults = [];
        this.state.quickPickerLoading = false;
        this.updateQuickPickerResultsPane(panel);
        return;
      }
      this.state.quickPickerLoading = true;
      this.updateQuickPickerResultsPane(panel);
      this.quickPickerDebounce = setTimeout(() => {
        void this.runQuickPickerSearch(q, panel);
      }, 250);
    });
    panel.appendChild(searchInput);

    // Results pane — cap at 280px per spec so the surrounding form
    // (Name / contact / submit) stays in view. Internal scroll when
    // results overflow.
    const resultsPane = el("div", {
      class: "qb-quick-picker-results",
      style: "max-height:280px;overflow-y:auto",
    });
    panel.appendChild(resultsPane);

    card.appendChild(panel);

    this.updateQuickPickerResultsPane(panel);

    // Autofocus the search box on expand. queueMicrotask so it runs
    // after the shadow DOM update completes.
    queueMicrotask(() => { try { searchInput.focus(); } catch { /* swallow */ } });
  }

  private async runQuickPickerSearch(q: string, card: HTMLElement) {
    // Preview: inert picker — render the empty result state, never fetch.
    if (this.preview) {
      this.state.quickPickerResults = [];
      this.state.quickPickerLoading = false;
      this.updateQuickPickerResultsPane(card);
      return;
    }
    try {
      const url = `${API_ORIGIN}/api/public/product/search?shop=${encodeURIComponent(SHOP_SLUG)}&q=${encodeURIComponent(q)}`;
      const r = await fetch(url, { credentials: "omit" });
      const j: any = await r.json().catch(() => ({}));
      // Bail if the customer changed the query mid-flight (the saved
      // state holds the latest input) — we'd overwrite a fresher
      // search's results otherwise.
      if ((this.state.quickPickerQuery || "").trim() !== q) return;
      this.state.quickPickerResults = Array.isArray(j?.products) ? j.products : [];
      this.state.quickPickerLoading = false;
      this.updateQuickPickerResultsPane(card);
    } catch {
      this.state.quickPickerResults = [];
      this.state.quickPickerLoading = false;
      this.updateQuickPickerResultsPane(card);
    }
  }

  /** Re-render just the results pane within the picker card. Keeps
   *  the search input's focus + caret intact between debounced
   *  searches (a full this.renderStep() remounts the input). */
  private updateQuickPickerResultsPane(card: HTMLElement) {
    const pane = card.querySelector(".qb-quick-picker-results") as HTMLElement | null;
    if (!pane) return;
    pane.replaceChildren();
    const q = (this.state.quickPickerQuery || "").trim();
    if (!q) {
      pane.appendChild(el("div", {
        style: "padding:24px 8px;text-align:center;font-size:12px;color:#94a3b8",
      }, "Start typing to search products."));
      return;
    }
    if (this.state.quickPickerLoading) {
      pane.appendChild(el("div", {
        style: "padding:24px 8px;text-align:center;font-size:12px;color:#94a3b8",
      }, "Searching…"));
      return;
    }
    const results = this.state.quickPickerResults || [];
    if (results.length === 0) {
      pane.appendChild(el("div", {
        style: "padding:24px 8px;text-align:center;font-size:12px;color:#94a3b8",
      }, "No products found."));
      return;
    }
    results.forEach(p => pane.appendChild(this.renderQuickPickerRow(p)));
  }

  /** Render the inventory-state badge stack for a variant. Each
   *  badge is a span; caller appends to whatever container they
   *  want (picker row, cart line, etc). Returns [] when nothing
   *  flagged — caller can skip the wrapper div. */
  private renderInventoryBadges(opts: {
    discontinued?: boolean;
    availableForSale?: boolean;
    inventoryPolicy?: "DENY" | "CONTINUE";
  }): HTMLElement[] {
    const out: HTMLElement[] = [];
    const badgeStyle = "display:inline-block;font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;letter-spacing:.02em;white-space:nowrap";
    if (opts.discontinued) {
      out.push(el("span", {
        style: `${badgeStyle};background:#e2e8f0;color:#475569`,
      }, "Discontinued"));
    }
    if (opts.availableForSale === false) {
      if (opts.inventoryPolicy === "CONTINUE") {
        // Backorder — Shopify will sell anyway but we want the
        // customer to know it's not on-hand.
        out.push(el("span", {
          style: `${badgeStyle};background:#fef3c7;color:#92400e`,
        }, "Backorder"));
      } else {
        out.push(el("span", {
          style: `${badgeStyle};background:#fee2e2;color:#b91c1c`,
        }, "Out of stock"));
      }
    }
    return out;
  }

  /** Whether a line item / picker variant should be blocked in
   *  strict mode. "Flagged AND unreserved" gates submit + add. V2
   *  will OR in a reservation-state check; V1 has no reservations
   *  so flagged === blocked. */
  private isInventoryFlagged(opts: {
    discontinued?: boolean;
    availableForSale?: boolean;
  }): boolean {
    if (opts.discontinued) return true;
    if (opts.availableForSale === false) return true;
    return false;
  }

  /** Resolve the two gate booleans, applying the legacy
   *  inventory_strict fallback when the split pair is missing.
   *  Mirror of resolveInventoryGates in shop-config-types — the
   *  embed bundle can't import @/lib without pulling pg/redis in,
   *  so the resolution lives here too. Keep the two in sync; same
   *  rule both sides: explicit new-flag values win, undefined
   *  inherits from inventory_strict. */
  private resolvedGates(): { blockOos: boolean; blockDisc: boolean } {
    const legacy = this.config.inventory_strict === true;
    const blockOos = typeof this.config.block_out_of_stock === "boolean"
      ? this.config.block_out_of_stock
      : legacy;
    const blockDisc = typeof this.config.block_discontinued === "boolean"
      ? this.config.block_discontinued
      : legacy;
    return { blockOos, blockDisc };
  }

  private renderQuickPickerRow(p: QuickPickerProduct): HTMLElement {
    const accent = this.resolveQuickAccent();
    const row = el("div", {
      style: "display:flex;gap:10px;padding:10px;border:1px solid #e2e8f0;border-radius:10px;background:#fff;margin-bottom:8px;align-items:center",
    });
    if (p.image) {
      row.appendChild(el("img", {
        src: p.image, alt: p.title, loading: "lazy",
        style: "width:52px;height:52px;border-radius:8px;object-fit:cover;flex:0 0 52px;background:#f1f5f9",
      }));
    } else {
      row.appendChild(el("div", {
        style: "width:52px;height:52px;border-radius:8px;background:#f1f5f9;flex:0 0 52px",
      }));
    }
    const meta = el("div", { style: "flex:1;min-width:0" });
    meta.appendChild(el("div", {
      style: "font-size:13px;font-weight:700;color:#0f172a;line-height:1.3;word-break:break-word",
    }, p.title));
    // Price range — min/max across variants. Single variant = single price.
    const prices = p.variants.map(v => parseFloat(v.price)).filter(n => !isNaN(n));
    if (prices.length > 0) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      const label = min === max ? fmtUSD(min) : `${fmtUSD(min)} — ${fmtUSD(max)}`;
      meta.appendChild(el("div", {
        style: "font-size:11px;color:#64748b;margin-top:2px",
      }, label));
    }
    // Inventory badges — product-level discontinued plus the worst
    // variant-level stock state across the listed variants. Single-
    // variant products surface the variant's own state; multi-
    // variant products surface OOS only if EVERY variant is OOS
    // (otherwise the customer can pick an in-stock variant via the
    // select). Discontinued always shows because it's product-wide.
    const variantStates = p.variants.map(v => ({
      availableForSale: v.availableForSale ?? v.available,
      inventoryPolicy: v.inventoryPolicy,
    }));
    const allOutOfStock = variantStates.length > 0
      && variantStates.every(s => s.availableForSale === false);
    const someBackorder = allOutOfStock
      && variantStates.some(s => s.inventoryPolicy === "CONTINUE");
    const productBadges = this.renderInventoryBadges({
      discontinued: p.discontinued,
      availableForSale: allOutOfStock ? false : undefined,
      inventoryPolicy: someBackorder ? "CONTINUE" : "DENY",
    });
    if (productBadges.length > 0) {
      const badgeRow = el("div", { style: "margin-top:4px;display:flex;gap:4px;flex-wrap:wrap" });
      productBadges.forEach(b => badgeRow.appendChild(b));
      meta.appendChild(badgeRow);
    }
    row.appendChild(meta);

    if (p.variants.length === 0) {
      // Edge case — product with no variants (rare; Shopify always
      // ships at least one). Render disabled state so the customer
      // sees the row but can't add it.
      row.appendChild(el("span", {
        style: "font-size:11px;color:#94a3b8;font-weight:600",
      }, "Unavailable"));
      return row;
    }

    // Add gates — split into two independent policies. Discontinued
    // is product-level (always wrong to add ANY variant of a
    // discontinued product); OOS is variant-level (the customer can
    // pick a different in-stock variant from the dropdown).
    //   • blockDisc=true + product discontinued → all variants
    //     blocked, button reads "Unavailable" with a discontinued
    //     tooltip.
    //   • blockOos=true + variant OOS → that variant blocked; the
    //     dropdown disables its option so the customer can pick an
    //     in-stock sibling instead.
    const { blockOos, blockDisc } = this.resolvedGates();
    const productBlocked = blockDisc && !!p.discontinued;

    if (p.variants.length === 1) {
      const v = p.variants[0];
      const alreadyIn = this.state.cartItems.some(c => c.variantId === v.variantId);
      const variantOos = (v.availableForSale ?? v.available) === false;
      const variantBlocked = blockOos && variantOos;
      const blocked = productBlocked || variantBlocked;
      const tooltip = productBlocked
        ? "Discontinued — blocked by store policy"
        : variantBlocked
          ? "Out of stock — blocked by store policy"
          : "";
      const btn = el("button", {
        type: "button",
        disabled: blocked ? "true" : undefined as any,
        title: tooltip,
        style: blocked
          ? `background:#e2e8f0;color:#94a3b8;border:none;border-radius:8px;padding:8px 14px;font-size:12px;font-weight:700;cursor:not-allowed;font-family:inherit;flex:0 0 auto`
          : `background:${accent};color:#fff;border:none;border-radius:8px;padding:8px 14px;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;flex:0 0 auto`,
        onclick: blocked ? undefined as any : (() => this.addQuickPickerVariant(p, v)),
      }, blocked ? "Unavailable" : (alreadyIn ? "+ 1 more" : "+ Add"));
      row.appendChild(btn);
      return row;
    }

    // Multi-variant — render a <select> picker + Add button. Default
    // selection is the first variant.
    const select = el("select", {
      class: "qb-select",
      style: "padding:6px 8px;font-size:12px;flex:0 0 auto;max-width:140px",
    }) as HTMLSelectElement;
    p.variants.forEach(v => {
      // Annotate OOS variants in the dropdown so the customer can
      // see why one option is preferable. block_out_of_stock=true
      // flags them as unselectable; off leaves them selectable
      // with the suffix as the warning signal.
      const flagged = v.availableForSale === false;
      const suffix = flagged ? (v.inventoryPolicy === "CONTINUE" ? " (backorder)" : " (out of stock)") : "";
      const opt = el("option", { value: v.variantId }, (v.title || "Default") + suffix) as HTMLOptionElement;
      if (blockOos && flagged) opt.disabled = true;
      select.appendChild(opt);
    });
    row.appendChild(select);
    const addBtn = el("button", {
      type: "button",
      disabled: productBlocked ? "true" : undefined as any,
      title: productBlocked ? "Discontinued — blocked by store policy" : "",
      style: productBlocked
        ? `background:#e2e8f0;color:#94a3b8;border:none;border-radius:8px;padding:8px 14px;font-size:12px;font-weight:700;cursor:not-allowed;font-family:inherit;flex:0 0 auto`
        : `background:${accent};color:#fff;border:none;border-radius:8px;padding:8px 14px;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit;flex:0 0 auto`,
      onclick: productBlocked ? undefined as any : (() => {
        const variantId = select.value;
        const v = p.variants.find(x => x.variantId === variantId);
        if (!v) return;
        // Per-variant block — catches the case where someone
        // disables block_out_of_stock via devtools then clicks Add
        // on an OOS variant. UI already disables the option but a
        // server-side equivalent check would still gate.
        if (blockOos && (v.availableForSale ?? v.available) === false) return;
        this.addQuickPickerVariant(p, v);
      }),
    }, productBlocked ? "Unavailable" : "+ Add");
    row.appendChild(addBtn);
    return row;
  }

  private addQuickPickerVariant(p: QuickPickerProduct, v: QuickPickerProduct["variants"][number]) {
    // Dedup-by-variant (existing → bump qty, new → new line) is the write-
    // through helper's / basket store's own semantic. Keeps the picker
    // open after adding — the customer can add several in a row; the
    // helper's re-render repaints the cart list above AND re-mounts the
    // picker panel (its search input keeps its value via quickPickerQuery).
    this.cartAdd({
      variantId: v.variantId,
      productId: p.productId,
      title: p.title,
      variantTitle: v.title || "",
      sku: v.sku || "",
      qty: 1,
      price: v.price || "0",
      vendor: p.vendor || "",
      imageUrl: p.image || null,
      // Inventory awareness — carry through so line-item badges
      // + submit gate work without re-fetching the product.
      availableForSale: v.availableForSale ?? v.available,
      inventoryPolicy: v.inventoryPolicy,
      inventoryQuantity: v.inventoryQuantity ?? null,
      discontinued: p.discontinued,
    });
  }

  /** Render one labeled quick-form input + inline error. Wired to a
   *  callback because the embed state mutates per-field on input
   *  (lazily — quickErrors clears on submit). */
  private renderQuickField(opts: {
    id: string; label: string; required: boolean;
    value: string; error?: string; type?: string;
    onInput: (v: string) => void;
  }): HTMLElement {
    const wrap = el("div", { class: "qb-field" });
    wrap.appendChild(el("label", { class: "qb-label" }, opts.label + (opts.required ? " *" : "")));
    const inputEl = el("input", {
      class: "qb-input", type: opts.type || "text",
    }) as HTMLInputElement;
    inputEl.value = opts.value || "";
    inputEl.addEventListener("input", () => opts.onInput(inputEl.value));
    wrap.appendChild(inputEl);
    if (opts.error) {
      wrap.appendChild(el("div", {
        style: "font-size:12px;color:#b91c1c;font-weight:600;margin-top:4px",
      }, opts.error));
    }
    return wrap;
  }

  /** Validate + submit the quick form. Lighter than the full submit:
   *  name required; at least one of phone/email when those fields are
   *  shown (and email format check when present); qty positive int. */
  private async submitQuick() {
    // Preview: inert (mirrors submit()).
    if (this.preview) return;
    // Pin country into the allowed set — quick mode has no shipping
    // step, so normalizeCountry() never runs via fieldCountry() (mirrors submit()).
    this.normalizeCountry();
    const qf = resolveQuickForm(this.config.quick_form, this.config);
    const errs: Record<string, string> = {};
    const fullName = [this.state.firstName, this.state.lastName].filter(Boolean).join(" ").trim();
    if (!fullName) errs.name = "Please enter your name.";
    const phone = (this.state.phone || "").trim();
    const email = (this.state.email || "").trim();
    // Per-field required toggles take precedence over the legacy
    // "at least one" rule. Server stays permissive (other paths may
    // submit without these) — see /api/quote-requests.
    if (qf.show_phone && qf.phone_required && !phone) {
      errs.phone = "Phone number required.";
    }
    if (qf.show_email && qf.email_required && !email) {
      errs.email = "Email required.";
    }
    // OR-gate: only kicks in when neither side is individually
    // required AND both are visible. If one of the per-field gates
    // already flagged, skip this — the customer will see the more
    // specific error first.
    const orGateApplies =
      qf.show_phone && qf.show_email && !qf.phone_required && !qf.email_required;
    if (orGateApplies && !phone && !email && !errs.phone && !errs.email) {
      const msg = "Enter a phone number or email.";
      errs.phone = msg;
      errs.email = msg;
    }
    if (qf.show_email && email && !isValidEmail(email)) {
      errs.email = "Email doesn't look right.";
    }
    // Format check only when a phone was actually entered — an empty
    // optional phone stays valid (mirrors the email check above).
    // Region-aware (kickoffs/contact-field-validation.md): the quick
    // form has no shipping step, so this.state.country is already the
    // best-known region — normalizeCountry() (called at the top of this
    // method) pins it to the shop's configured default when unset,
    // matching the server's shipping-country → shop-default → "US"
    // fallback chain.
    if (qf.show_phone && phone && !isValidPhone(phone, this.state.country)) {
      errs.phone = "That phone number doesn't look right.";
    }
    // Multi-product: every line item needs qty >= 1. Coerce sloppy
    // values then re-validate.
    if (this.state.cartItems.length === 0) {
      errs._submit = "Add at least one product before submitting.";
    } else {
      // Build a fresh validated array rather than mutating qty in place —
      // the quick form submits exactly its own item set, no in-place
      // accumulation onto the live state objects. Only commit the
      // normalized array when every line validates.
      const validated: CartItem[] = [];
      for (const it of this.state.cartItems) {
        const qty = parseInt(String(it.qty), 10);
        if (!qty || qty < 1) {
          errs._submit = "Each item needs a quantity of at least 1.";
          break;
        }
        validated.push({ ...it, qty });
      }
      if (!errs._submit) this.state.cartItems = validated;
    }
    // Consent gate — reads the quick form's OWN resolved consent
    // (falls back to the shared consent_* setting when unset — see
    // resolveQuickForm). Optional consent (enabled but not required)
    // renders the checkbox without blocking submit.
    if (qf.consent_enabled && qf.consent_required && !this.state.consentAccepted) {
      errs.consent = "Please confirm you've read and agree to the statement above.";
    }
    // Custom binary question — only blocks when enabled AND required;
    // default required:false never blocks (kickoff: quick-form-binary-
    // field.md). Mirrors the phone_required/email_required gates above.
    if (qf.custom_field?.enabled && qf.custom_field.required) {
      const v = this.state.customValues[qf.custom_field.id];
      if (v !== "yes" && v !== "no") {
        errs.custom_field = "Please make a selection.";
      }
    }
    if (Object.keys(errs).length > 0) {
      this.state.quickErrors = errs;
      this.renderStep();
      return;
    }

    this.state.quickErrors = {};
    this.state.submitting = true;
    this.renderStep();

    // Flag the submit as quick-mode so the dashboard can later filter.
    // Stashed on the state payload that submitQuoteRequest reads; the
    // backend stores it under services.form_mode (analytics metadata).
    (this.state as any).form_mode = "quick";
    const res = await submitQuoteRequest(this.state);
    this.state.submitting = false;
    if (res.ok) {
      // Sentinel -1 when shop_seq is missing — keeps state.submitted
      // truthy for the "show confirmation" gate without leaking
      // the global id into the confirmation headline. The render
      // path gates the "#QR-N" display on submitted > 0.
      this.state.submitted = res.shopSeq ?? -1;
      this.clearPersistedDraft();
      try { this.onSubmitSuccess?.(); } catch { /* basket clear must not break the confirmation */ }
      try { merchantConfig.onSubmit?.(res.id); } catch { /* swallow */ }
    } else {
      this.state.quickErrors = { _submit: res.error };
    }
    this.renderStep();
  }

  // ── Step 0: About You ───────────────────────────────────────────────────
  private renderStep0(card: HTMLElement) {
    this.renderStepIntro(card, "about");
    this.renderOrderedRowsWithPairing(card, "about");
  }

  /** Optional consent checkbox. Renders only when the merchant has
   *  enabled the feature in /settings/forms; legacy shops without the
   *  toggle on see no change. Label is escaped because it's merchant-
   *  controlled text — paste of `<script>` should appear as literal
   *  text, not execute.
   *
   *  Wizard mode: called from renderStep3, lives on Finish right
   *  above the pinned footer Submit. Builder mode: renderStep3's
   *  call is suppressed here (layoutMode check) since the builder's
   *  finish section sits mid-scroll, not next to Submit —
   *  renderBuilderLayout makes its own forced call placed after the
   *  inline submitWrap instead, so consent still lands directly
   *  below Submit without double-rendering. */
  private renderConsent(card: HTMLElement, opts: { force?: boolean } = {}) {
    if (!this.config.consent_enabled) return;
    if (this.layoutMode === "builder" && !opts.force) return;
    // Default copy mirrors CONSENT_DEFAULT_TEXT in shop-config-types
    // but is duplicated inline because the embed bundle deliberately
    // avoids importing the full types module — keeps the bundle
    // small + self-contained for cross-origin loading.
    const labelText = this.config.consent_text || "I agree to be contacted about my quote request.";
    const required = this.config.consent_required !== false;
    // Plain consent — checkbox + free text, no card/border/background
    // bubble (form-polish-v3 #1). Sits at the bottom of the Finish step
    // content, just above the pinned-footer Submit.
    const wrap = el("label", {
      class: "qb-field",
      style: "display:flex;align-items:flex-start;gap:10px;cursor:pointer;margin-top:14px",
    });
    const cb = el("input", {
      type: "checkbox",
      style: "width:18px;height:18px;margin-top:1px;flex-shrink:0;cursor:pointer",
    });
    cb.checked = this.state.consentAccepted;
    cb.addEventListener("change", () => {
      this.state.consentAccepted = (cb as HTMLInputElement).checked;
    });
    wrap.appendChild(cb);
    const text = el("span", {
      style: "font-size:12.5px;color:#475569;line-height:1.5",
    });
    text.appendChild(document.createTextNode(labelText));
    if (required) {
      text.appendChild(el("span", { style: "color:#dc2626;margin-left:4px;font-weight:500" }, "*"));
    }
    wrap.appendChild(text);
    card.appendChild(wrap);
  }

  // Renders every merchant-defined CustomField scoped to the given step.
  private renderCustomFields(card: HTMLElement, step: EmbedCustomFieldStep) {
    const fields = (this.config.custom_fields || []).filter(f => f.step === step);
    for (const f of fields) this.renderSingleCustomField(card, f);
  }

  // Render exactly one merchant-defined custom field. Used by both
  // renderCustomFields() and renderOrderedField() so the visual
  // treatment stays single-sourced.
  private renderSingleCustomField(card: HTMLElement, f: EmbedCustomField) {
    // Legacy select/multiselect rows resolve to dropdown/checkbox here, so
    // a shop that never reopens the designer renders exactly as before.
    const control = canonicalCustomFieldType(f.type);

    // `file` draws the SAME control the generic drop zone draws, scoped to
    // this one question — its own label, help text, required star, limits
    // line, and its own list of attachments. It owns the whole row, so it
    // returns before the shared label/helpText chrome below.
    if (control === "file") {
      this.renderUploadControl(card, {
        label: f.label,
        fieldId: f.id,
        required: f.required,
        helpText: f.helpText,
        limits: this.uploadLimitsFor(f),
      });
      return;
    }

    const wrap = el("div", { class: "qb-field" });
    const reqStar = f.required ? " *" : "";
    wrap.appendChild(el("label", { class: "qb-label" }, f.label + reqStar));
    if (f.helpText) {
      wrap.appendChild(el("div", { style: "font-size:12px;color:#64748b;margin-top:-2px;margin-bottom:6px;line-height:1.5" }, f.helpText));
    }
    if (control === "dropdown") {
      const sel = el("select", { class: "qb-select" });
      sel.appendChild(el("option", { value: "" }, "— Select —"));
      (f.options || []).forEach(o => {
        const opt = el("option", { value: o.id }, o.label);
        if (this.state.customValues[f.id] === o.id) opt.setAttribute("selected", "");
        sel.appendChild(opt);
      });
      sel.addEventListener("change", e => {
        this.state.customValues = { ...this.state.customValues, [f.id]: (e.target as HTMLSelectElement).value };
        // A <select> fires `change` on commit (not per keystroke), so a
        // full repaint here is safe — no focus/cursor to lose. Without
        // this, any show_when rule keyed on a select controller never
        // re-evaluates (the bug this call fixes).
        this.repaintActiveSurface();
      });
      wrap.appendChild(sel);
    } else if (control === "radio" || control === "checkbox") {
      wrap.appendChild(this.renderChoiceCards(f, control));
    } else if (control === "text") {
      const inputEl = el("input", { class: "qb-input", type: "text" });
      inputEl.value = typeof this.state.customValues[f.id] === "string" ? (this.state.customValues[f.id] as string) : "";
      inputEl.addEventListener("input", () => {
        this.state.customValues = { ...this.state.customValues, [f.id]: inputEl.value };
      });
      wrap.appendChild(inputEl);
    } else if (control === "textarea") {
      const ta = el("textarea", { class: "qb-textarea", rows: "4" });
      ta.value = typeof this.state.customValues[f.id] === "string" ? (this.state.customValues[f.id] as string) : "";
      ta.addEventListener("input", () => {
        this.state.customValues = { ...this.state.customValues, [f.id]: ta.value };
      });
      wrap.appendChild(ta);
    }
    // control === null (an unknown type that slipped past the normalizer):
    // the label renders, the control doesn't. Nothing is silently coerced.
    card.appendChild(wrap);
  }

  /** The stacked choice cards shared by `radio` and `checkbox`. The only
   *  difference between the two is the input type, the group `name`, the
   *  indicator glyph, and the shape of what lands in state.customValues —
   *  everything else (geometry, tap target, accent treatment) is one code
   *  path so the two controls can never drift apart visually.
   *
   *  The native <input> stays inside the <label>, visually hidden but
   *  focusable. Radios in one group share a `name`, so the BROWSER enforces
   *  "exactly one" and gives us arrow-key roving for free. This is the whole
   *  point of the lane: the owner's customers could answer "Installation
   *  needed?" with both Yes and No because the question was modeled as a
   *  multiselect. We do not re-implement exclusivity in JavaScript. */
  private renderChoiceCards(f: EmbedCustomField, control: "radio" | "checkbox"): HTMLElement {
    const list = el("div", { class: "qb-choice-list" });
    const isRadio = control === "radio";
    // Namespaced per field so two radio groups on one step stay independent.
    const groupName = `qb-cf-${f.id}`;
    const raw = this.state.customValues[f.id];
    const selected: string[] = isRadio
      ? (typeof raw === "string" && raw ? [raw] : [])
      : (Array.isArray(raw) ? raw : []);

    (f.options || []).forEach(o => {
      const checked = selected.includes(o.id);
      const lab = el("label", {
        class: `qb-choice qb-choice-${control}${checked ? " qb-choice-on" : ""}`,
      });
      const input = el("input", {
        class: "qb-choice-input",
        type: isRadio ? "radio" : "checkbox",
        name: groupName,
        value: o.id,
      }) as HTMLInputElement;
      input.checked = checked;

      input.addEventListener("change", () => {
        if (isRadio) {
          this.state.customValues = { ...this.state.customValues, [f.id]: o.id };
        } else {
          const prev = Array.isArray(this.state.customValues[f.id]) ? (this.state.customValues[f.id] as string[]) : [];
          this.state.customValues = {
            ...this.state.customValues,
            // Read `input.checked` (post-toggle truth from the browser)
            // rather than the render-time `checked` snapshot.
            [f.id]: input.checked ? [...prev, o.id] : prev.filter(x => x !== o.id),
          };
        }
        // The one repaint seam (00af064c) — a show_when rule keyed on this
        // field re-evaluates in whichever layout is mounted. renderStep()
        // dispatches on layoutMode itself; never branch on it here.
        this.repaintActiveSurface();
        // repaintActiveSurface() rebuilds the card's DOM, which destroys the
        // element that currently has focus. Without this, every arrow-key
        // press on a radio group would move the selection and then drop
        // focus to <body>, making the group unnavigable from the keyboard.
        // Programmatic focus preserves the browser's own :focus-visible
        // heuristic: the ring reappears after a key press, stays hidden
        // after a mouse click.
        this.refocusChoice(groupName, o.id);
      });

      const mark = el("span", { class: "qb-choice-mark" });
      mark.appendChild(el("span", { class: isRadio ? "qb-choice-dot" : "qb-choice-check" }));

      lab.appendChild(input);
      lab.appendChild(mark);
      lab.appendChild(el("span", { class: "qb-choice-text" }, o.label));
      lab.appendChild(el("span", { class: "qb-choice-focus" }));
      list.appendChild(lab);
    });
    return list;
  }

  /** Re-focus the choice input identified by (group, option id) after a
   *  repaint has replaced it with a fresh node. Matches on the `value`
   *  property rather than an attribute selector so a merchant-authored
   *  option id never has to be CSS-escaped. */
  private refocusChoice(groupName: string, optionId: string) {
    const inputs = this.shadow.querySelectorAll<HTMLInputElement>(".qb-choice-input");
    for (const input of Array.from(inputs)) {
      if (input.name === groupName && input.value === optionId) {
        input.focus();
        return;
      }
    }
  }

  // ── Step 1: Shipping ────────────────────────────────────────────────────
  private renderStep1(card: HTMLElement) {
    // Phase 2: multi-location B2B contacts get a "Shipping to:"
    // dropdown above the address fields. Picking a location
    // re-pre-fills the four address inputs from that location's
    // address. Single-location companies don't render anything
    // extra — their address is already filled from boot.
    this.appendLocationPickerIfNeeded(card);

    this.renderStepIntro(card, "shipping");
    // Walk the merged + ordered field list for shipping with
    // generalized half-width pairing applied. Custom fields can
    // theoretically land here in the future; today CustomFieldStep
    // is "about" | "finish" so this is built-ins only in practice.
    this.renderOrderedRowsWithPairing(card, "shipping");
    // Separate-billing block, after the shipping address rows.
    this.renderBillingBlock(card);
  }

  /** Optional SEPARATE billing address — embed mirror of the hosted
   *  form's bespoke billing block. Called from renderStep1, so it's
   *  shipping-step only (inherits the shippingEnabled gate). A "Billing
   *  same as shipping" checkbox DEFAULT CHECKED; unchecking reveals the
   *  fields. NOT routed through the ordered-field walker so merchant
   *  field-ordering can't fragment it. Re-renders the step on toggle /
   *  billing-country change so the reveal + region control repaint. */
  private renderBillingBlock(card: HTMLElement) {
    const wrap = el("label", {
      style: "display:flex;align-items:center;gap:10px;cursor:pointer;padding:12px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;margin-top:6px",
    });
    const cb = el("input", { type: "checkbox", style: "width:18px;height:18px;flex-shrink:0;cursor:pointer" });
    cb.checked = this.state.billingSame;
    cb.addEventListener("change", () => {
      this.state.billingSame = (cb as HTMLInputElement).checked;
      this.renderStep();
    });
    wrap.appendChild(cb);
    wrap.appendChild(el("span", { style: "font-size:13px;color:#0f172a;line-height:1.5" }, "Billing address is the same as shipping"));
    card.appendChild(wrap);

    if (this.state.billingSame) return;

    const fields = el("div", { style: "margin-top:14px" });
    fields.appendChild(this.field("Billing name", "billingName", "off", "Optional"));
    fields.appendChild(this.field("Billing company", "billingCompany", "off", "Optional"));
    fields.appendChild(this.field("Billing street address", "billingAddress1", "off"));
    fields.appendChild(this.field("Billing apt / suite", "billingAddress2", "off", "Optional"));

    // City/state/zip/country group — mirrors the shipping block's
    // greedy ½+½ pairing via the shared pairRowsForStep helper (country
    // hidden + reflow when only one country is offered).
    const billingOffered = allowedCountries((this.config as any).allowed_countries);
    const billingFixedCountry = billingOffered.length === 1 ? billingOffered[0] : null;
    if (!billingOffered.some((c: { code: string }) => c.code === this.state.billingCountry)) {
      this.state.billingCountry = billingOffered[0].code;
    }
    const region = regionConfigForCountry(this.state.billingCountry);
    const billingZipLabel = postalLabelForCountry(this.state.billingCountry);

    const cityField = el("div", { class: "qb-field" });
    cityField.appendChild(el("label", { class: "qb-label" }, "Billing city"));
    const cityInput = el("input", { class: "qb-input", type: "text", autocomplete: "off" });
    cityInput.value = this.state.billingCity || "";
    cityInput.addEventListener("input", () => { this.state.billingCity = (cityInput as HTMLInputElement).value; });
    cityField.appendChild(cityInput);

    const regionField = el("div", { class: "qb-field" });
    regionField.appendChild(el("label", { class: "qb-label" }, `Billing ${region.label}`));
    if (region.mode === "select") {
      const sel = el("select", { class: "qb-select", autocomplete: "off" });
      sel.appendChild(el("option", { value: "" }, `— ${region.label} —`));
      (region.options || []).forEach(opt => {
        const o = el("option", { value: opt.code }, opt.code);
        if (this.state.billingState === opt.code) o.setAttribute("selected", "");
        sel.appendChild(o);
      });
      sel.addEventListener("change", () => { this.state.billingState = (sel as HTMLSelectElement).value; });
      regionField.appendChild(sel);
    } else {
      const input = el("input", { class: "qb-input", type: "text", autocomplete: "off" });
      input.value = this.state.billingState || "";
      input.addEventListener("input", () => { this.state.billingState = (input as HTMLInputElement).value; });
      regionField.appendChild(input);
    }

    const zipField = el("div", { class: "qb-field" });
    zipField.appendChild(el("label", { class: "qb-label" }, `Billing ${billingZipLabel}`));
    const zipInput = el("input", { class: "qb-input", type: "text", autocomplete: "off" });
    zipInput.value = this.state.billingZip || "";
    zipInput.addEventListener("input", () => { this.state.billingZip = (zipInput as HTMLInputElement).value; });
    zipField.appendChild(zipInput);

    const billingAddressRows: { key: string; column_width: "half"; node: HTMLElement }[] = [
      { key: "city", column_width: "half", node: cityField },
      { key: "state", column_width: "half", node: regionField },
      { key: "zip", column_width: "half", node: zipField },
    ];
    if (!billingFixedCountry) {
      const countryField = el("div", { class: "qb-field" });
      countryField.appendChild(el("label", { class: "qb-label" }, "Billing country"));
      const countrySel = el("select", { class: "qb-select", autocomplete: "off" });
      billingOffered.forEach((c: { code: string; name: string }) => {
        const o = el("option", { value: c.code }, c.name);
        if (this.state.billingCountry === c.code) o.setAttribute("selected", "");
        countrySel.appendChild(o);
      });
      countrySel.addEventListener("change", () => {
        this.state.billingCountry = (countrySel as HTMLSelectElement).value;
        this.state.billingState = "";
        this.renderStep();
      });
      countryField.appendChild(countrySel);
      billingAddressRows.push({ key: "country", column_width: "half", node: countryField });
    }

    for (const grouped of pairRowsForStep(billingAddressRows)) {
      if (grouped.type === "pair") {
        const row = el("div", { class: "qb-row-2" });
        row.appendChild(grouped.fields[0].node);
        row.appendChild(grouped.fields[1].node);
        fields.appendChild(row);
      } else if (grouped.type === "single") {
        fields.appendChild(grouped.field.node);
      }
    }

    card.appendChild(fields);
  }

  /** Render an optional intro paragraph for a step. */
  private renderStepIntro(card: HTMLElement, step: "about" | "shipping" | "finish") {
    const intro = this.stepIntro(step);
    if (!intro) return;
    card.appendChild(el("div", {
      style: "font-size:14px;color:#475569;margin:-4px 0 16px;line-height:1.55",
    }, intro));
  }

  /** Build the current form values map for isFieldVisible. Keys mirror
   *  the dependency ids the merchant picks in the designer — built-in
   *  keys verbatim (e.g. "phone"), custom field ids for customs. */
  private currentFormValues(): Record<string, string | string[] | undefined> {
    return {
      first_name: this.state.firstName,
      last_name:  this.state.lastName,
      email:      this.state.email,
      phone:      this.state.phone,
      company:    this.state.company,
      preferred_contact: this.state.preferred,
      address:    this.state.address,
      street_address_2: this.state.address2,
      city:       this.state.city,
      state:      this.state.state,
      zip:        this.state.zip,
      ...this.state.customValues,
    };
  }

  /** Walk the ordered list, filter out hidden conditionals, then
   *  greedy-pair consecutive half-width rows into 2-col grid rows.
   *  Trailing odd half renders full-width (no half-empty row).
   *  Thirds (V3 width option) only honored in the wider layouts
   *  (builder + inline) — modal/drawer panels are too narrow for
   *  3-col grids so thirds downgrade to full and render edge-to-
   *  edge there. Three consecutive thirds pack into a qb-row-3 grid;
   *  trailing 1 or 2 thirds render as singles. */
  private renderOrderedRowsWithPairing(card: HTMLElement, step: "about" | "shipping" | "finish") {
    const formValues = this.currentFormValues();
    const rows = resolveFormLayout(this.config as any, step, formValues) as OrderedEmbedField[];
    const supportsThirds = (MODE === "inline" || MODE === "builder");
    for (const grouped of pairRowsForStep(rows, { supportsThirds })) {
      if (grouped.type === "trio") {
        this.renderThreeColumnRow(card, grouped.fields[0], grouped.fields[1], grouped.fields[2]);
      } else if (grouped.type === "pair") {
        this.renderTwoColumnPair(card, grouped.fields[0], grouped.fields[1]);
      } else {
        this.renderOrderedField(card, grouped.field);
      }
    }
  }

  /** Render the "Shipping to:" location picker when the detected
   *  customer is a B2B contact at a Company with 2+ locations.
   *  Single-location companies and regular customers get nothing. */
  private appendLocationPickerIfNeeded(card: HTMLElement) {
    const co = this.state.customerContext?.company;
    if (!co || co.locations.length < 2) return;

    const accent = this.resolveAccent();

    const wrap = el("div", {
      style: `background:${accent}11;border:1px solid ${accent}33;border-radius:10px;padding:12px 14px;margin-bottom:14px;`,
    });
    wrap.appendChild(el("div", {
      class: "qb-label",
      style: "margin-bottom:8px;",
    }, "Shipping to"));

    const select = el("select", {
      class: "qb-select",
      onchange: (e: any) => {
        const id = e.target.value;
        this.state.selectedLocationId = id;
        const loc = co.locations.find(l => l.id === id);
        if (loc?.address) {
          // Overwrite the address fields when a location is
          // explicitly picked — different from boot's "only fill
          // empties" rule because the customer asked for this
          // switch.
          this.state.address = loc.address.address1;
          this.state.city = loc.address.city;
          this.state.state = loc.address.province;
          this.state.zip = loc.address.zip;
        }
        this.renderStep();
      },
    });
    for (const loc of co.locations) {
      const opt = el("option", { value: loc.id },
        `${loc.name || loc.id}${loc.taxExempt ? "  (tax-exempt)" : ""}`,
      ) as HTMLOptionElement;
      if (loc.id === this.state.selectedLocationId) opt.selected = true;
      select.appendChild(opt);
    }
    wrap.appendChild(select);

    // Surface tax-exempt status under the picker so the customer
    // knows the picked location won't be charged tax.
    const picked = co.locations.find(l => l.id === this.state.selectedLocationId);
    if (picked?.taxExempt) {
      wrap.appendChild(el("div", {
        style: "font-size:11px;font-weight:600;color:#065f46;margin-top:6px;",
      }, "✓ Tax-exempt on file with Shopify"));
    }
    card.appendChild(wrap);
  }

  /** Banner anchored above the steps strip when a Shopify customer
   *  was detected at boot. B2B contacts see their Company name;
   *  regular customers see their email. Anonymous → null. */
  private buildCustomerBanner(): HTMLElement | null {
    const ctx = this.state.customerContext;
    if (!ctx) return null;
    const accent = this.resolveAccent();
    const primary = this.resolvePrimary();
    const firstName = ctx.firstName || (ctx.email.split("@")[0] || "there");

    let message: string;
    if (ctx.company) {
      message = `Hi ${firstName} — we recognized your account at <strong>${escapeHtml(ctx.company.name)}</strong>. Fields are pre-filled, edit anything as needed.`;
    } else {
      message = `Hi ${escapeHtml(firstName)} — signed in as <strong>${escapeHtml(ctx.email)}</strong>. Fields are pre-filled.`;
    }
    void primary; // reserved for future treatment if we want a darker variant

    const div = el("div", {
      style: `display:flex;align-items:center;gap:10px;background:${accent}11;border:1px solid ${accent}44;color:#0f172a;border-radius:10px;padding:10px 14px;margin:0 18px 14px;font-size:13px;line-height:1.5;`,
    });
    const icon = el("span", {
      style: `flex:0 0 auto;width:22px;height:22px;border-radius:50%;background:${accent};color:#fff;font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;`,
    }, ctx.company ? "🏢" : "👋");
    const body = el("div", { style: "flex:1;min-width:0;", html: message });
    div.appendChild(icon);
    div.appendChild(body);
    return div;
  }

  /** Shared "Subtotal · N items" block. Appended at the bottom of the
   *  Build step in every layout (wizard inline, builder, modal, drawer)
   *  so customers see one consistent way to read the running total.
   *  Pre-extraction the wizard inline and builder layouts each built
   *  nearly-identical versions with cosmetic drift; now there's one
   *  source of truth. No-op when the cart is empty. Send-to-cart lives
   *  in the "Your items" header (see renderStep2), not here. */
  private appendSubtotalSummary(card: HTMLElement) {
    if (this.state.cartItems.length === 0) return;
    // Wizard (overlay + inline) uses the redesigned subtotal card. Builder
    // (dedicated-page) layout keeps its borderless summary line so it stays
    // consistent with the rest of that document-flow surface.
    if (this.layoutMode !== "builder") {
      card.appendChild(this.buildSubtotalCard());
      return;
    }
    const total = cartTotalWithOptions(this.state.cartItems);
    const itemCount = this.state.cartItems.reduce((s, it) => s + it.qty, 0);
    const summary = el("div", { class: "qb-builder-summary" });
    const leftCluster = el("div", { style: "display:flex;align-items:baseline;gap:10px;flex-wrap:wrap" });
    leftCluster.appendChild(el("span", { class: "qb-builder-summary-label" },
      `Subtotal · ${itemCount} item${itemCount === 1 ? "" : "s"}`));
    leftCluster.appendChild(el("span", { class: "qb-builder-summary-total" },
      fmtUSD(total)));
    summary.appendChild(leftCluster);
    // Optional merchant note — same subtotal_note field as the overlay
    // subtotal (form-polish-v3 #5c). Default empty → hidden (the old
    // hardcoded "Final pricing determined by your rep." is gone).
    const builderNote = (this.config.subtotal_note || "").trim();
    if (builderNote) {
      summary.appendChild(el("div", { class: "qb-builder-summary-note" }, builderNote));
    }
    card.appendChild(summary);
  }

  // ── Step 2: Build Your Quote ────────────────────────────────────────────
  /** Describe-mode toggle link — shared control, rendered in one of two
   *  spots depending on mode (fix, form-header-declutter-footer #3): top
   *  of the textarea view when describe mode is active (nothing else to
   *  lead with there), or folded into the de-emphasized "Add products"
   *  tooling group when the picker is showing. Some customers don't know
   *  exactly what they want and need a way to describe their need in
   *  their own words (the rep then quotes from that description). */
  private buildDescribeToggleLink(): HTMLElement {
    return el("button", {
      type: "button",
      style: `background:none;border:none;color:${this.resolveAccent()};font-size:12px;font-weight:500;cursor:pointer;padding:0;font-family:inherit`,
      onclick: () => {
        this.state.descriptionMode = !this.state.descriptionMode;
        // Clear any pending step-level validation errors when the
        // customer flips modes so the old "must add items" message
        // doesn't linger after they switch to describing instead.
        this.state.errors = [];
        this.renderStep();
      },
    }, this.state.descriptionMode
        ? "← Back to product picker"
        : "Not sure what you need? Describe it instead →");
  }

  // ── Describe step (docs/MULTI_FORM.md §4.3) ─────────────────────────────
  /** First-class Describe step — the free-text "what's the problem /
   *  project?" ask, promoted from the products-step describe-mode toggle
   *  into a leadable wizard step. Copy comes from DescribeSectionConfig
   *  (label / intro / placeholder / help_text); the value writes to
   *  state.description → submitted as the request's `description` field
   *  (distinct from `notes`, same as describe-mode). `required` is
   *  enforced in validate(). Renders ONLY when `describe` is in the
   *  resolved form's section_order (an intake form); the default form has
   *  no describe step and uses the inline toggle exactly as before. */
  private renderDescribeStep(card: HTMLElement) {
    const d = this.config.describe || {};
    const intro = typeof d.intro === "string" ? d.intro.trim() : "";
    if (intro) {
      card.appendChild(el("div", {
        style: "font-size:13px;color:#64748b;margin:0 0 14px;line-height:1.55",
      }, intro));
    }
    const field = el("div", { class: "qb-field" });
    field.appendChild(el("label", { class: "qb-label" }, this.describeLabel()));
    const placeholder = typeof d.placeholder === "string" && d.placeholder.trim().length > 0
      ? d.placeholder.trim()
      : "Tell us what you're trying to buy — sizes, quantities, deadlines, anything helpful.";
    const ta = el("textarea", {
      class: "qb-input",
      rows: "6",
      style: "min-height:140px;resize:vertical",
      placeholder,
    }) as HTMLTextAreaElement;
    ta.value = this.state.description || "";
    ta.addEventListener("input", () => { this.state.description = ta.value; });
    field.appendChild(ta);
    const help = typeof d.help_text === "string" ? d.help_text.trim() : "";
    if (help) {
      field.appendChild(el("div", {
        style: "font-size:12px;color:#94a3b8;margin-top:6px;line-height:1.4",
      }, help));
    }
    card.appendChild(field);
  }

  private renderStep2(card: HTMLElement) {
    // Describe-mode body — single textarea. Replaces the cart-sync
    // banner + product search + items list + subtotal entirely so
    // there's only one place for the customer to look. State
    // (descriptionMode + description) persists via the standard
    // snapshot path so a return visit lands back on the textarea.
    // Suppressed when the form has a standalone Describe step — that
    // step owns the free-text ask, so the inline toggle never shows and
    // a stale persisted descriptionMode flag doesn't hijack the picker.
    if (this.state.descriptionMode && !this.hasDescribeStep()) {
      const toggleRow = el("div", { style: "display:flex;justify-content:flex-end;margin-bottom:6px" });
      toggleRow.appendChild(this.buildDescribeToggleLink());
      card.appendChild(toggleRow);

      const field = el("div", { class: "qb-field" });
      field.appendChild(el("label", { class: "qb-label" }, "What are you looking for?"));
      const ta = el("textarea", {
        class: "qb-input",
        rows: "6",
        style: "min-height:140px;resize:vertical",
        placeholder: "Tell us what you're trying to buy — sizes, quantities, deadlines, anything helpful.",
      }) as HTMLTextAreaElement;
      ta.value = this.state.description || "";
      ta.addEventListener("input", () => { this.state.description = ta.value; });
      field.appendChild(ta);
      card.appendChild(field);
      return;
    }

    const hasItems = this.state.cartItems.length > 0;

    // ── Add products FIRST (form-polish-v3 #5b) — search + import-from-
    //    cart + bundles + describe-mode link. Sits between the step tabs
    //    and the "Your items" list; search-at-the-bottom read backwards.
    //    The item list + (builder-only) inline subtotal render AFTER this.
    const addWrap = el("div", {});

    // Bundles row — parked behind BUNDLES_ENABLED (feature-flags.ts).
    // Forcing an empty list keeps addBundle/lookupVariantsViaStorefront
    // intact (dead but preserved).
    const bundles = BUNDLES_ENABLED ? (this.config.bundles || []).filter(b => b.isActive) : [];
    if (bundles.length) {
      addWrap.appendChild(el("div", {
        style: "font-size:11.5px;font-weight:500;color:#64748b;margin-bottom:8px",
      }, "Suggested packages"));
      const grid = el("div", { class: "qb-bundle-grid" });
      bundles.forEach(b => {
        const bcard = el("div", { class: "qb-bundle" });
        if (b.imageUrl) bcard.appendChild(el("img", { class: "qb-bundle-img", src: b.imageUrl, alt: b.name, loading: "lazy" }));
        else bcard.appendChild(el("div", { class: "qb-bundle-img-empty" }));
        const meta = el("div");
        meta.innerHTML = `<div style="font-size:14px;font-weight:700;color:#0f172a">${escapeHtml(b.name)}</div>
          <div style="font-size:12px;color:#64748b;margin-top:4px;line-height:1.4;min-height:32px">${escapeHtml(b.description)}</div>`;
        bcard.appendChild(meta);
        bcard.appendChild(el("button", {
          class: "qb-btn",
          onclick: () => void this.addBundle(b),
        }, `+ Add ${b.lineItems.length} item${b.lineItems.length === 1 ? "" : "s"}`));
        grid.appendChild(bcard);
      });
      addWrap.appendChild(grid);
    }

    // Product search (uses Shopify storefront when detected, falls back to
    // our /api/shopify proxy otherwise — e.g. previewing the embed off-site).
    const searchField = el("div", { class: "qb-field", style: "margin-bottom:0" });
    searchField.appendChild(el("label", { class: "qb-label" }, this.buildSearchLabel()));
    const input = el("input", {
      class: "qb-input",
      type: "text",
      placeholder: this.buildSearchPlaceholder(),
      autocomplete: "off",
    });
    // Import-from-cart used to have its own copy sitting inline with the
    // search input in builder layout. Deleted (fix/no-cart-autoimport,
    // 2nd pass) — the hoisted actions row below now renders in every
    // layout (including builder), so that copy was a pure duplicate, and
    // the worse one (no importableCount gate, never disabled). One
    // Import affordance, all layouts.
    searchField.appendChild(input);
    const resultsBox = el("div", { class: "qb-results", style: "display:none" });
    let timer: any;
    input.addEventListener("input", () => {
      clearTimeout(timer);
      const q = input.value;
      if (!q.trim()) { resultsBox.style.display = "none"; return; }
      timer = setTimeout(async () => {
        const products = await this.searchProducts(q);
        resultsBox.replaceChildren();
        if (!products.length) {
          resultsBox.style.display = "block";
          resultsBox.appendChild(el("div", { style: "padding:14px;font-size:13px;color:#94a3b8" }, `No products found for "${q}".`));
          return;
        }
        resultsBox.style.display = "block";
        products.forEach(p => resultsBox.appendChild(this.renderProductRow(p)));
      }, 300);
    });
    // input is already attached above (either in the builder row
    // or directly to searchField). Just add the results dropdown.
    searchField.appendChild(resultsBox);
    addWrap.appendChild(searchField);

    // Describe-mode entry link — small, secondary, tucked at the bottom
    // of the add-products group (was floating alone at the very top of
    // the step). Hidden when the form has a standalone Describe step —
    // that step is the single entry point for the free-text ask.
    if (!this.hasDescribeStep()) {
      const toggleRow = el("div", { style: "display:flex;justify-content:flex-end;margin-top:10px" });
      toggleRow.appendChild(this.buildDescribeToggleLink());
      addWrap.appendChild(toggleRow);
    }

    card.appendChild(addWrap);

    // ── Cart-sync actions row — hoisted OUT of `if (hasItems)` (fix/no-
    //    cart-autoimport, 2nd pass): Part 1 deleted the /cart auto-import,
    //    so on /cart with a full Shopify cart and an EMPTY quote,
    //    Import-from-cart is the ONLY way in — it must render even with
    //    zero quote items, or a customer landing there has no path
    //    forward. "Your items (N)" only means something once there's
    //    something to count, and Send-to-cart needs quote items to push,
    //    so those two stay gated on hasItems; Import doesn't. Renders
    //    right after the add-products tools (form-polish-v3 #5b),
    //    separated by a divider, whenever either half has something to
    //    show. Preview renders Import unconditionally (there's no
    //    storefront to detect, and hiding it would reopen the very
    //    drift this preview exists to close) — importCart()/exportCart()
    //    both no-op in preview. Both grey out (disabled) when
    //    cartInSync — importing OR exporting would be no-ops.
    const showImport = this.preview || (this.state.shopifyDetected && this.state.importableCount > 0);
    const showExport = hasItems && (this.state.shopifyDetected || this.preview);
    if (hasItems || showImport) {
      const synced = this.cartInSync;
      const actions = el("div", { style: "display:flex;gap:8px;flex-wrap:wrap" },
        showImport ? el("button", {
          type: "button",
          class: "qb-btn qb-btn-ghost",
          style: "font-size:12px;padding:6px 12px;min-height:0",
          disabled: synced,
          "aria-disabled": synced ? "true" : null,
          onclick: () => void this.importCart(),
        }, "Import from cart") : null,
        showExport ? el("button", {
          type: "button",
          class: "qb-btn qb-btn-ghost",
          style: "font-size:12px;padding:6px 12px;min-height:0",
          disabled: synced,
          "aria-disabled": synced ? "true" : null,
          onclick: () => {
            if (typeof window !== "undefined" && typeof window.confirm === "function") {
              const ok = window.confirm("Replace your current cart contents with these items?");
              if (!ok) return;
            }
            void this.exportCart();
          },
        }, "Send to cart →") : null,
      );
      card.appendChild(el("div", {
        class: "qb-label",
        style: "margin-top:16px;padding-top:16px;border-top:1px solid rgba(2,6,23,.08);margin-bottom:6px;display:flex;align-items:center;justify-content:space-between;gap:10px",
      },
        hasItems ? el("span", {}, `Your items (${this.state.cartItems.length})`) : el("span", {}),
        actions,
      ));
    }

    // In the wizard the running subtotal is PINNED in the footer (see
    // renderStep dispatcher, #5a), so it is NOT rendered inline here; the
    // builder layout has no pinned footer, so it keeps the inline subtotal.
    if (hasItems) {
      const itemsWrap = el("div", {});
      this.state.cartItems.forEach(it => itemsWrap.appendChild(this.renderCartRow(it)));
      card.appendChild(itemsWrap);
      if (this.layoutMode === "builder") this.appendSubtotalSummary(card);
    } else {
      card.appendChild(el("div", {
        class: "qb-empty", style: "margin-top:16px",
      }, "No products added yet — search or import above to get started."));
    }
  }

  // Fallback to our /api/shopify proxy when we're not inside a Shopify
  // storefront (e.g. when a merchant previews the embed on a marketing
  // landing page). Uses the grouped response shape.
  /** The one product-search entry point for the Build step. Storefront-native
   *  search when we're inside a Shopify theme, our proxy otherwise — and
   *  nothing at all in a designer preview, which must never hit the network.
   *  The preview still renders the search field + its "No products found"
   *  empty state, so the merchant sees the real control they're styling. */
  private async searchProducts(q: string): Promise<StorefrontProduct[]> {
    if (this.preview) return [];
    return this.state.shopifyDetected ? searchStorefront(q) : this.searchViaApi(q);
  }

  private async searchViaApi(q: string): Promise<StorefrontProduct[]> {
    try {
      const r = await fetch(`${API_ORIGIN}/api/shopify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        // shop is required for this anonymous proxy op — the server
        // validates it + scopes the catalog to this tenant (the
        // /api/shopify public path 500s without it).
        body: JSON.stringify({ productQuery: q, groupByProduct: true, shop: SHOP_SLUG }),
      });
      if (!r.ok) return [];
      const d = await r.json();
      return (d.products || []).map((p: any): StorefrontProduct => ({
        productId: p.productId, title: p.title, vendor: p.vendor,
        imageUrl: p.imageUrl || null,
        defaultVariantId: p.defaultVariantId,
        variants: (p.variants || []).map((v: any) => ({
          variantId: v.variantId, title: v.title, sku: v.sku,
          price: v.price, available: v.available,
        })),
      }));
    } catch { return []; }
  }

  private renderProductRow(p: StorefrontProduct): HTMLElement {
    const row = el("div", { class: "qb-result-row" });
    if (p.imageUrl) row.appendChild(el("img", { class: "qb-thumb", src: p.imageUrl, alt: p.title, loading: "lazy" }));
    else row.appendChild(el("div", { class: "qb-thumb-empty" }, "No Image"));

    const meta = el("div", { style: "flex:1;min-width:0" });
    const firstV = p.variants[0];
    meta.innerHTML = `<div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.35">${escapeHtml(p.title)}</div>
      <div style="font-size:12px;color:#94a3b8;margin-top:2px">${escapeHtml(p.vendor || "")}${firstV?.sku ? " · " + escapeHtml(firstV.sku) : ""}${p.variants.length > 1 ? " · " + p.variants.length + " options" : (firstV ? " · " + fmtUSD(parseFloat(firstV.price) || 0) : "")}</div>`;
    row.appendChild(meta);

    if (p.variants.length === 0) {
      // Suggest.json hit without inline variants — hydrate on click
      // via /products/{handle}.js. Handle was captured in
      // searchStorefront(); if it's somehow missing, the fetch
      // gracefully no-ops (loadProductDetail returns null on empty
      // input) and the click visibly bails. The earlier bug was
      // passing the numeric productId here, which Shopify 404'd.
      const btn = el("button", { class: "qb-btn", style: "padding:8px 14px;font-size:13px;", onclick: async () => {
        const detail = await loadProductDetail(p.handle || "");
        if (!detail) return;
        if (detail.variants.length > 1) this.showVariantPicker(detail);
        else if (detail.variants[0]) this.addVariant(detail, detail.variants[0].variantId);
      }}, "+ Add");
      row.appendChild(btn);
    } else if (p.variants.length > 1) {
      row.appendChild(el("button", { class: "qb-btn", style: "padding:8px 14px;font-size:13px;", onclick: () => this.showVariantPicker(p) }, "Choose option"));
    } else {
      const v = p.variants[0];
      const added = this.state.cartItems.some(c => c.variantId === v.variantId);
      const btn = el("button", {
        class: "qb-btn",
        style: "padding:8px 14px;font-size:13px;" + (added ? "background:#f0fdf4;color:#16a34a" : ""),
        disabled: added ? "true" : undefined as any,
        onclick: () => this.addVariant(p, v.variantId),
      }, added ? "✓ Added" : "+ Add");
      row.appendChild(btn);
    }
    return row;
  }

  /** A staged row is shown as sold out when EITHER Shopify named its
   *  title on the last failed send (reactive) OR the item carries a
   *  known out-of-stock flag — availableForSale=false and NOT a
   *  backorder (inventoryPolicy CONTINUE means Shopify still sells it). */
  private rowSoldOut(it: CartItem): boolean {
    const names = this.state.soldOutTitles;
    if (names && names.length) {
      const t = (it.title || "").trim().toLowerCase();
      if (names.some(n => n.trim().toLowerCase() === t)) return true;
    }
    return it.availableForSale === false && it.inventoryPolicy !== "CONTINUE";
  }

  /** Compact − N + quantity stepper. Same cartSetQty handler the whole
   *  embed uses; purely a visual restyle from the old .qb-qty-btn pair. */
  private renderStepper(it: CartItem): HTMLElement {
    const wrap = el("div", { class: "qb-stepper" });
    wrap.appendChild(el("button", {
      type: "button", "aria-label": "Decrease",
      onclick: () => this.cartSetQty(it.variantId, Math.max(1, it.qty - 1)),
    }, "−"));
    wrap.appendChild(el("span", { class: "qb-stepper-val" }, String(it.qty)));
    wrap.appendChild(el("button", {
      type: "button", "aria-label": "Increase",
      onclick: () => this.cartSetQty(it.variantId, Math.min(9999, it.qty + 1)),
    }, "+"));
    return wrap;
  }

  /** Shared two-line product card — used by the quick form item list and
   *  the wizard's "Build Your Quote" step so both read identically:
   *  Line 1 = title (+ optional sold-out pill); Line 2 = "<variant> ·
   *  $<each> each" on the left, qty stepper + line total + remove on the
   *  right. `showQty` hides the stepper when the merchant disabled qty on
   *  the quick form. `badges` are the inventory chips (discontinued / OOS).
   *  Pricing/qty math and the cart handlers are untouched.
   *
   *  Below that: the item's captured options (APO v2 / theme
   *  `properties[...]` selections — option-capture.ts), if any — muted
   *  small lines so a customer who configured $2,900 of add-ons sees
   *  what's driving their line total, not just a bare title (kickoffs/
   *  options-visible-to-customer.md). Rendered via renderItemOptionsHtml
   *  (render-item-options.ts), which escapes + caps; an item with no
   *  `properties` appends nothing, so the card stays byte-identical to
   *  before this existed.
   *
   *  "each"/line total include any priced-option deltas captured on
   *  the item (item-price.ts) — the customer's own configurator
   *  already showed them this number; a bare variant price here was
   *  the live-reported trust gap (kickoffs/option-price-math.md). A
   *  small muted "(incl. options)" hint appears next to "each" only
   *  when deltas > 0. The submitted `price` field stays untouched
   *  (BASE) — this is display-only math. */
  private renderItemCard(it: CartItem, opts: { showQty: boolean; badges?: HTMLElement[]; soldOut?: boolean }): HTMLElement {
    const basePrice = parseFloat(it.price) || 0;
    const optionsDelta = optionsPriceDelta(it.properties);
    const price = itemUnitPriceWithOptions(basePrice, it.properties);
    const card = el("div", { class: "qb-item-card" });
    if (it.imageUrl) {
      card.appendChild(el("img", { class: "qb-item-thumb", src: it.imageUrl, alt: it.title, loading: "lazy" }));
    } else {
      card.appendChild(el("div", { class: "qb-item-thumb qb-item-thumb-empty", "aria-hidden": "true", html: THUMB_BOX_SVG }));
    }

    const body = el("div", { class: "qb-item-body" });
    const title = el("div", { class: "qb-item-title" });
    title.appendChild(document.createTextNode(it.title));
    if (opts.soldOut) title.appendChild(el("span", { class: "qb-soldout-pill" }, "Sold out"));
    body.appendChild(title);

    if (opts.badges && opts.badges.length) {
      const badgeRow = el("div", { style: "margin-top:6px;display:flex;gap:4px;flex-wrap:wrap" });
      opts.badges.forEach(b => badgeRow.appendChild(b));
      body.appendChild(badgeRow);
    }

    const line2 = el("div", { class: "qb-item-line2" });
    const metaBits = [it.variantTitle, it.sku].filter(Boolean).join(" · ");
    const metaEl = el("div", { class: "qb-item-meta" },
      metaBits ? `${metaBits} · ${fmtUSD(price)} each` : `${fmtUSD(price)} each`);
    if (optionsDelta > 0) metaEl.appendChild(el("span", { class: "qb-item-meta-hint" }, " (incl. options)"));
    line2.appendChild(metaEl);
    const controls = el("div", { class: "qb-item-controls" });
    if (opts.showQty) controls.appendChild(this.renderStepper(it));
    controls.appendChild(el("div", { class: "qb-item-total" }, fmtUSD(price * it.qty)));
    controls.appendChild(el("button", {
      type: "button", class: "qb-item-remove", "aria-label": "Remove",
      onclick: () => this.cartRemove(it.variantId),
    }, "✕"));
    line2.appendChild(controls);
    body.appendChild(line2);

    const optionsHtml = renderItemOptionsHtml(it.properties);
    if (optionsHtml) body.appendChild(el("div", { class: "qb-item-options", html: optionsHtml }));

    card.appendChild(body);
    return card;
  }

  /** Shared subtotal block (quick form + wizard). Label + optional
   *  rep-pricing note on the left, total on the right — one clean row at
   *  every width. Send-to-cart lives in the "Your items" header now, not
   *  here (see renderStep2). */
  private buildSubtotalCard(): HTMLElement {
    const total = cartTotalWithOptions(this.state.cartItems);
    const itemCount = this.state.cartItems.reduce((s, it) => s + it.qty, 0);
    const box = el("div", { class: "qb-subtotal" });
    const main = el("div", { class: "qb-subtotal-main" });
    main.appendChild(el("div", { class: "qb-subtotal-label" },
      `Subtotal · ${itemCount} item${itemCount === 1 ? "" : "s"}`));
    // Optional merchant note under the subtotal (form-polish-v3 #5c).
    // Default empty → hidden (the old hardcoded "Final pricing set by
    // your rep" is gone unless the merchant re-adds it via subtotal_note).
    const note = (this.config.subtotal_note || "").trim();
    if (note) main.appendChild(el("div", { class: "qb-subtotal-note" }, note));
    box.appendChild(main);
    box.appendChild(el("div", { class: "qb-subtotal-total" }, fmtUSD(total)));
    return box;
  }

  private renderCartRow(it: CartItem): HTMLElement {
    // Builder (dedicated-page) layout keeps its original row treatment —
    // this redesign is scoped to the overlay quick form + wizard. The
    // wizard's Build step now uses the shared two-line product card.
    if (this.layoutMode === "builder") return this.renderLegacyCartRow(it);
    return this.renderItemCard(it, { showQty: true, soldOut: this.rowSoldOut(it) });
  }

  private renderLegacyCartRow(it: CartItem): HTMLElement {
    const row = el("div", { class: "qb-cart-row" });
    if (it.imageUrl) row.appendChild(el("img", { class: "qb-cart-thumb", src: it.imageUrl, alt: it.title, loading: "lazy" }));
    else row.appendChild(el("div", { class: "qb-cart-thumb", style: "color:#cbd5e1;font-size:9px;font-weight:600;display:flex;align-items:center;justify-content:center" }, "No Image"));

    const basePrice = parseFloat(it.price) || 0;
    const optionsDelta = optionsPriceDelta(it.properties);
    const price = itemUnitPriceWithOptions(basePrice, it.properties);
    const meta = el("div", { style: "flex:1;min-width:140px" });
    const soldOutPill = this.rowSoldOut(it)
      ? ` <span class="qb-soldout-pill">Sold out</span>` : "";
    const optionsHint = optionsDelta > 0 ? " (incl. options)" : "";
    meta.innerHTML = `<div style="font-size:14px;font-weight:600;color:#0f172a;line-height:1.35">${escapeHtml(it.title)}${soldOutPill}</div>
      ${it.variantTitle ? `<div style="font-size:12px;color:#64748b;margin-top:2px">${escapeHtml(it.variantTitle)}</div>` : ""}
      <div style="font-size:11px;color:#94a3b8;margin-top:2px">${escapeHtml(it.sku)} · ${fmtUSD(price)} each${escapeHtml(optionsHint)}</div>`;
    row.appendChild(meta);

    const qtyRow = el("div", { style: "display:flex;align-items:center;gap:6px" });
    qtyRow.appendChild(el("button", { class: "qb-qty-btn", "aria-label": "Decrease", onclick: () => this.cartSetQty(it.variantId, Math.max(1, it.qty - 1)) }, "−"));
    qtyRow.appendChild(el("span", { style: "width:28px;text-align:center;font-size:14px;font-weight:700" }, String(it.qty)));
    qtyRow.appendChild(el("button", { class: "qb-qty-btn", "aria-label": "Increase", onclick: () => this.cartSetQty(it.variantId, it.qty + 1) }, "+"));
    row.appendChild(qtyRow);

    row.appendChild(el("div", { style: "font-size:14px;font-weight:700;color:#0f172a;min-width:70px;text-align:right" }, fmtUSD(price * it.qty)));
    row.appendChild(el("button", { style: "background:none;border:none;color:#94a3b8;font-size:18px;cursor:pointer;padding:6px;min-width:32px", "aria-label": "Remove", onclick: () => this.cartRemove(it.variantId) }, "✕"));
    return row;
  }

  private showVariantPicker(p: StorefrontProduct) {
    const overlay = el("div", { class: "qb-variant-modal-overlay", onclick: () => overlay.remove() });
    const modal = el("div", { class: "qb-variant-modal", onclick: (e: any) => e.stopPropagation() });
    const head = el("div", { style: "display:flex;align-items:center;gap:12px;margin-bottom:16px" });
    if (p.imageUrl) head.appendChild(el("img", { class: "qb-thumb", src: p.imageUrl, alt: p.title }));
    else head.appendChild(el("div", { class: "qb-thumb-empty" }, "No Image"));
    head.appendChild(el("div", { style: "flex:1", html:
      `<div style="font-size:16px;font-weight:700">${escapeHtml(p.title)}</div>
       <div style="font-size:12px;color:#94a3b8;margin-top:2px">${escapeHtml(p.vendor || "")}</div>` }));
    head.appendChild(el("button", { class: "qb-qty-btn", style: "border:none;background:none;font-size:20px", onclick: () => overlay.remove() }, "✕"));
    modal.appendChild(head);
    modal.appendChild(el("div", { class: "qb-label", style: "margin-bottom:8px" }, "Pick a variant"));
    p.variants.forEach(v => {
      const added = this.state.cartItems.some(c => c.variantId === v.variantId);
      const row = el("div", {
        class: `qb-variant-row ${added ? "added" : ""}`,
        onclick: () => {
          if (added) return;
          this.addVariant(p, v.variantId);
          overlay.remove();
        },
      });
      row.innerHTML = `<div><div>${escapeHtml(v.title || "Default")}</div>${v.sku ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px;font-weight:400">${escapeHtml(v.sku)}</div>` : ""}</div>
        <div style="font-size:14px;font-weight:700">${added ? "✓ Added" : fmtUSD(parseFloat(v.price) || 0)}</div>`;
      modal.appendChild(row);
    });
    overlay.appendChild(modal);
    this.shadow.querySelector(".qb-root")?.appendChild(overlay);
  }

  private addVariant(p: StorefrontProduct, variantId: string) {
    const v = p.variants.find(x => x.variantId === variantId);
    if (!v) return;
    if (this.state.cartItems.some(c => c.variantId === variantId)) return;
    this.cartAdd({
      variantId, sku: v.sku, title: p.title, variantTitle: v.title,
      qty: 1, price: v.price || "0", vendor: p.vendor, imageUrl: p.imageUrl,
    });
  }

  // ── File attachments ────────────────────────────────────────────────────
  // ONE control, two callers: the generic catch-all drop zone on the Finish
  // step (fieldId undefined), and a `file` custom field asking for a named
  // document (fieldId set). Extracted rather than copied — two drop zones
  // that drift apart is two upload UIs to keep honest.
  //
  // Inline-styled to match the rest of the embed. Drag-drop zone +
  // standard file input fallback (mobile browsers don't fire dragover).
  // Re-rendering the whole step on every state change keeps the data
  // model simple — the embed doesn't reuse DOM nodes between renders.
  //
  // Every limit here is a UI AFFORDANCE. The server re-derives all of
  // them from its own config before minting a token.
  private renderUploadControl(card: HTMLElement, opts: {
    label: string;
    fieldId?: string;
    required?: boolean;
    helpText?: string;
    limits: { max_files: number; max_file_size_mb: number; allowed_types: string[] };
    helperText?: string;
  }) {
    const { fieldId, limits } = opts;
    const accent = this.resolveAccent();
    // A field's control lists ONLY its own attachments; the generic zone
    // lists only the untagged ones. An attachment belongs to exactly one.
    const mine = this.state.attachments.filter(a => a.fieldId === fieldId);

    const sec = el("div", { class: "qb-field" });
    sec.appendChild(el("label", { class: "qb-label" }, opts.label + (opts.required ? " *" : "")));
    if (opts.helpText) {
      sec.appendChild(el("div", {
        style: "font-size:12px;color:#64748b;margin-top:-2px;margin-bottom:6px;line-height:1.5",
      }, opts.helpText));
    }

    const multiple = limits.max_files > 1;
    // Drop zone — clickable wrapper around a hidden <input type=file>.
    const zone = el("div", {
      style: "border:2px dashed #cbd5e1;border-radius:10px;padding:18px 16px;text-align:center;cursor:pointer;background:#f8fafc;transition:border-color .12s, background .12s",
    });
    zone.innerHTML = `<div style="font-size:13px;color:#475569;font-weight:600;margin-bottom:4px">Drop ${multiple ? "files" : "a file"} here or <span style="color:${accent};text-decoration:underline">browse</span></div>
      <div style="font-size:11px;color:#94a3b8">Up to ${limits.max_files} file${multiple ? "s" : ""} · max ${limits.max_file_size_mb} MB each</div>`;
    // Hidden file input — `accept` carries the allow-list so the OS
    // file picker filters appropriately. Server still re-checks.
    const input = el("input", {
      type: "file",
      ...(multiple ? { multiple: "true" } : {}),
      accept: (limits.allowed_types || []).join(","),
      style: "display:none",
    }) as HTMLInputElement;
    input.addEventListener("change", () => {
      if (input.files) this.addFiles(Array.from(input.files), fieldId);
      // Reset so picking the same file twice re-fires `change`.
      input.value = "";
    });
    zone.appendChild(input);
    zone.addEventListener("click", () => input.click());
    // Drag-drop handlers. preventDefault on dragover is what lets the
    // drop fire — easy thing to forget.
    zone.addEventListener("dragover", (e: any) => {
      e.preventDefault();
      zone.style.borderColor = accent;
      zone.style.background = "#eff6ff";
    });
    zone.addEventListener("dragleave", () => {
      zone.style.borderColor = "#cbd5e1";
      zone.style.background = "#f8fafc";
    });
    zone.addEventListener("drop", (e: any) => {
      e.preventDefault();
      zone.style.borderColor = "#cbd5e1";
      zone.style.background = "#f8fafc";
      const dt = e.dataTransfer;
      if (dt?.files?.length) this.addFiles(Array.from(dt.files), fieldId);
    });
    sec.appendChild(zone);

    if (opts.helperText) {
      sec.appendChild(el("div", {
        style: "font-size:11px;color:#94a3b8;margin-top:6px",
      }, opts.helperText));
    }

    // List of attachments (one row each). Status drives the right-hand
    // affordance: progress bar while uploading, error pill on failure,
    // checkmark + remove button on success.
    if (mine.length > 0) {
      const list = el("div", { style: "margin-top:10px;display:flex;flex-direction:column;gap:6px" });
      for (const a of mine) {
        const row = el("div", {
          style: "display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;font-size:12px",
        });
        const meta = el("div", { style: "flex:1;min-width:0" });
        meta.innerHTML = `<div style="font-weight:600;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(a.filename)}</div>
          <div style="font-size:11px;color:#94a3b8">${this.fmtBytes(a.size_bytes)}${a.status === "error" && a.error ? ` · <span style="color:#dc2626">${escapeHtml(a.error)}</span>` : ""}</div>`;
        row.appendChild(meta);

        if (a.status === "uploading") {
          const bar = el("div", { style: "width:100px;height:4px;background:#e2e8f0;border-radius:99px;overflow:hidden;flex-shrink:0" });
          const fill = el("div", { style: `height:100%;background:${accent};width:${Math.round(a.progress * 100)}%;transition:width .15s` });
          bar.appendChild(fill);
          row.appendChild(bar);
        } else if (a.status === "done") {
          row.appendChild(el("span", { style: "color:#16a34a;font-weight:700;font-size:13px;flex-shrink:0" }, "✓"));
        } else {
          row.appendChild(el("span", { style: "color:#dc2626;font-weight:700;font-size:13px;flex-shrink:0" }, "!"));
        }

        const rm = el("button", {
          type: "button",
          style: "background:none;border:none;color:#94a3b8;cursor:pointer;font-size:16px;padding:0 4px;line-height:1",
          "aria-label": "Remove file",
          onclick: () => { this.removeAttachment(a.id); },
        }, "×");
        row.appendChild(rm);
        list.appendChild(row);
      }
      sec.appendChild(list);
    }

    card.appendChild(sec);
  }

  /** Effective client-side limits for one upload surface. Mirrors the
   *  server's resolveUploadRules() layering (per-field overrides over the
   *  shop's, `max_files` defaulting to 1 on a field) so the customer sees
   *  the same numbers the server will enforce. Advisory ONLY — a tampered
   *  copy of this buys nothing; the token endpoint derives its own. */
  private uploadLimitsFor(f?: EmbedCustomField): { max_files: number; max_file_size_mb: number; allowed_types: string[] } {
    const fu = this.config.file_upload;
    // Fall back to the SAME defaults resolveFileUploadConfig() applies on
    // the server. A shop with a `file` field but no `file_upload` block at
    // all would otherwise show an empty allow-list here and reject every
    // file the server would have accepted.
    const shopMaxFiles = fu?.max_files ?? DEFAULT_FILE_UPLOAD.max_files;
    const shopMaxSize = fu?.max_file_size_mb ?? DEFAULT_FILE_UPLOAD.max_file_size_mb;
    const shopTypes = (fu?.allowed_types && fu.allowed_types.length > 0)
      ? fu.allowed_types
      : DEFAULT_FILE_UPLOAD.allowed_types;
    if (!f) return { max_files: shopMaxFiles, max_file_size_mb: shopMaxSize, allowed_types: shopTypes };
    return {
      max_files: f.max_files ?? 1,
      max_file_size_mb: f.max_file_size_mb ?? shopMaxSize,
      allowed_types: (f.allowed_types && f.allowed_types.length > 0) ? f.allowed_types : shopTypes,
    };
  }

  /** Add one or more files the customer selected/dropped, scoped to the
   *  control they dropped them on (`fieldId` undefined = the generic
   *  catch-all zone). Per-file client-side validation up front so the
   *  customer sees errors before we hit the network. Server re-checks
   *  every limit. */
  private addFiles(files: File[], fieldId?: string) {
    const field = fieldId
      ? (this.config.custom_fields || []).find(f => f.id === fieldId && canonicalCustomFieldType(f.type) === "file")
      : undefined;
    // A `file` field authorizes its own uploads — the shop-level toggle
    // gates only the generic zone. Same split the server makes.
    if (fieldId && !field) return;
    if (!fieldId && !this.config.file_upload?.enabled) return;

    const limits = this.uploadLimitsFor(field);
    const mine = this.state.attachments.filter(a => a.fieldId === fieldId);
    const remaining = limits.max_files - mine.length;
    if (remaining <= 0) {
      this.state.attachments.push({
        id: this.localId(),
        file: files[0],
        filename: files[0]?.name || "file",
        size_bytes: files[0]?.size || 0,
        content_type: files[0]?.type || "",
        status: "error",
        progress: 0,
        error: limits.max_files === 1 ? "Only one file" : `Maximum ${limits.max_files} files`,
        ...(fieldId ? { fieldId } : {}),
      });
      this.renderStep();
      return;
    }
    const toAdd = files.slice(0, remaining);
    for (const f of toAdd) {
      const a: Attachment = {
        id: this.localId(),
        file: f,
        filename: f.name,
        size_bytes: f.size,
        content_type: f.type || "application/octet-stream",
        status: "uploading",
        progress: 0,
        ...(fieldId ? { fieldId } : {}),
      };
      // Pre-flight: size + type. Mirrors server validation so the
      // customer sees the same error message without a round trip.
      const maxBytes = limits.max_file_size_mb * 1024 * 1024;
      if (f.size > maxBytes) {
        a.status = "error";
        a.error = `Too large (${this.fmtBytes(f.size)}, max ${limits.max_file_size_mb} MB)`;
      } else if (!this.matchesAllowedType(limits.allowed_types || [], f.name, a.content_type)) {
        a.status = "error";
        a.error = "File type not allowed";
      }
      this.state.attachments.push(a);
      if (a.status === "uploading") void this.uploadOne(a);
    }
    this.renderStep();
  }

  private localId(): string {
    return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  }

  private fmtBytes(n: number): string {
    if (!Number.isFinite(n) || n < 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  }

  /** Match a candidate against an allow-list of MIME types, MIME
   *  wildcards, and dot-extensions. Mirrors the server's
   *  isAllowedFile() in src/lib/file-upload.ts — duplicated rather
   *  than imported because the embed bundle stays free of server-only
   *  modules. */
  private matchesAllowedType(allowed: string[], filename: string, contentType: string): boolean {
    const ct = (contentType || "").toLowerCase();
    const name = (filename || "").toLowerCase();
    for (const rawPat of allowed) {
      const pat = String(rawPat || "").toLowerCase().trim();
      if (!pat) continue;
      if (pat.startsWith(".")) { if (name.endsWith(pat)) return true; continue; }
      if (pat.endsWith("/*"))  { if (ct.startsWith(pat.slice(0, -1))) return true; continue; }
      if (ct === pat) return true;
    }
    return false;
  }

  /** Drive a single file through the Vercel Blob client-upload
   *  pipeline. Updates the attachment row in-place via state and
   *  re-renders so progress / done / error transitions show up. */
  private async uploadOne(a: Attachment) {
    // The designer preview renders the REAL embed, and a merchant will
    // drop a file into it. A preview must never mint a token, never
    // touch the network, never write a blob. Leave the row visibly
    // "done" so the merchant sees what the customer would see.
    if (this.preview) {
      const cur = this.state.attachments.find(x => x.id === a.id);
      if (cur) { cur.status = "done"; cur.progress = 1; }
      this.renderStep();
      return;
    }
    try {
      // Scoped pathname — shop_slug stamps the object in storage so
      // a quick scan tells the right shop. quote-request-id isn't
      // known yet (we upload BEFORE submit) so we use "pending"; the
      // DB row gets the real id stamped at submit time anyway.
      const safe = a.filename.replace(/[^A-Za-z0-9._\- ]/g, "_").slice(0, 80);
      const pathname = `quote-requests/${SHOP_SLUG}/pending/${a.id}-${safe}`;
      const blob = await blobUpload(pathname, a.file, {
        access: "private",
        handleUploadUrl: `${API_ORIGIN}/api/embed/upload-token`,
        contentType: a.content_type,
        // The server treats every one of these as attacker-controlled.
        // field_id + form_id together are what authorize an upload for a
        // `file` question — the server resolves the form and requires a
        // `file` field with that id ON IT. We never send limits.
        clientPayload: JSON.stringify({
          shop_slug: SHOP_SLUG,
          content_type: a.content_type,
          size_bytes: a.size_bytes,
          ...(a.fieldId ? { field_id: a.fieldId } : {}),
          ...(this.formId ? { form_id: this.formId } : {}),
        }),
        // Multipart for anything over 5MB so we don't risk timeouts
        // on big drawings.
        multipart: a.size_bytes > 5 * 1024 * 1024,
        onUploadProgress: (event: any) => {
          // Vercel Blob's progress callback fires with either a
          // 0..1 fraction or { loaded, total } depending on version.
          // Coerce both.
          let p = 0;
          if (typeof event === "number") p = event;
          else if (event && typeof event.percentage === "number") p = event.percentage / 100;
          else if (event && typeof event.loaded === "number" && event.total) p = event.loaded / event.total;
          // Mutate in place + re-render. Frequent enough that we
          // batch via raf — but a plain re-render is fine at this
          // scale (one row).
          const cur = this.state.attachments.find(x => x.id === a.id);
          if (cur && cur.status === "uploading") {
            cur.progress = Math.max(0, Math.min(0.99, p));
            this.renderStep();
          }
        },
      });
      const cur = this.state.attachments.find(x => x.id === a.id);
      if (!cur) return;  // user removed it mid-upload
      cur.status = "done";
      cur.progress = 1;
      cur.blob_url = blob.url;
      cur.blob_pathname = blob.pathname;
      this.renderStep();
    } catch (e: any) {
      const cur = this.state.attachments.find(x => x.id === a.id);
      if (!cur) return;
      cur.status = "error";
      cur.error = e?.message || "Upload failed";
      this.renderStep();
    }
  }

  private removeAttachment(id: string) {
    // Local-only — we don't bother deleting the blob from storage
    // when the customer removes mid-form because (a) the upload may
    // not have completed and (b) abandoned uploads get pruned by a
    // future cron sweep. The DB row only gets written for submitted
    // files, so a removed-pre-submit blob is just a temporary
    // orphan in Blob.
    this.state.attachments = this.state.attachments.filter(a => a.id !== id);
    this.renderStep();
  }

  // ── Step 3: Finish Up ───────────────────────────────────────────────────
  private renderStep3(card: HTMLElement) {
    this.renderStepIntro(card, "finish");
    // Walk the merged + ordered field list with the same half-width
    // pairing + conditional filter the other steps use. Built-ins the
    // merchant moved into Finish surface here alongside Finish customs.
    this.renderOrderedRowsWithPairing(card, "finish");

    // Notes
    const nf = el("div", { class: "qb-field" });
    nf.appendChild(el("label", { class: "qb-label" }, this.resolveNotesLabel()));
    const ta = el("textarea", { class: "qb-textarea", rows: "5",
      placeholder: this.config.notes_placeholder || "Anything that'll help us serve you better.",
    });
    ta.value = this.state.notes;
    ta.addEventListener("input", () => { this.state.notes = ta.value; });
    nf.appendChild(ta);
    card.appendChild(nf);

    // ── File attachments (the generic catch-all zone) ───────────────────
    // Rendered only when the merchant has opted in via shop_config.
    // Hidden completely otherwise — no banner, no placeholder — so
    // shops that don't want this surface stay visually identical to
    // before the feature shipped. A `file` custom field does NOT turn
    // this on: "upload your water report" and "anything else we should
    // see?" are different questions. It lists untagged files only.
    if (this.config.file_upload?.enabled === true) {
      const fu = this.config.file_upload;
      this.renderUploadControl(card, {
        label: "Attachments",
        limits: this.uploadLimitsFor(),
        helperText: fu.helper_text,
      });
    }

    // Quick review (skip the shipping line when require_shipping=false)
    const review = el("div", { style: "background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;font-size:13px;color:#475569" });
    const showShip = this.shippingEnabled();
    review.innerHTML = `<div style="font-weight:700;color:#0f172a;margin-bottom:8px">Quick review</div>
      <div>${escapeHtml(this.state.firstName)} ${escapeHtml(this.state.lastName)}${this.state.company ? ` · ${escapeHtml(this.state.company)}` : ""}</div>
      ${showShip ? `<div>${escapeHtml(this.state.address)}</div>${this.state.address2 ? `<div>${escapeHtml(this.state.address2)}</div>` : ""}<div>${escapeHtml(this.state.city)}, ${escapeHtml(this.state.state)} ${escapeHtml(this.state.zip)}</div>` : ""}
      <div>${this.state.cartItems.length} item${this.state.cartItems.length === 1 ? "" : "s"} · Est. ${fmtUSD(cartTotalWithOptions(this.state.cartItems))}</div>`;
    card.appendChild(review);

    const trustLine = (this.config.trust_signal_text || "").trim()
      ? (this.config.trust_signal_text || "").trim()
      : `We respond within ${(this.config.response_time_text || "1 business day").trim()}. No spam — just your quote.`;
    card.appendChild(el("div", { class: "qb-trust", html: escapeHtml(trustLine) }));

    // Consent — anchored to Finish, right above the footer Submit
    // in wizard mode. No-ops in builder mode (see renderConsent);
    // renderBuilderLayout renders it explicitly after the submit
    // button instead.
    this.renderConsent(card);

    // Cart-push CTA intentionally absent on Step 4 — single canonical
    // home is Step 3 (Build Your Quote) where the customer is still
    // reviewing items. Adding "self-checkout" alongside the submit
    // button here muddies the primary intent (submitting the quote).
  }

  // ── Confirmation ───────────────────────────────────────────────────────
  private renderConfirmation(shell: HTMLElement) {
    shell.replaceChildren();
    // Inline resolveNextSteps — mirrors src/lib/form-steps.ts resolveNextSteps.
    // Blank steps are omitted; all-blank falls back to the default 3.
    const _s1 = (this.config.next_step_1 || "").trim();
    const _s2 = (this.config.next_step_2 || "").trim();
    const _s3 = (this.config.next_step_3 || "").trim();
    const _filled = [_s1, _s2, _s3].filter(s => s.length > 0);
    const _steps: string[] = _filled.length > 0 ? _filled : [
      "A rep reviews your items and verifies pricing + availability.",
      "We email you a finalized quote within 1 business day.",
      "You review, accept, and we get your order moving.",
    ];
    const card = el("div", { class: "qb-card" });
    card.innerHTML = `
      <div style="text-align:center;margin-bottom:18px">
        <div style="width:64px;height:64px;border-radius:32px;background:#dcfce7;color:#16a34a;margin:0 auto 12px;display:flex;align-items:center;justify-content:center;font-size:30px">✓</div>
        <h1 style="font-size:22px;font-weight:800;color:#0f172a;letter-spacing:-.02em;margin:0 0 6px">${escapeHtml(this.config.confirmation_headline || (typeof this.state.submitted === "number" && this.state.submitted > 0 ? `Your request #QR-${this.state.submitted} is in!` : `Your request is in!`))}</h1>
        <p style="font-size:14px;color:#64748b;margin:0;line-height:1.5">${escapeHtml(this.config.confirmation_body || `Thanks ${this.state.firstName || "there"} — we got everything you sent.`)}</p>
      </div>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px 18px;margin-bottom:18px">
        <div style="font-size:12px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.08em;margin-bottom:10px">${escapeHtml(this.config.next_steps_heading || "Here's what happens next")}</div>
        <ol style="margin:0;padding-left:18px;font-size:13px;color:#475569;line-height:1.8">
          ${_steps.map(s => `<li>${escapeHtml(s)}</li>`).join("")}
        </ol>
      </div>
    `;
    // Cart-push CTA intentionally absent on the confirmation screen —
    // contradicts the "wait for rep" messaging above. Self-checkout is
    // offered earlier (Step 3) where the customer is still in browse
    // mode; once they've submitted a quote request, the next move is
    // the rep's, not theirs.
    shell.appendChild(card);
  }

  // ── Field helpers ──────────────────────────────────────────────────────
  private field(label: string, key: keyof State, autocomplete: string, placeholder = "", helper = ""): HTMLElement {
    const wrap = el("div", { class: "qb-field" });
    wrap.appendChild(el("label", { class: "qb-label" }, label));
    const input = el("input", {
      class: "qb-input",
      type: key === "email" ? "email" : (key === "phone" ? "tel" : "text"),
      autocomplete,
      placeholder,
    });
    input.value = String((this.state as any)[key] || "");
    input.addEventListener("input", () => {
      (this.state as any)[key] = input.value;
    });
    wrap.appendChild(input);
    if (helper) {
      // Inline merchant-supplied helper line (e.g. "Optional but
      // speeds up our response"). Surfaces the per-field copy set
      // in /settings/forms → Built-in fields.
      wrap.appendChild(el("div", {
        style: "font-size:12px;color:#94a3b8;margin-top:6px;line-height:1.45",
      }, helper));
    }
    return wrap;
  }

  /** Lookup the merchant's override for a built-in field. Mirrors
   *  resolveBuiltinField() in src/lib/builtin-fields.ts — defined
   *  inline so the embed bundle doesn't pull a separate module. */
  private builtinField(key: BuiltinKey): { required: boolean; helper: string; suffix: string; label: string } {
    const cfg = (this.config as any).builtin_fields?.[key] || {};
    const required = typeof cfg.required === "boolean" ? cfg.required : !!BUILTIN_DEFAULTS_REQUIRED[key];
    const helper = (cfg.helper || "").toString();
    const label = (typeof cfg.label === "string" && cfg.label.trim().length > 0)
      ? cfg.label : BUILTIN_LABELS[key];
    return { required, helper, suffix: required ? " *" : "", label };
  }

  /** Country-aware postal label for the zip field. Merchant override
   *  wins; else US "Zip code" / non-US "Postal code". Mirrors the hosted
   *  form's postalLabelForCountry precedence. */
  private postalLabel(): string {
    const override = (this.config as any).builtin_fields?.zip?.label;
    if (typeof override === "string" && override.trim().length > 0) return override;
    return postalLabelForCountry(this.state.country);
  }

  /** Pin state.country into the merchant's allowed-country set. The
   *  fieldState() render does this live, but submit can fire from a path
   *  that never rendered the shipping step (shipping disabled, quick
   *  mode, a resumed draft) — so we re-pin here too, mirroring the hosted
   *  form's unconditional normalizer, so a single-country store never
   *  submits a stale "US". offered always has ≥1 entry. */
  private normalizeCountry() {
    const offered = allowedCountries((this.config as any).allowed_countries);
    if (!offered.some(c => c.code === this.state.country)) {
      this.state.country = offered[0].code;
    }
  }

  /** Render two ordered field rows side-by-side in a 2-col grid.
   *  Legacy backstop for the first_name+last_name pairing the embed
   *  had pre-V2; full per-field column_width comes in a follow-up. */
  private renderTwoColumnPair(card: HTMLElement, a: OrderedEmbedField, b: OrderedEmbedField) {
    const row = el("div", { class: "qb-row-2" });
    this.renderOrderedField(row, a);
    this.renderOrderedField(row, b);
    card.appendChild(row);
  }

  private renderThreeColumnRow(card: HTMLElement, a: OrderedEmbedField, b: OrderedEmbedField, c: OrderedEmbedField) {
    const row = el("div", { class: "qb-row-3" });
    this.renderOrderedField(row, a);
    this.renderOrderedField(row, b);
    this.renderOrderedField(row, c);
    card.appendChild(row);
  }

  private renderOrderedField(card: HTMLElement, row: OrderedEmbedField) {
    if (row.kind === "custom") {
      this.renderSingleCustomField(card, row.field);
      return;
    }
    const k = row.key;
    const bf = this.builtinField(k);
    switch (k) {
      case "first_name":
      case "last_name":
      case "company":
      case "email":
      case "phone":
      case "address":
      case "street_address_2":
      case "city":
      case "zip": {
        const stateKey: keyof State =
          k === "first_name" ? "firstName" :
          k === "last_name" ? "lastName" :
          k === "company" ? "company" :
          k === "email" ? "email" :
          k === "phone" ? "phone" :
          k === "address" ? "address" :
          k === "street_address_2" ? "address2" :
          k === "city" ? "city" :
          "zip";
        const autocomplete =
          k === "first_name" ? "given-name" :
          k === "last_name" ? "family-name" :
          k === "company" ? "organization" :
          k === "email" ? "email" :
          k === "phone" ? "tel" :
          k === "address" ? "street-address" :
          k === "street_address_2" ? "address-line2" :
          k === "city" ? "address-level2" :
          "postal-code";
        const placeholder = ((k === "company" || k === "street_address_2") && !bf.required) ? "Optional" : "";
        // Postal label localizes from country (US "Zip code" / else
        // "Postal code") unless the merchant set an explicit zip-label
        // override (which bf.label already reflects). Mirrors the hosted
        // form; repaints with the step when country changes.
        const labelBase = k === "zip" ? this.postalLabel() : bf.label;
        card.appendChild(this.field(labelBase + bf.suffix, stateKey, autocomplete, placeholder, bf.helper));
        return;
      }
      case "preferred_contact": {
        const wrap = el("div", { class: "qb-field" });
        wrap.appendChild(el("label", { class: "qb-label" }, bf.label + bf.suffix));
        const toggleRow = el("div", { class: "qb-toggle-row" });
        (["email", "phone", "sms"] as const).forEach(v => {
          const b = el("button", {
            class: `qb-btn ${this.state.preferred === v ? "active" : ""}`,
            onclick: () => { this.state.preferred = v; this.renderStep(); },
          }, v === "sms" ? "SMS" : v[0].toUpperCase() + v.slice(1));
          toggleRow.appendChild(b);
        });
        wrap.appendChild(toggleRow);
        if (bf.helper) {
          wrap.appendChild(el("div", { style: "font-size:12px;color:#94a3b8;margin-top:6px;line-height:1.45" }, bf.helper));
        }
        card.appendChild(wrap);
        return;
      }
      case "country": {
        // Single allowed country → hide; normalizeCountry() pins the fixed
        // code into state so the submit payload is unaffected.
        this.normalizeCountry();
        if (allowedCountries((this.config as any).allowed_countries).length === 1) return;
        card.appendChild(this.fieldCountry());
        return;
      }
      case "state": {
        card.appendChild(this.fieldRegion());
        return;
      }
    }
  }

  // Country picker only. A single allowed country → static read-only display
  // (de-clutter; cascade still reads this.state.country). Multiple → dropdown
  // of offered countries; on change clears region so a stale code can't ride
  // along under a different country. Mirrors RequestPageClient case "country".
  private fieldCountry(): HTMLElement {
    const ct = this.builtinField("country");
    // Pin country into the offered set before rendering — a stale localStorage
    // value outside the allow-list would otherwise pre-select a hidden option.
    this.normalizeCountry();
    const offered = allowedCountries((this.config as any).allowed_countries);
    const fixedCountry = offered.length === 1 ? offered[0] : null;
    const countryField = el("div", { class: "qb-field" });
    countryField.appendChild(el("label", { class: "qb-label" }, ct.label + ct.suffix));
    if (fixedCountry) {
      countryField.appendChild(el("div", { class: "qb-input", style: "background:#f8fafc;color:#475569;cursor:default" }, fixedCountry.name));
    } else {
      const sel = el("select", { class: "qb-select", autocomplete: "country" });
      offered.forEach(c => {
        const o = el("option", { value: c.code }, c.name);
        if (this.state.country === c.code) o.setAttribute("selected", "");
        sel.appendChild(o);
      });
      sel.addEventListener("change", () => {
        this.state.country = (sel as HTMLSelectElement).value;
        this.state.state = "";
        this.renderStep();
      });
      countryField.appendChild(sel);
    }
    if (ct.helper) {
      countryField.appendChild(el("div", { style: "font-size:12px;color:#94a3b8;margin-top:6px;line-height:1.45" }, ct.helper));
    }
    return countryField;
  }

  // Region control only — reads this.state.country for the cascade so the
  // label + options always reflect the currently-selected country, regardless
  // of the two fields' relative display_order. Mirrors RequestPageClient case "state".
  private fieldRegion(): HTMLElement {
    const st = this.builtinField("state");
    const region = regionConfigForCountry(this.state.country);
    const regionField = el("div", { class: "qb-field" });
    regionField.appendChild(el("label", { class: "qb-label" }, region.label + st.suffix));
    if (region.mode === "select") {
      const sel = el("select", { class: "qb-select", autocomplete: "address-level1" });
      sel.appendChild(el("option", { value: "" }, `— ${region.label} —`));
      (region.options || []).forEach(opt => {
        const o = el("option", { value: opt.code }, opt.code);
        if (this.state.state === opt.code) o.setAttribute("selected", "");
        sel.appendChild(o);
      });
      sel.addEventListener("change", () => { this.state.state = (sel as HTMLSelectElement).value; });
      regionField.appendChild(sel);
    } else {
      const input = el("input", { class: "qb-input", type: "text", autocomplete: "address-level1" });
      input.value = this.state.state || "";
      input.addEventListener("input", () => { this.state.state = (input as HTMLInputElement).value; });
      regionField.appendChild(input);
    }
    if (st.helper) {
      regionField.appendChild(el("div", {
        style: "font-size:12px;color:#94a3b8;margin-top:6px;line-height:1.45",
      }, st.helper));
    }
    return regionField;
  }

  // ── Cart sync (2.2) ─────────────────────────────────────────────────────
  /** True when the quote and the live Shopify cart hold the same
   *  `{ variantId → qty }` contents — both Import-from-cart and
   *  Send-to-cart would be no-ops, so both grey out (see the "Your
   *  items" header). Compares against the LAST-FETCHED snapshot
   *  (boot, or refreshed after importCart()/exportCart()) — a cart
   *  changed in another tab is stale until the next open. */
  private get cartInSync(): boolean {
    return cartContentsMatch(this.state.cartItems, this.state.shopifyCartItems);
  }

  private async importCart() {
    if (this.preview) return;
    const cart = await fetchShopifyCart();
    if (!cart.length) {
      this.showToast("Your Shopify cart is empty — nothing to import.", "error");
      return;
    }
    // Authoritative: replace staged items with EXACTLY the Shopify cart
    // (never accumulate onto prior state). Re-clicking is idempotent —
    // the same cart yields the same result and never inflates quantities.
    // Defensive per-item copy so we never alias the fetched objects.
    // Unify: cartReplace writes the live cart into the basket store
    // (populating it — Part 1.6 cart-import semantics) when basket-backed,
    // else assigns state.cartItems directly.
    // Refresh the snapshot to the just-fetched cart BEFORE cartReplace —
    // once it lands, cartItems === shopifyCartItems, so cartInSync flips
    // true and both header buttons grey out on the render cartReplace
    // triggers (basket subscription or its own renderStep()).
    const importedCount = cart.reduce((s, it) => s + it.qty, 0);
    this.state.importableCount = cart.length;
    this.state.shopifyCartItems = cart;
    this.cartReplace(cart.map(it => ({ ...it })));
    this.showToast(`✓ Imported ${importedCount} item${importedCount === 1 ? "" : "s"} from your cart`, "success");
  }

  private async exportCart() {
    // Preview: the "Your items" header renders its "Send to cart →" button so
    // the merchant sees the live form, but there's no storefront cart to write to.
    if (this.preview) return;
    if (!this.state.cartItems.length) {
      this.showToast("Add at least one item before replacing your cart.", "error");
      return;
    }
    // Clear any stale "Sold out" badges from a previous failed attempt
    // before retrying. Items stay intact — this never empties the form.
    if (this.state.soldOutTitles && this.state.soldOutTitles.length) {
      this.state.soldOutTitles = [];
      this.renderStep();
    }
    const itemCount = this.state.cartItems.reduce((s, it) => s + it.qty, 0);
    const result = await pushCartToShopify(this.state.cartItems);
    if (result.ok) {
      this.showToast(`✓ Cart updated · ${itemCount} item${itemCount === 1 ? "" : "s"}`, "success");
      // Send-to-cart pushes a COPY of the builder's items to the Shopify
      // cart — it must NOT wipe the form. Leave state.cartItems and the
      // visible "Your items" list (and contact) exactly as they were; we
      // clear nothing on the draft here. (A later cart-page re-entry
      // re-imports the live cart authoritatively, so there's no stale-
      // accumulation risk.)
      // Refresh the theme's cart UI (count/ticker + cart-page line items)
      // so the new contents show without a manual reload.
      await this.dispatchCartUpdated();
      // Cart now holds exactly state.cartItems (result.ok means every
      // line landed) — refresh the snapshot so cartInSync flips true and
      // both header buttons grey out immediately, without a re-boot.
      this.state.importableCount = this.state.cartItems.length;
      this.state.shopifyCartItems = this.state.cartItems.map(it => ({ ...it }));
      this.renderStep();
    } else {
      // Humanized, never the raw `add: 422: {...}` JSON. Restore-aware so
      // the shopper knows their prior cart is safe.
      let msg: string;
      if (result.soldOut) {
        const names = result.soldOutNames || [];
        const restoredSuffix = result.restored ? " — your previous cart was restored" : "";
        if (names.length) {
          // Name the offending product(s) so the user knows exactly
          // which row to remove, and badge those rows below.
          const list = names.map(n => `"${n}"`).join(", ");
          const verb = names.length === 1 ? "is" : "are";
          const obj = names.length === 1 ? "it" : "them";
          msg = `${list} ${verb} sold out and couldn't be added${restoredSuffix}. Remove ${obj} and try again.`;
          this.state.soldOutTitles = names;
          this.renderStep();
        } else {
          msg = result.restored
            ? "Some items are sold out and couldn't be added — your previous cart was restored. Remove the sold-out item and try again."
            : "Some items are sold out and couldn't be added. Remove the sold-out item and try again.";
        }
      } else if (result.restored) {
        msg = "We couldn't update your cart, so your previous cart was restored. Please try again.";
      } else {
        msg = "We couldn't update your cart. Please try again in a moment.";
      }
      this.showToast(msg, "error");
    }
  }

  /** Confirmation toast. Success toasts auto-dismiss (~3s). Error
   *  toasts STAY until the customer dismisses them (× control) — an
   *  out-of-stock explanation must be readable, not flash past in 3s.
   *  Mounted in the shadow root so theme CSS can't reach it;
   *  position:fixed escapes any clipped overflow ancestors. */
  private showToast(message: string, kind: "success" | "error" = "success") {
    const root = this.shadow;
    if (!root) return;
    // Reuse any in-flight toast so a quick double-click doesn't
    // stack two on top of each other.
    let toast = root.querySelector(".qb-toast") as HTMLElement | null;
    if (!toast) {
      toast = el("div", { class: "qb-toast" });
      root.appendChild(toast);
    }
    const node = toast;
    const dismiss = () => {
      node.classList.remove("qb-toast-visible");
      setTimeout(() => node.remove(), 300);
    };
    const prevTimer = (node as any).__qbDismissTimer;
    if (prevTimer) { clearTimeout(prevTimer); (node as any).__qbDismissTimer = null; }
    // Rebuild content: message span + (errors only) a × close button.
    node.replaceChildren(el("span", { class: "qb-toast-msg" }, message));
    node.className = `qb-toast qb-toast-${kind}`;
    if (kind === "error") {
      node.appendChild(el("button", {
        class: "qb-toast-close", type: "button", "aria-label": "Dismiss", onclick: dismiss,
      }, "×"));
    }
    // Force reflow so the opacity transition runs on every call,
    // not just the first.
    void node.offsetWidth;
    node.classList.add("qb-toast-visible");
    // Success auto-dismisses; errors persist until the × is clicked.
    if (kind !== "error") {
      (node as any).__qbDismissTimer = setTimeout(dismiss, 3000);
    }
  }

  /** Refresh the theme's cart UI after a successful send-to-cart so the
   *  count/ticker (and, on the cart page, the line items + subtotal)
   *  update without a manual reload. Two layers, both best-effort:
   *    1. Re-fetch /cart.js and dispatch the common cart events WITH the
   *       fresh cart in `detail` — themes that listen update their ticker.
   *    2. On the cart page, re-render the cart sections via the Section
   *       Rendering API so the line items refresh in place.
   *  Conventions vary across themes (Dawn / Horizon / Debut / custom), so
   *  this is intentionally generous + tolerant of no listener. */
  private async dispatchCartUpdated() {
    if (typeof document === "undefined") return;
    // 1. Fresh cart payload for event listeners.
    let cart: any = null;
    try {
      const r = await fetch("/cart.js", { credentials: "same-origin" });
      if (r.ok) cart = await r.json().catch(() => null);
    } catch { /* best-effort — events still fire without detail */ }
    try {
      const detail = cart ? { cart } : {};
      ["cart:refresh", "cart:updated", "cart-updated"].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { detail }));
      });
      window.dispatchEvent(new CustomEvent("cart:refresh", { detail }));
      // Themes that re-fetch sections on shopify:section:load.
      document.dispatchEvent(new CustomEvent("shopify:section:load"));
      // Dawn/Horizon PubSub — themes subscribe to a 'cart-update' channel.
      // Exposed inconsistently across themes; fire if a publish hook exists.
      const publish = (window as any).Shopify?.PubSub?.publish || (window as any).publish;
      if (typeof publish === "function") {
        try { publish("cart-update", { source: "quotesense", cart }); } catch { /* swallow */ }
      }
    } catch { /* swallow — best-effort */ }
    // 2. Cart page: re-render the cart sections in place.
    if (this.isCartPageContext()) {
      await this.refreshCartSections().catch(() => { /* best-effort */ });
    }
  }

  /** Re-render the cart page's Shopify sections via the Section Rendering
   *  API (`GET {path}?sections=<ids>`) and swap their inner HTML, so the
   *  cart line items + subtotal reflect the new contents without a reload.
   *  Targets sections whose id contains "cart" (Dawn: main-cart-items /
   *  main-cart-footer). Best-effort + theme-dependent: a theme that names
   *  its cart sections without "cart", or renders the cart entirely client-
   *  side, won't be refreshed in place — the event dispatch above is the
   *  fallback and a manual reload always works. */
  private async refreshCartSections() {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const nodes = Array.from(document.querySelectorAll('[id^="shopify-section-"]')) as HTMLElement[];
    const ids = nodes
      .map(n => n.id.replace(/^shopify-section-/, ""))
      .filter(id => id && /cart/i.test(id));
    if (!ids.length) return;
    const url = `${window.location.pathname}?sections=${encodeURIComponent(ids.join(","))}`;
    const r = await fetch(url, { credentials: "same-origin" });
    if (!r.ok) return;
    const data = await r.json().catch(() => null);
    if (!data || typeof data !== "object") return;
    for (const id of ids) {
      const html = (data as Record<string, unknown>)[id];
      const target = document.getElementById(`shopify-section-${id}`);
      if (typeof html === "string" && target) target.innerHTML = html;
    }
  }

  // ── Bundle add ─────────────────────────────────────────────────────────
  private async addBundle(b: Bundle) {
    // Preview: bundle chips render, but adding one would fetch variant meta.
    if (this.preview) return;
    // Hydrate variant details via storefront product detail OR our API.
    const ids = b.lineItems.map(li => li.variantId);
    const lookup = this.state.shopifyDetected
      ? await this.lookupVariantsViaStorefront(b)
      : await this.lookupVariantsViaApi(ids);

    for (const li of b.lineItems) {
      const meta = lookup[li.variantId];
      if (!meta) continue;
      // Dedup-by-variant (existing → bump qty by li.qty) is the write-
      // through helper's / store's own semantic.
      this.cartAdd({
        variantId: meta.variantId, sku: meta.sku, title: meta.title,
        variantTitle: meta.variantTitle, qty: li.qty || 1, price: meta.price,
        vendor: meta.vendor, imageUrl: meta.imageUrl || null,
      });
    }
  }

  private async lookupVariantsViaApi(ids: string[]): Promise<Record<string, CartItem>> {
    try {
      const r = await fetch(`${API_ORIGIN}/api/shopify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        // shop required — anonymous proxy op, server-validated + scoped.
        body: JSON.stringify({ lookupVariantIds: ids, shop: SHOP_SLUG }),
      });
      const d = await r.json();
      const out: Record<string, CartItem> = {};
      for (const it of (d.items || [])) {
        out[String(it.variantId)] = {
          variantId: String(it.variantId), productId: String(it.productId || ""),
          title: it.title, variantTitle: it.variantTitle || "", sku: it.sku || "",
          qty: 1, price: it.price || "0", vendor: it.vendor || "", imageUrl: it.imageUrl || null,
        };
      }
      return out;
    } catch { return {}; }
  }

  // Storefront fallback — walks /products.json for variant metadata. Slow-ish
  // but works without an API key. In practice bundles are small (<10 lines).
  private async lookupVariantsViaStorefront(b: Bundle): Promise<Record<string, CartItem>> {
    const out: Record<string, CartItem> = {};
    // Bundles store opaque variant ids; without a product handle we can't
    // do per-variant lookups via storefront. We fall back to our proxy.
    return this.lookupVariantsViaApi(b.lineItems.map(li => li.variantId));
  }

  // ── Validate + advance ─────────────────────────────────────────────────
  private next() {
    this.state.errors = this.validate();
    if (this.state.errors.length) { this.renderStep(); return; }
    this.state.step = Math.min(this.stepDefs.length - 1, this.state.step + 1);
    this.renderStep();
  }

  private validate(): string[] {
    const sid = this.currentStepId;
    if (sid === "about" || sid === "shipping" || sid === "finish") {
      const builtinValues: ValidateBuiltinValues = {
        first_name: this.state.firstName,
        last_name:  this.state.lastName,
        email:      this.state.email,
        phone:      this.state.phone,
        company:    this.state.company,
        preferred_contact: this.state.preferred,
        address:    this.state.address,
        street_address_2: this.state.address2,
        city:       this.state.city,
        country:    this.state.country,
        state:      this.state.state,
        zip:        this.state.zip,
      };
      return validateFormState(
        sid,
        this.config as any,
        builtinValues,
        this.state.customValues,
        {
          consentAccepted:  this.state.consentAccepted,
          billingSame:      this.state.billingSame,
          billingAddress1:  this.state.billingAddress1,
          billingCity:      this.state.billingCity,
          billingZip:       this.state.billingZip,
          // A required `file` question is answered by an attachment, not
          // by a value — isCustomFilled() reads this list. Drafts drop
          // attachments deliberately, so a resumed draft correctly shows
          // the question as unanswered until the customer re-attaches.
          attachments: this.state.attachments.map(a => ({
            field_id: a.fieldId ?? null,
            status: a.status,
          })),
        },
      );
    } else if (sid === "quote") {
      // Describe-mode bypasses the items requirement when the
      // customer typed something — the rep gets a free-text ask
      // instead of a structured cart. Empty describe-mode still
      // fails: the customer needs to either pick items OR write
      // a description; staring at an empty textarea isn't a
      // submittable ask.
      const hasItems = this.state.cartItems.length > 0;
      const hasDescription = this.state.descriptionMode && this.state.description.trim().length > 0;
      if (!hasItems && !hasDescription) {
        return ["Add at least one item, or describe what you need above."];
      }
    } else if (sid === "describe") {
      // First-class Describe step. Only blocks submit when the merchant
      // marked the section required (DescribeSectionConfig.required) and
      // the textarea is empty — an optional describe step never gates.
      if (this.config.describe?.required && this.state.description.trim().length === 0) {
        return ["Please tell us a bit about what you need."];
      }
    }
    return [];
  }

  /** Current value for a built-in field, normalized to a string for
   *  the required-empty check. */
  private builtinValue(k: BuiltinKey): string {
    switch (k) {
      case "first_name": return this.state.firstName;
      case "last_name":  return this.state.lastName;
      case "email":      return this.state.email;
      case "phone":      return this.state.phone;
      case "company":    return this.state.company;
      case "preferred_contact": return this.state.preferred;
      case "address":    return this.state.address;
      case "street_address_2": return this.state.address2;
      case "city":       return this.state.city;
      case "country":    return this.state.country;
      case "state":      return this.state.state;
      case "zip":        return this.state.zip;
    }
  }

  private async submit() {
    // Preview: Submit is inert. Not even validation runs — surfacing errors
    // on demo data would read as a broken form to the merchant.
    if (this.preview) return;
    // Pin country into the allowed set before submit — the shipping
    // step (and its fieldCountry normalizer) may never have rendered
    // (shipping disabled / resumed draft), so a single-country store
    // must not submit a stale "US".
    this.normalizeCountry();
    // Builder mode validates every step at once (there are no per-
    // step transitions to gate). Wizard mode validates only the
    // current step — same behavior as before. Aggregate errors
    // surface at the top of the form; the renderBuilderLayout
    // scrolls them into view if off-screen.
    this.state.errors = this.layoutMode === "builder" ? this.validateAll() : this.validate();
    if (this.state.errors.length) {
      this.renderStep();
      if (this.layoutMode === "builder") {
        const errBox = this.shadow.querySelector("[data-qb-builder-errors]") as HTMLElement | null;
        if (errBox && typeof errBox.scrollIntoView === "function") {
          errBox.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      return;
    }
    this.state.submitting = true;
    this.renderStep();
    // Drop hidden conditional custom fields from the payload so stale
    // values from now-irrelevant questions don't ride along.
    const hiddenCustomIds = new Set<string>();
    const submitFormValues = this.currentFormValues();
    for (const sid of ["about", "shipping", "finish"] as const) {
      for (const row of resolveFormLayout(this.config as any, sid)) {
        if (row.kind === "custom" && !isFieldVisible(row, submitFormValues)) hiddenCustomIds.add(row.field.id);
      }
    }
    (this.state as any).hiddenCustomFieldIds = Array.from(hiddenCustomIds);
    const res = await submitQuoteRequest(this.state);
    this.state.submitting = false;
    if (res.ok) {
      // `submitted` drives the QR-N confirmation copy below — store
      // the per-shop sequence so new tenants don't see their first
      // submission labelled with the global PK (e.g. QR-6 instead of
      // QR-1 just because SW already burned ids 1..5).
      // Sentinel -1 when shop_seq is missing — keeps state.submitted
      // truthy for the "show confirmation" gate without leaking
      // the global id into the confirmation headline. The render
      // path gates the "#QR-N" display on submitted > 0.
      this.state.submitted = res.shopSeq ?? -1;
      // Drop the persisted draft — the customer just submitted; we
      // don't want their next storefront visit to re-hydrate with
      // the same form.
      this.clearPersistedDraft();
      // Basket-originated session: empty the standalone basket so the
      // just-submitted lines don't linger in the indicator.
      try { this.onSubmitSuccess?.(); } catch { /* basket clear must not break the confirmation */ }
      try { merchantConfig.onSubmit?.(res.id); } catch { /* host code shouldn't break our flow */ }
    } else {
      this.state.errors = [res.error];
    }
    this.renderStep();
  }

  /** Builder-mode validation. Walks every step's validation logic in
   *  sequence and aggregates the results — the wizard's validate()
   *  only ever checks the current step, which doesn't work when
   *  there ARE no current-step transitions. Temporarily mutates
   *  this.state.step around each validate() call (validate() reads
   *  currentStepId) and restores it. Dedup by string so an error
   *  that two steps would both flag (rare) doesn't show twice. */
  private validateAll(): string[] {
    const originalStep = this.state.step;
    const defs = this.stepDefs;
    const all: string[] = [];
    try {
      for (let i = 0; i < defs.length; i++) {
        this.state.step = i;
        all.push(...this.validate());
      }
    } finally {
      this.state.step = originalStep;
    }
    return Array.from(new Set(all));
  }

  /** Builder layout — render the same form content as the wizard, but
   *  as a single scrolling page with section headings, in whatever
   *  order the resolved form's section_order derives (this.stepDefs —
   *  same config-driven order the wizard's step strip uses, so a
   *  builder-mode placement pointed at a non-products form renders
   *  that form's sections, not a hardcoded products-first layout).
   *  Single Submit at the bottom validates every step at once via
   *  validateAll(). The qb-nav-footer slot is cleared since there's
   *  no Back/Next navigation. */
  private renderBuilderLayout(shell: HTMLElement, navFooter: HTMLElement | null) {
    shell.replaceChildren();
    if (navFooter) navFooter.replaceChildren();

    // Customer-recognized banner (B2B / logged-in customer auto-fill)
    // sits above all sections so it stays visible to the customer
    // throughout the scroll.
    const banner = this.buildCustomerBanner();
    if (banner) shell.appendChild(banner);

    // Aggregate error box — all step errors at once, anchored at
    // top so a scrollIntoView on submit failure lands here.
    if (this.state.errors.length) {
      const errBox = el("div", {
        class: "qb-errors",
        "data-qb-builder-errors": "true",
        style: "margin-bottom:14px",
      });
      this.state.errors.forEach(e => errBox.appendChild(el("div", {}, e)));
      shell.appendChild(errBox);
    }

    const buildSection = (heading: string, render: (card: HTMLElement) => void) => {
      const card = el("div", { class: "qb-card", style: "margin-top:14px" });
      card.appendChild(el("h2", { class: "qb-title" }, heading));
      render(card);
      shell.appendChild(card);
    };

    // Loop the resolved step order — this.stepDefs already derives
    // section_order (Products droppable, Describe includable, Finish
    // forced last; see resolveWizardSteps in embed-step-order.ts). With
    // no section_order the fallback order is quote→about→shipping(if
    // enabled)→finish — today's exact builder order — so an
    // un-migrated shop renders byte-for-byte as before with no branch
    // here dedicated to that case. The subtotal summary (and cart-push
    // escape hatch) for the quote/products section gets appended
    // inside renderStep2 via the shared appendSubtotalSummary helper —
    // same surface across wizard inline, builder, modal, and drawer
    // layouts — and naturally disappears when Products is dropped from
    // section_order.
    for (const s of this.stepDefs) {
      // Each section renderer (renderStep0/1/3 → renderStepIntro,
      // renderDescribeStep) renders its own intro internally — buildSection
      // must not render a second copy.
      buildSection(s.label, (card) => this.renderSectionInto(s.id, card));
    }

    // Single submit at the bottom of the scroll region. Disabled
    // while uploads are still in flight (same gate the wizard's
    // final-step submit uses). Block-level full width so the action
    // reads as the page's primary CTA.
    const uploadsPending = this.state.attachments.some(a => a.status === "uploading");
    const submitWrap = el("div", { style: "margin-top:18px" });
    const submitBtn = el("button", {
      type: "button",
      class: "qb-btn qb-btn-primary",
      style: "width:100%;padding:14px 20px;font-size:15px;font-weight:700",
      disabled: (this.state.submitting || uploadsPending) ? "true" : undefined as any,
      onclick: () => void this.submit(),
    }, this.state.submitting
        ? "Submitting…"
        : uploadsPending
          ? "Uploading files…"
          : this.resolveSubmitLabel());
    submitWrap.appendChild(submitBtn);
    shell.appendChild(submitWrap);

    // Consent — below Submit (the finish-section renderStep3 call
    // above suppresses its own consent render in builder mode so it
    // isn't double-rendered above submitWrap). Forced past the
    // layoutMode guard since this IS the builder's intended slot.
    this.renderConsent(shell, { force: true });

    // No support-email footer in builder mode — the merchant's
    // storefront page already has its own footer. Adding another
    // "Questions? info@..." line inside the embed reads as
    // duplicate chrome.
  }
}

// ── Entry-point mounting ──────────────────────────────────────────────────
function escapeHtml(s: string): string {
  return (s || "").replace(/[&<>"']/g, (c) => (
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;"
  ));
}

function mountInline(target: HTMLElement) {
  // Inline `display: block` is intentional — DO NOT REMOVE.
  //
  // The wrapper looks empty from the light DOM (every rendered node
  // lives inside its shadow root). Defensive theme CSS like
  // `div:empty { display: none }` — present on dawn-derived Shopify
  // themes by default — hides the wrapper, which silently hides the
  // entire embed even though the shadow DOM rendered it correctly.
  // Setting display:block as an inline style outranks the theme's
  // class-level rule. The form renders + scrolls naturally because
  // its own qb-shell layout takes over inside the shadow root.
  const wrapper = el("div", {
    "data-qb-wrapper": "inline",
    style: "display:block",
  });
  target.replaceChildren(wrapper);
  const qb = new QuoteBuilder(wrapper, mountAppearance(target));
  void qb.boot();
}

/** Per-mount form appearance (#4 color, #3 size) read off the theme
 *  block's mount div. The inline-quote-builder block writes
 *  data-accent-color + data-form-scale on the [data-quotesense-mount]
 *  element so each placed block can style its own form independently of
 *  the shared embed script tag (multiple blocks can coexist on a page).
 *  Empty/absent attrs leave the overrides unset → falls back to the
 *  shop brand accent / no scaling, preserving today's behavior. */
function mountAppearance(target: HTMLElement): { accentColor?: string; formScale?: number; formId?: string } {
  const out: { accentColor?: string; formScale?: number; formId?: string } = {};
  const accent = (target.getAttribute("data-accent-color") || "").trim();
  if (accent) out.accentColor = accent;
  const scaleRaw = (target.getAttribute("data-form-scale") || "").trim();
  if (scaleRaw) out.formScale = clampFormScale(scaleRaw);
  const formId = resolveMountFormId(target);
  if (formId) out.formId = formId;
  return out;
}

/** Multi-form id for a mount/launcher (docs/MULTI_FORM.md §6). Prefers a
 *  per-mount `data-form-id` / `data-qa-form-id` (Phase 2's theme-block
 *  setting), then the script-tag-level SCRIPT_FORM_ID, else undefined
 *  (shop default). Only a slug-shaped, non-"default" value threads
 *  through; the server's resolveForm() is the final authority. */
function resolveMountFormId(target?: HTMLElement | null): string | undefined {
  const raw = (
    target?.getAttribute("data-form-id") ||
    target?.getAttribute("data-qa-form-id") ||
    ""
  ).trim().toLowerCase();
  const fromMount = /^[a-z0-9][a-z0-9-]*$/.test(raw) && raw !== "default" ? raw : "";
  return fromMount || SCRIPT_FORM_ID || undefined;
}

function mountBuilder(target: HTMLElement) {
  // Same display:block defense as mountInline — keep theme CSS
  // (`div:empty { display: none }`) from hiding the wrapper before
  // shadow content paints. Builder mode differs from inline only in
  // layoutMode — the wrapper marker stays "inline" so any inline-
  // mode CSS / DOM probes pick it up unchanged. The QuoteBuilder's
  // renderStep dispatcher reads layoutMode to pick the scrolling
  // page layout instead of the wizard.
  const wrapper = el("div", {
    "data-qb-wrapper": "inline",
    style: "display:block",
  });
  target.replaceChildren(wrapper);
  const qb = new QuoteBuilder(wrapper, { layoutMode: "builder", ...mountAppearance(target) });
  void qb.boot();
}

// ── Designer live preview ─────────────────────────────────────────────────
// One QuoteBuilder per container, tracked so a re-render tears the previous
// instance down instead of stacking shadow hosts. WeakMap so a container
// removed from the DOM (page unmount) takes its entry with it.
const _previewInstances = new WeakMap<HTMLElement, QuoteBuilder>();

/** Render the REAL form inline into `container` from an INJECTED public
 *  config — the form designer's live preview.
 *
 *  This is the whole point of the entry: the designer used to hand-build a
 *  React mock of this form, and the two drifted on every embed change. Now
 *  there is one renderer. Whatever the storefront draws, the preview draws.
 *
 *    QuoteApp.renderPreview(el, publicConfig, { mode: "builder" })
 *
 *  - `publicConfig` is a PublicShopConfig (the designer computes it client-
 *    side from its unsaved draft via toPublicShopConfig(resolveForm(draft, id))).
 *    Nothing is fetched, cached, or persisted.
 *  - Inert by construction: submit / send-to-cart / product search / bundle
 *    add / cart import all no-op, and the basket store is never touched.
 *  - Re-callable: call it again with a new config to track a live edit. The
 *    previous instance is destroyed and the container repainted — idempotent,
 *    no duplicate nodes. The merchant's current wizard step carries over
 *    unless `opts.activeStep` pins one.
 *  - `opts.showConfirmation: true` seeds render state so the real post-submit
 *    confirmation screen paints (no submit call, no fetch, no storage write).
 *    Mutually exclusive with `opts.activeStep` — confirmation wins.
 *
 *  Returns a handle whose `destroy()` empties the container (React effect
 *  cleanup calls it). */
function renderPreview(
  container: HTMLElement,
  publicConfig: ShopConfig,
  opts: RenderPreviewOptions = {},
): { destroy: () => void } {
  const { layoutMode, formMode, items, activeStep, showConfirmation } = normalizePreviewOptions(opts);

  const prev = _previewInstances.get(container);
  // Carry the merchant's step across a live re-render unless they pinned one.
  // Confirmation mode doesn't touch/consume the carried step — toggling it
  // off returns the merchant to whatever step they were on before.
  const step = activeStep != null ? activeStep : (prev ? prev.previewStep : null);
  if (prev) {
    try { prev.destroy(); } catch { /* teardown must never block a repaint */ }
    _previewInstances.delete(container);
  }

  // Structurally identical to mountInline's wrapper — same display:block
  // defense, same "inline" marker so every CSS rule and DOM probe that keys
  // off it resolves the way it does on a real inline storefront mount. A
  // bespoke marker here would be a third rendering state to keep in sync,
  // which is the drift this entry exists to kill. replaceChildren drops the
  // prior wrapper (and with it, its shadow root).
  const wrapper = el("div", { "data-qb-wrapper": "inline", style: "display:block" });
  container.replaceChildren(wrapper);

  const qb = new QuoteBuilder(wrapper, { preview: true, layoutMode, formMode });
  _previewInstances.set(container, qb);
  qb.bootPreview(publicConfig, items, step, showConfirmation);

  return {
    destroy() {
      try { qb.destroy(); } catch { /* ignore */ }
      if (_previewInstances.get(container) === qb) _previewInstances.delete(container);
      container.replaceChildren();
    },
  };
}

function mountModal(target: HTMLElement) {
  // Replace target with a CTA button. Clicking opens the modal.
  const btnLabel = attr("data-cta-label") || "Request a Quote";
  const accent = ACCENT_OVERRIDE || "#2563eb";
  target.replaceChildren();
  const button = el("button", {
    style: `background:${accent};color:#fff;border:none;border-radius:10px;padding:14px 28px;font-size:15px;font-weight:700;cursor:pointer;font-family:inherit;min-height:48px`,
    onclick: () => openOverlay("modal", { formMode: SCRIPT_FORM_MODE || defaultFormModeFor("modal"), formId: resolveMountFormId(target) }),
  }, btnLabel);
  target.appendChild(button);
}

function mountDrawer(target: HTMLElement | null) {
  // Drawer mode = persistent floating trigger, auto-injected into the
  // bottom-right of every page. Unlike modal mode (where the merchant
  // controls where their CTA lives), drawer mode advertises itself
  // from a fixed corner so the form is always one tap away while the
  // customer browses. This is what makes drawer mode distinct — a
  // drawer without a trigger is invisible.
  //
  // Backwards-compat: if the merchant did pass a target, we also
  // render an inline button there. The floating button still mounts.
  const btnLabel = attr("data-cta-label") || "Request a Quote";
  const accent = ACCENT_OVERRIDE || "#2563eb";
  const corner = (attr("data-trigger-position") || "bottom-right").toLowerCase();
  if (target) {
    target.replaceChildren();
    target.appendChild(el("button", {
      style: `background:${accent};color:#fff;border:none;border-radius:10px;padding:14px 28px;font-size:15px;font-weight:700;cursor:pointer;font-family:inherit;min-height:48px`,
      onclick: () => openOverlay("drawer", { formMode: SCRIPT_FORM_MODE || defaultFormModeFor("drawer"), formId: resolveMountFormId(target) }),
    }, btnLabel));
  }

  // Avoid double-mounting the floating trigger if the embed is loaded
  // twice (e.g. theme bug). One trigger per page.
  if (document.querySelector("[data-qb-floating-trigger]")) return;

  // Corner position. Default bottom-right is the universal
  // ecommerce-chat / cart-drawer location; left variants supported
  // for themes where the right is occupied by an existing widget.
  const cornerStyle = corner === "bottom-left"
    ? "left:18px;right:auto;bottom:calc(18px + env(safe-area-inset-bottom))"
    : corner === "top-right"
    ? "top:calc(18px + env(safe-area-inset-top));right:18px;bottom:auto;left:auto"
    : corner === "top-left"
    ? "top:calc(18px + env(safe-area-inset-top));left:18px;right:auto;bottom:auto"
    : "right:18px;bottom:calc(18px + env(safe-area-inset-bottom))";

  const trigger = el("button", {
    "data-qb-floating-trigger": "true",
    "aria-label": btnLabel,
    style: [
      "position:fixed",
      cornerStyle,
      "z-index:2147483590",
      `background:${accent}`,
      "color:#fff",
      "border:none",
      "border-radius:999px",
      "padding:14px 22px",
      "font-size:14px",
      "font-weight:700",
      "letter-spacing:.01em",
      "cursor:pointer",
      "font-family:inherit",
      "min-height:48px",
      // Soft drop shadow so it lifts off any background; matches
      // the universal floating-chat-bubble visual language.
      "box-shadow:0 10px 25px -6px rgba(15,23,42,.30), 0 4px 10px -4px rgba(15,23,42,.20)",
    ].join(";"),
    onclick: () => openOverlay("drawer", { formMode: SCRIPT_FORM_MODE || defaultFormModeFor("drawer"), formId: SCRIPT_FORM_ID || undefined }),
  }, btnLabel);
  document.body.appendChild(trigger);
}

/** Track the open overlay's QuoteBuilder hook so a second
 *  openRequest call with different prefill can navigate the in-
 *  flight instance instead of stacking a second wrapper. Cleared
 *  on close. */
let activeOverlayWrapper: HTMLElement | null = null;

/** Build the slide-in/modal overlay CHROME (backdrop, panel, scroll-
 *  lock, close paths, floating-widget suppression, Escape/X wiring) and
 *  return the empty content host + a close() fn. Content-agnostic: the
 *  form path (openOverlay) mounts a QuoteBuilder into the host. This is
 *  the FORM's overlay shell — the one review/submit surface the basket
 *  seeds via openBasketReview; there is no separate basket drawer. */
function openOverlayShell(kind: "modal" | "drawer"): { host: HTMLElement; close: () => void } {
  // Re-opening with new prefill: close the existing overlay first
  // so we don't stack two drawers. Cheap teardown — the body-scroll
  // restore + Shadow DOM unmount happen via the wrapper.remove()
  // close path the existing handlers already wire.
  if (activeOverlayWrapper && activeOverlayWrapper.isConnected) {
    try { (activeOverlayWrapper as any).__qbBeforeClose?.(); } catch { /* swallow */ }
    activeOverlayWrapper.remove();
    activeOverlayWrapper = null;
  }
  // Build the overlay in the light DOM (top-level) so it escapes any
  // ancestor stacking contexts in the merchant's theme. The QuoteBuilder
  // attaches its own Shadow DOM inside.
  //
  // Shape (same for both kinds, sized differently): a fixed full-
  // viewport container with a sticky header bar at the top and a
  // scrollable body that hosts the Shadow DOM. The form's own next /
  // back / submit nav is made sticky-to-bottom via CSS inside the
  // Shadow root (see qb-overlay-host class added to qb-root below).
  //
  // Body-scroll lock: iOS Safari leaks touch scrolls through to the
  // background page even with `overflow: hidden`. We capture the
  // previous inline styles and restore them on close so we don't
  // clobber merchant-set values.
  // display:block inline — DO NOT REMOVE. Same defense as the inline
  // mount: themes with `div:empty { display: none }` (default-ish on
  // dawn-derived Shopify themes) hide the wrapper between insertion
  // and the next paint, even though the children (backdrop + panel)
  // are about to attach. Inline style outranks the theme rule and
  // doesn't interact with backdrop/panel positioning since both
  // children are position:fixed anyway.
  const wrapper = el("div", {
    "data-qb-wrapper": kind,
    style: "display:block",
  });
  const docEl = document.documentElement;
  const body = document.body;
  const prev = {
    bodyOverflow: body.style.overflow,
    bodyTouchAction: (body.style as any).touchAction || "",
    bodyPosition: body.style.position,
    bodyTop: body.style.top,
    bodyWidth: body.style.width,
    docOverflow: docEl.style.overflow,
    scrollY: window.scrollY,
  };
  // iOS-safe lock: position:fixed on body prevents the underlying
  // page from scrolling AND prevents the iOS Safari rubber-band
  // scroll-through. We also pin the body to the current scroll
  // offset so opening doesn't visually jump to the top.
  body.style.overflow = "hidden";
  (body.style as any).touchAction = "none";
  body.style.position = "fixed";
  body.style.top = `-${prev.scrollY}px`;
  body.style.width = "100%";
  docEl.style.overflow = "hidden";

  const restoreScroll = () => {
    body.style.overflow = prev.bodyOverflow;
    (body.style as any).touchAction = prev.bodyTouchAction;
    body.style.position = prev.bodyPosition;
    body.style.top = prev.bodyTop;
    body.style.width = prev.bodyWidth;
    docEl.style.overflow = prev.docOverflow;
    window.scrollTo(0, prev.scrollY);
    try { restoreFloatingWidgets(); } catch { /* never block teardown */ }
  };

  // Escape handler — declared here so closeAll() can tear it down
  // on every close path (backdrop / Escape / X button), not just the
  // Escape path. Without this, closing via X or backdrop left the
  // keydown listener bound until the next openOverlay reset it.
  let escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  const closeAll = () => {
    // Manual close path (backdrop / Escape / X) — fire __qbBeforeClose
    // for symmetry with the in-shadow close X path.
    try { (wrapper as any).__qbBeforeClose?.(); } catch { /* never block teardown */ }
    if (escapeHandler) {
      document.removeEventListener("keydown", escapeHandler);
      escapeHandler = null;
    }
    if (activeOverlayWrapper === wrapper) activeOverlayWrapper = null;
    wrapper.remove();
  };
  (wrapper as any).__qbBeforeClose = restoreScroll;

  // Shared host CSS — full viewport flex column with a sticky header
  // and a scrollable body that fills the rest. Differences between
  // modal and drawer are sizing (centered card vs. right-edge panel)
  // and the slide-in animation for drawer mode.
  const isModal = kind === "modal";

  // Backdrop catches outside clicks. NOT a scroll container — that's
  // the body section below. position:fixed inset:0 covers viewport.
  // z-index one below the panel and near the int32 max so it (and the
  // panel above it) sit in front of chat launchers, the Shopify cart
  // bubble, and corner widgets — which the embed's own edge-tab/trigger
  // (z 2147483590–594) also fall behind, so they're covered too. A
  // slightly heavier backdrop on the drawer reads as a proper modal
  // side-drawer overlay.
  const backdrop = el("div", {
    style: `position:fixed;inset:0;background:rgba(15,23,42,${isModal ? ".55" : ".5"});z-index:2147483646`,
    onclick: closeAll,
  });

  // The overlay card / panel itself. Stops click propagation so a
  // click inside doesn't bubble to the backdrop.
  // dvh > vh: vh on mobile measures against the LARGEST possible
  // viewport (browser chrome hidden) and doesn't shrink when the
  // address bar comes back, so the panel runs off the bottom of the
  // screen. dvh tracks the dynamic viewport — exactly what we need.
  const panelStyle = isModal
    // Centered card, anchored from the top with a MAX-HEIGHT cap (fix,
    // form-polish-header-subtotal #3 — was top+bottom anchored, which
    // gave a DEFINITE height regardless of content: a 1-2 item quote
    // left a big empty scroll void above the pinned footer). Dropping
    // `bottom` and capping via `max-height: calc(100dvh - 128px)`
    // (same 32px-top + 96px-chat-widget-clearance budget as before, now
    // an UPPER bound instead of a forced height) lets the panel shrink
    // to its actual content — short lists just fit, no dead space.
    //
    // Long content still can't blow past the viewport: the shadow-DOM
    // flex chain (.qb-overlay .qb-shell: flex:1 1 auto; min-height:0;
    // overflow-y:auto, with the header/footer as flex-shrink:0
    // siblings) is exactly the "auto height, capped, scrollable middle"
    // pattern — once total content hits the max-height cap, the
    // browser compresses qb-shell (not header/footer) and its own
    // overflow-y:auto takes over, keeping Submit reachable. This is
    // the same guarantee the old fixed-height anchor gave, just
    // achieved without forcing the panel taller than short content.
    //
    // On mobile (<= 600px) the inline @media block stretches it to
    // full-viewport via height:100dvh + max-height:100dvh !important
    // (unchanged — full-bleed sheets don't have an "empty gap" the way
    // a floating desktop panel does).
    // Width capped to a consistent ~500px on desktop (was ballooning to
    // 720px on wide screens, stretching the two-line product rows and
    // making the step indicator's width jump between steps) — matches
    // the drawer's width so both surfaces read at the same scale.
    ? "position:fixed;top:32px;left:50%;transform:translateX(-50%);width:min(500px,calc(100vw - 32px));max-height:calc(100dvh - 128px);background:#fff;border:0.5px solid rgba(2,6,23,.10);border-radius:16px;z-index:2147483647;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 24px 60px rgba(2,6,23,.28), 0 4px 12px rgba(2,6,23,.12)"
    // Right-edge drawer — a REAL full-viewport-height drawer (fix,
    // drawer-fullheight-mobile #1): anchored top:0 → bottom:0, fixed
    // ~500px wide, flush to the right edge. This intentionally REVERTS
    // the prior "content-sized" treatment (top:32px + max-height cap):
    // the owner wants a proper side drawer, not a floating popup. Header
    // pins top, footer pins bottom, the middle scrolls — the shadow-DOM
    // flex chain (.qb-overlay flex:1 1 auto → qb-shell scroll → footer
    // flex-shrink:0) does that, and with a DEFINITE panel height (top:0
    // + bottom:0) it's rock-solid. Removing the max-height also fixes a
    // mobile bug: the old cap had NO mobile override on the drawer, so
    // the panel rendered ~128px short of full height, leaking the
    // storefront through below the footer. z-index at the int32 max so
    // the drawer + backdrop cover chat launchers / cart bubbles / corner
    // widgets (belt-and-braces with suppressFloatingWidgets). Left-edge
    // shadow reads as a drawer sliding in from the right. Mobile @media
    // widens to 100vw (already full-height via top/bottom:0).
    : "position:fixed;top:0;right:0;bottom:0;width:min(500px,100vw);background:#fff;border-left:0.5px solid rgba(2,6,23,.10);z-index:2147483647;display:flex;flex-direction:column;overflow:hidden;box-shadow:-12px 0 40px rgba(2,6,23,.28), -2px 0 8px rgba(2,6,23,.12);animation:qb-slide .25s ease-out";
  const panel = el("div", {
    "data-qb-panel": kind,
    style: panelStyle,
    onclick: (e: any) => e.stopPropagation(),
  });

  // Inline media-query stylesheet for mobile sizing + the drawer
  // slide-in keyframe. Scoped to the wrapper via [data-qb-wrapper].
  const mountStyle = el("style", {
    html: `
      @keyframes qb-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
      [data-qb-wrapper] [data-qb-panel="modal"] {
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
      }
      [data-qb-wrapper] [data-qb-panel="drawer"] {
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
      }
      @media (max-width: 600px) {
        [data-qb-wrapper] [data-qb-panel="modal"] {
          /* Reset the desktop top-anchor + max-height cap to a flush
             full-viewport panel. bottom:0 explicit + height:100dvh so
             mobile stays a definite full-bleed sheet regardless of
             content length — the "shrink to content" behavior above
             is desktop-only, full-bleed is still correct on mobile. */
          top: 0 !important; bottom: 0 !important; left: 0 !important;
          transform: none !important;
          width: 100vw !important; height: 100dvh !important;
          max-height: 100dvh !important; border-radius: 0 !important;
        }
        [data-qb-wrapper] [data-qb-panel="drawer"] {
          /* Mobile drawer: widen the desktop full-height side drawer to
             a flush full-SCREEN panel (edge-to-edge, no inner border/
             shadow). Already top:0/bottom:0 from the desktop base, so
             only the width + edge chrome change. The sticky-bottom nav
             inside qb-shell owns its own iOS safe-area padding. */
          width: 100vw !important; left: 0 !important;
          border-left: none !important; box-shadow: none !important;
        }
      }
    `,
  });

  wrapper.appendChild(mountStyle);
  wrapper.appendChild(backdrop);
  wrapper.appendChild(panel);
  document.body.appendChild(wrapper);
  // Hide known floating chat widgets (Shopify Inbox, Intercom, etc.)
  // that would otherwise render on top of the overlay's bottom corner
  // and cover the form footer/Next button. Restored from the same
  // __qbBeforeClose path (restoreScroll) on every close route.
  try { suppressFloatingWidgets(); } catch { /* never block the overlay from opening */ }

  // The QuoteBuilder host element fills the rest of the panel
  // beneath the (Shadow-DOM-internal) header. Setting flex:1 lets
  // the Shadow content size itself naturally; CSS inside the shadow
  // (qb-overlay-host class on qb-root) makes the form area scroll
  // and pins the nav row to the bottom.
  const qbHost = el("div", {
    "data-qb-host": "true",
    style: "flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden",
  });
  panel.appendChild(qbHost);

  // Visible close X — top-right of the panel. Backdrop click + Escape
  // also close, but neither is discoverable without trying. Sized to
  // be a comfortable touch target on mobile (36px) and aligned to the
  // safe-area inset so notched devices don't tuck it behind chrome.
  // Positioned on the panel itself (not the wrapper) so it inherits
  // the panel's z-index + slide-in animation. z-index:1 inside the
  // panel keeps it above the QuoteBuilder host while the panel's
  // overflow:hidden clips it to the panel bounds.
  const closeBtn = el("button", {
    "data-qb-close": "true",
    "aria-label": "Close quote request form",
    type: "button",
    style: [
      "position:absolute",
      "top:calc(env(safe-area-inset-top) + 10px)",
      "right:14px",
      "width:36px;height:36px",
      "background:rgba(255,255,255,.9)",
      "border:1px solid #e2e8f0",
      "border-radius:50%",
      "color:#475569",
      "font-size:18px;line-height:1",
      "cursor:pointer",
      "display:flex;align-items:center;justify-content:center",
      "box-shadow:0 1px 3px rgba(15,23,42,.08)",
      "z-index:1",
      "padding:0",
    ].join(";"),
    html: "&times;",
    onclick: closeAll,
  });
  panel.appendChild(closeBtn);

  // Cache the open wrapper so a subsequent openOverlay call can
  // close it before opening a new one (re-prefill from a different
  // product page without closing first).
  activeOverlayWrapper = wrapper;

  escapeHandler = function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") closeAll();
  };
  document.addEventListener("keydown", escapeHandler);
  return { host: qbHost, close: closeAll };
}

/** Open the request FORM in an overlay — mounts a QuoteBuilder into a
 *  fresh overlay shell. Unchanged behavior for every existing caller;
 *  `onSubmitSuccess` is the only new opt (basket review-and-submit sets
 *  it to clear the basket on success). */
function openOverlay(
  kind: "modal" | "drawer",
  opts: { prefill?: PrefillOpts; formMode?: FormMode; accentColor?: string; formScale?: number; formId?: string; onSubmitSuccess?: () => void } = {},
) {
  const { host } = openOverlayShell(kind);
  const qb = new QuoteBuilder(host, {
    prefill: opts.prefill,
    formMode: opts.formMode,
    accentColor: opts.accentColor,
    formScale: opts.formScale,
    formId: opts.formId,
    onSubmitSuccess: opts.onSubmitSuccess,
  });
  void qb.boot();
}

// ── Quote Basket wiring ─────────────────────────────────────────────
// The standalone, always-on "quote cart" and unified item source of
// truth. The store lives in src/embed/basket.ts (its own localStorage
// key, decoupled from the form draft); this section is the embed-side
// glue: config resolution, the Add-to-Quote handler, the "View Quote"
// edge tab, and the seed-the-form hand-off (openBasketReview → the
// embedded multi-step form, which is the one review/submit surface).
// There is no standalone basket drawer, floating bubble, or cart-adjacent
// count — the only persistent chrome is the slim edge tab.

interface ResolvedBasket {
  enabled: boolean;
  /** "View Quote" edge-tab label (the count is appended, e.g.
   *  "View Quote (2)"). */
  tabLabel: string;
  /** Edge-tab color — empty when unset (mount falls back to the accent). */
  tabColor: string;
  /** Viewport edge the tab affixes to. */
  tabSide: "right" | "left";
  /** Whether to mount the edge tab at all (merchant can disable it). */
  showTab: boolean;
}
function resolveBasketConfig(cfg: ShopConfig | null | undefined): ResolvedBasket {
  const b = cfg?.basket || {};
  return {
    enabled: b.enabled === true,
    tabLabel: (typeof b.tab_label === "string" && b.tab_label.trim()) ? b.tab_label.trim() : "View Quote",
    tabColor: (typeof b.tab_color === "string" && b.tab_color.trim()) ? b.tab_color.trim() : "",
    tabSide: b.tab_side === "left" ? "left" : "right",
    // Default ON — the tab is the re-entry into the quote.
    showTab: b.show_tab !== false,
  };
}

let _basket: BasketStore | null = null;
/** Lazily create the one basket store for this page. */
function getBasket(): BasketStore {
  if (!_basket) _basket = new BasketStore({ slug: SHOP_SLUG });
  return _basket;
}

/** Latest resolved basket.enabled, kept in sync by activateBasketAndButton
 *  (cache phase + network phase). Read by the public addToQuote() so it
 *  can degrade gracefully before a basket store even exists. */
let _basketEnabled = false;

/** Fetch full product/variant info from the same public endpoint
 *  applyPrefill uses, so a basket line carries title/price/image, not
 *  just an id. Returns null on any failure (caller degrades to a
 *  minimal line). */
async function fetchProductInfo(variantId: string, productId: string): Promise<CartItem | null> {
  const shop = SHOP_SLUG;
  const id = variantId || productId;
  if (!shop || !id) return null;
  const type = variantId ? "variant" : "product";
  try {
    const url = `${API_ORIGIN}/api/public/product/${encodeURIComponent(id)}?shop=${encodeURIComponent(shop)}&type=${type}`;
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) return null;
    const it = await r.json();
    if (!it || !it.variantId) return null;
    return it as CartItem;
  } catch {
    return null;
  }
}

/** Add-to-Quote sink wired into the product-button injector. Enriches
 *  the click payload (variant + properties) into a full basket line and
 *  adds it, then shows a brief confirmation. Never throws. */
async function handleAddToQuote(payload: AddToQuotePayload): Promise<void> {
  try {
    const variantId = payload.variantId ? String(payload.variantId).trim() : "";
    const productId = payload.productId ? String(payload.productId).trim() : "";
    if (!variantId && !productId) return;
    // Chokepoint #2: payload.properties already carries auto-place's own
    // click-time capture (add_to_quote mode) or a caller's explicit
    // properties (api.addToQuote's own param) — either wins untouched.
    // Absent → fresh page capture, covering the app-block/manual-snippet
    // "Add to quote basket" door that never builds its own properties.
    const properties = normalizeLineProperties(
      resolveSeedProperties({ hasProductContext: true, existing: payload.properties, ...currentWinDoc() }),
    );
    const info = await fetchProductInfo(variantId, productId);
    const vid = info && info.variantId ? String(info.variantId) : variantId;
    if (!vid) return; // no resolvable variant — nothing safe to dedup on
    const item: BasketItem = info && info.variantId
      ? {
          variantId: vid,
          productId: info.productId ? String(info.productId) : (productId || undefined),
          title: info.title || "Product",
          variantTitle: info.variantTitle || undefined,
          sku: info.sku || undefined,
          qty: 1,
          price: info.price != null ? String(info.price) : undefined,
          vendor: info.vendor || undefined,
          imageUrl: info.imageUrl ?? null,
          ...(properties.length ? { properties } : {}),
        }
      : {
          variantId: vid,
          productId: productId || undefined,
          title: "Product",
          qty: 1,
          ...(properties.length ? { properties } : {}),
        };
    getBasket().add(item);
    showBasketToast("Added to quote", { label: "Review quote →", onClick: () => openBasketReview() });
  } catch {
    /* a broken add must never surface as an unhandled error */
  }
}

const BASKET_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

let _basketToastTimer: any = null;
/** Transient "Added to quote" confirmation, bottom-center. Re-used
 *  (single node) so rapid adds don't stack toasts. An optional action
 *  renders a clickable link (e.g. "Review quote →") beside the message —
 *  the immediate re-entry into the seeded form, and the safety net when
 *  a theme has no detectable cart icon for the cart-adjacent count. The
 *  pill itself stays pointer-events:none (never blocks page clicks); only
 *  the link is clickable. A toast carrying an action lingers longer so
 *  the customer has time to click. */
function showBasketToast(message: string, action?: { label: string; onClick: () => void }): void {
  if (typeof document === "undefined" || !document.body) return;
  try {
    let toast = document.querySelector("[data-qs-basket-toast]") as HTMLElement | null;
    if (!toast) {
      toast = el("div", {
        "data-qs-basket-toast": "1",
        style: [
          "position:fixed",
          "left:50%",
          "bottom:84px",
          "transform:translateX(-50%)",
          "z-index:2147483595",
          "display:flex",
          "align-items:center",
          "gap:12px",
          "background:#0f172a",
          "color:#fff",
          "padding:10px 18px",
          "border-radius:999px",
          "font-size:13px",
          "font-weight:600",
          `font-family:${BASKET_FONT}`,
          "box-shadow:0 8px 24px rgba(15,23,42,.28)",
          "pointer-events:none",
          "opacity:0",
          "transition:opacity .18s ease",
        ].join(";"),
      });
      document.body.appendChild(toast);
    }
    // Rebuild children each show — a prior toast may have carried an
    // action link this one doesn't (or vice-versa).
    toast.replaceChildren();
    toast.appendChild(el("span", {}, message));
    if (action) {
      const link = el("button", {
        type: "button",
        // Only the link takes pointer events; the pill stays inert.
        style: [
          "pointer-events:auto",
          "background:none",
          "border:none",
          "padding:0",
          "margin:0",
          "color:#93c5fd",
          "font-size:13px",
          "font-weight:700",
          `font-family:${BASKET_FONT}`,
          "cursor:pointer",
          "white-space:nowrap",
        ].join(";"),
        onclick: () => { try { action.onClick(); } catch { /* swallow */ } },
      }, action.label);
      toast.appendChild(link);
    }
    // Force a reflow-free next-tick so the opacity transition runs even
    // on the first show.
    toast.style.opacity = "1";
    if (_basketToastTimer) clearTimeout(_basketToastTimer);
    _basketToastTimer = setTimeout(() => {
      try { if (toast) toast.style.opacity = "0"; } catch { /* swallow */ }
    }, action ? 5000 : 2000);
  } catch {
    /* toast is best-effort — never block the add */
  }
}

/** THE canonical "open the quote in the embedded form" action. Under the
 *  unified model the multi-step form seeds its items straight from the
 *  basket store on mount (the single source of truth) — so this just opens
 *  the full form with NO prefill and lets seed-from-basket populate it.
 *  (Passing a lineItems prefill would round-trip through the inventory-less
 *  prefill shape and strip the badge/gate fields.) Clears the basket on
 *  submit success (redundant with the submit path's own clear, kept as a
 *  belt-and-braces). Called from every re-entry point: the "View Quote"
 *  edge tab and the add toast's "Review quote →". openOverlay's re-open
 *  guard tears down any active overlay first. NOTE: the "drawer" here is
 *  the FORM's own overlay-shell sizing (openOverlayShell), not a basket
 *  drawer. */
function openBasketReview(): void {
  if (!getBasket().lineCount()) return;
  openOverlay("drawer", {
    formMode: "full",
    onSubmitSuccess: () => { try { getBasket().clear(); } catch { /* swallow */ } },
  });
}

// ── "View Quote" edge tab ───────────────────────────────────────────
// The persistent re-entry into the quote: a slim tab affixed to a
// viewport edge (like a "Show cart" tab), shown ONLY when the quote is
// non-empty. Click opens the multi-step form seeded from the basket
// (openBasketReview). It's anchored to the VIEWPORT (position:fixed), not
// the theme's DOM, so — unlike the old cart-adjacent count — it needs no
// header anchor and no re-assert observer: it always has a home. The
// label may show the count ("View Quote (2)"). A merchant can hide it
// (show_tab:false); re-entry then falls back to the toast's "Review
// quote →" link.
const EDGE_TAB_ATTR = "data-qs-quote-tab";
// Exposed as a DOM attribute (not just read from closure) so the mobile
// media-query stylesheet below can flip the rotate(180deg) mirror per side
// without needing two separate style blocks.
const EDGE_TAB_SIDE_ATTR = "data-qs-quote-tab-side";
let _edgeTabUnsub: (() => void) | null = null;

function buildEdgeTabEl(color: string, side: "right" | "left"): HTMLElement {
  const isLeft = side === "left";
  return el("button", {
    [EDGE_TAB_ATTR]: "1",
    [EDGE_TAB_SIDE_ATTR]: side,
    type: "button",
    style: [
      "position:fixed",
      "top:50%",
      isLeft ? "left:0" : "right:0",
      // Vertical label (writing-mode below) renders top→bottom, rotated
      // clockwise — correct as-is for a right-edge tab. A left-edge tab
      // needs the mirror image (rounded corners + reading direction both
      // flip), which a single rotate(180deg) gives for free: it's a point
      // reflection, so it preserves the box's bounding rect (no width/
      // height swap like a 90° rotate would) and — paired with the SAME
      // border-radius value used for the right side below — turns the
      // corners rounded-away-from-edge automatically.
      isLeft ? "transform:translateY(-50%) rotate(180deg)" : "transform:translateY(-50%)",
      "z-index:2147483594",
      "display:none",            // shown by renderEdgeTab when count > 0
      "align-items:center",
      "justify-content:center",
      "gap:6px",
      "margin:0",
      // Padding swapped from the old horizontal-pill layout: more room
      // along the text run (now vertical, top/bottom) and slim across it
      // (now the tab's on-screen width).
      "padding:13px 9px",
      `background:${color}`,
      "color:#fff",
      "border:none",
      "border-radius:10px 0 0 10px",
      "font-size:13px",
      "font-weight:700",
      "line-height:1",
      "letter-spacing:.01em",
      `font-family:${BASKET_FONT}`,
      "cursor:pointer",
      "writing-mode:vertical-rl",
      "text-orientation:mixed",
      "white-space:nowrap",
      "box-shadow:0 4px 14px rgba(15,23,42,.20)",
    ].join(";"),
  });
}

function renderEdgeTab(node: HTMLElement, label: string, count: number): void {
  node.textContent = count > 0 ? `${label} (${count})` : label;
  // Only present when the quote is non-empty — an empty-quote tab would be
  // dead chrome.
  node.style.display = count > 0 ? "inline-flex" : "none";
}

/** Mount the "View Quote" edge tab + wire the live-count subscription for
 *  show/hide + label updates. Idempotent across the cache→network boot
 *  phases (single shared node + subscription). show_tab:false tears it
 *  down (the merchant opted out). accentColor is the shop's resolved
 *  `config.accent_color` — the settings UI says the tab "defaults to your
 *  brand accent color," so an unset tab_color must fall back to it (same
 *  accentOverride → ACCENT_OVERRIDE → config.accent_color → slate-blue
 *  chain used everywhere else in the embed), not straight to the
 *  hardcoded blue last-ditch default. */
function mountEdgeTab(basket: BasketStore, rb: ResolvedBasket, accentColor?: string): void {
  if (typeof document === "undefined" || !document.body) return;
  if (!rb.showTab) { removeEdgeTab(); return; }
  const color = rb.tabColor || ACCENT_OVERRIDE || accentColor || "#2563eb";
  // Mobile treatment (fix, drawer-fullheight-mobile #4): the desktop
  // side-tab is vertically centered on the edge (top:50%), which on a
  // phone sits right over the product page's "Add to cart" / buy
  // buttons. A one-time media-query stylesheet drops it out of the
  // vertical center to the lower edge — above the typical bottom
  // chat/FAB/sticky-ATC-bar zone, clear of the centered CTA — and
  // slims it down. Stays an edge tab (keeps its inline left/right side);
  // only the vertical anchor + size change. Injected once; inert when
  // no tab is mounted, so no teardown needed.
  const TAB_STYLE_ID = "qs-quote-tab-style";
  if (!document.getElementById(TAB_STYLE_ID)) {
    try {
      const st = document.createElement("style");
      st.id = TAB_STYLE_ID;
      st.textContent =
        `@media (max-width:600px){` +
        `[${EDGE_TAB_ATTR}]{` +
        `top:auto !important;` +
        `bottom:calc(84px + env(safe-area-inset-bottom)) !important;` +
        `padding:8px 11px !important;font-size:12px !important;}` +
        // Drop the vertical-centering translateY on mobile (top is now
        // auto/bottom-anchored) but keep the left side's 180° mirror —
        // otherwise the left tab's text would read backwards / its
        // rounded corners would flip onto the wrong edge on mobile.
        `[${EDGE_TAB_ATTR}][${EDGE_TAB_SIDE_ATTR}="right"]{transform:none !important;}` +
        `[${EDGE_TAB_ATTR}][${EDGE_TAB_SIDE_ATTR}="left"]{transform:rotate(180deg) !important;}` +
        `}`;
      (document.head || document.body).appendChild(st);
    } catch { /* never block the tab from mounting on a hostile head */ }
  }
  let node = document.querySelector(`[${EDGE_TAB_ATTR}]`) as HTMLElement | null;
  if (!node || !node.isConnected) {
    node = buildEdgeTabEl(color, rb.tabSide);
    node.addEventListener("click", (e: Event) => {
      try { e.preventDefault(); e.stopPropagation(); } catch { /* swallow */ }
      openBasketReview();
    });
    try { document.body.appendChild(node); } catch { return; }
  }
  const tab = node;
  const paint = () => { try { renderEdgeTab(tab, rb.tabLabel, basket.count()); } catch { /* swallow */ } };
  paint();
  if (!_edgeTabUnsub) _edgeTabUnsub = basket.subscribe(() => paint());
}

/** Tear down the edge tab — drop the live subscription and remove the node
 *  (used when a network-phase config disagrees with a stale enabled cache,
 *  or when show_tab is turned off). */
function removeEdgeTab(): void {
  try { _edgeTabUnsub && _edgeTabUnsub(); } catch { /* ignore */ }
  _edgeTabUnsub = null;
  if (typeof document === "undefined") return;
  try {
    const n = document.querySelector(`[${EDGE_TAB_ATTR}]`);
    if (n && n.parentNode) n.parentNode.removeChild(n);
  } catch {
    /* ignore */
  }
}

// ── Collection-card Add-to-Quote (Door 2) ──────────────────────────
// The Hide-ATC catalog rule flags matched collection/search cards with
// data-qs-hide-atc-target="1" (catalog-rules.ts) and hides their ATC.
// Where ATC is hidden we inject an Add-to-Quote control that feeds the
// same basket sink as the product-page button. Gated on basket.enabled;
// the target attr is itself the catalog-config gate (no Hide-ATC rule →
// no target cards → no buttons), so a basket-off OR hide-atc-off shop's
// collection pages are byte-identical to before. Idempotent per card +
// a debounced observer re-asserts over theme-AJAX pagination / infinite
// scroll and the async per-card attr set catalog-rules performs.
let _cardAtqObserver: any = null;
let _cardAtqScheduled = false;

function scanCollectionCardAtq(label: string, bgColor: string, textColor: string): void {
  if (typeof document === "undefined") return;
  let cards: any;
  try {
    cards = document.querySelectorAll('[data-qs-hide-atc-target="1"]');
  } catch {
    return;
  }
  if (!cards || typeof cards.forEach !== "function") return;
  cards.forEach((card: any) => {
    try {
      injectCardAddToQuote({
        card,
        doc: document as any,
        label,
        bgColor,
        textColor,
        addToQuote: (p) => { void handleAddToQuote(p); },
      });
    } catch {
      /* never throw from the scan */
    }
  });
}

function activateCollectionCardAtq(cfg: ShopConfig): void {
  if (typeof document === "undefined" || !document.body) return;
  const resolved = resolveProductButtonConfig(cfg.product_button as any, cfg.accent_color);
  const label =
    (typeof cfg.product_button?.label === "string" && cfg.product_button.label.trim())
      ? cfg.product_button.label.trim()
      : "Add to Quote";
  const bgColor = resolved.bgColor;
  const textColor = resolved.textColor;

  scanCollectionCardAtq(label, bgColor, textColor);

  if (_cardAtqObserver) return; // single shared observer
  const Ctor = typeof MutationObserver !== "undefined" ? MutationObserver : null;
  if (!Ctor) return;
  try {
    _cardAtqObserver = new Ctor(() => {
      // Debounce a burst (theme AJAX injects many cards / catalog-rules
      // stamps target attrs async) into a single re-assert; the inject
      // is idempotent so a spurious extra run is harmless.
      if (_cardAtqScheduled) return;
      _cardAtqScheduled = true;
      const run = () => {
        _cardAtqScheduled = false;
        try { scanCollectionCardAtq(label, bgColor, textColor); } catch { /* swallow */ }
      };
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
      else if (typeof setTimeout !== "undefined") setTimeout(run, 0);
      else run();
    });
    // childList catches AJAX'd-in cards; the attributeFilter catches the
    // data-qs-hide-atc-target attr catalog-rules sets asynchronously
    // (after its per-card /products/<handle>.js fetch) AFTER our first
    // scan already ran — without it those late-flagged cards would never
    // get a button until some unrelated childList mutation fired.
    _cardAtqObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-qs-hide-atc-target"],
    });
  } catch {
    /* observer setup failed — the one-shot scan above still stands */
  }
}

function teardownCollectionCardAtq(): void {
  try {
    _cardAtqObserver && _cardAtqObserver.disconnect && _cardAtqObserver.disconnect();
  } catch {
    /* ignore */
  }
  _cardAtqObserver = null;
  _cardAtqScheduled = false;
  if (typeof document === "undefined") return;
  try {
    const nodes = document.querySelectorAll("." + CARD_ATQ_BTN_CLASS);
    nodes.forEach((n: any) => {
      try { if (n && n.parentNode) n.parentNode.removeChild(n); } catch { /* ignore */ }
    });
  } catch {
    /* ignore */
  }
}

/** Cache + network phases in boot() both call this. Activates the
 *  product-page button, and — when basket mode is enabled — creates the
 *  basket store, mounts the cart-adjacent "Quote (N)" count, wires the
 *  Add-to-Quote sink so `product_button.mode="add_to_quote"` stages items
 *  instead of opening the form, and injects Add-to-Quote onto Hide-ATC'd
 *  collection cards. Basket OFF → today's behavior exactly (no count, ATQ
 *  sink absent so any stray add_to_quote config degrades to the form
 *  path, no collection-card buttons). */
function activateBasketAndButton(cfg: ShopConfig): void {
  const rb = resolveBasketConfig(cfg);
  _basketEnabled = rb.enabled;
  if (rb.enabled) {
    const basket = getBasket();
    mountEdgeTab(basket, rb, cfg.accent_color);
    autoPlaceProductButton(cfg as any, { addToQuote: (p) => { void handleAddToQuote(p); } });
    activateCollectionCardAtq(cfg);
  } else {
    removeEdgeTab();
    autoPlaceProductButton(cfg as any);
    teardownCollectionCardAtq();
  }
}

/** Read the live selected variant id off the current product page and
 *  resolve it against the id the theme button passed in. URL ?variant=
 *  wins → the product form's [name="id"] (kept in sync by the theme's
 *  variant picker) → the passed id. DOM-free precedence logic lives in
 *  resolveLiveVariantId (variant-resolver.ts); this only feeds it the
 *  two live page sources. SSR-safe: with no document, returns the
 *  passed id unchanged. */
function readLiveSelectedVariantId(passedVariantId?: string | number): string {
  if (typeof document === "undefined") {
    return passedVariantId == null ? "" : String(passedVariantId).trim();
  }
  const search = typeof location !== "undefined" ? location.search : "";
  // Primary: the add-to-cart form's selected-variant input. Fallback:
  // a <product-form> custom element whose form action didn't match
  // (some Online Store 2.0 themes wrap the input differently).
  const formEl =
    document.querySelector('form[action*="/cart/add"] [name="id"]') ||
    document.querySelector('product-form [name="id"]');
  const formVariantId =
    formEl && "value" in formEl ? (formEl as HTMLInputElement).value : "";
  return resolveLiveVariantId({ search, formVariantId, passedVariantId });
}

function boot() {
  if (typeof document === "undefined") return;
  // Double-load guard. Theme app extension's app-embed block AND a
  // legacy manual <script> tag in theme.liquid can both ship on the
  // same storefront during the migration window. Without this,
  // both copies would mount their own modal/drawer overlay,
  // double-fire opens, and double-register the global. Bail when
  // we've already booted — preserves the FIRST install's
  // configuration verbatim.
  if ((window as any).QuoteBuilder?.version) return;

  // Expose the global API FIRST — independent of whether there's an
  // inline auto-mount target on this page. Theme app extension blocks
  // (Request Quote button, Floating Quote CTA) call
  // `window.QuoteApp.openRequest()` to surface the drawer on demand;
  // they don't need (or expect) `#quote-builder` to exist on the page.
  // Without this re-ordering, embed.js used to bail at the target
  // check on pages without `#quote-builder` and never expose the
  // global, leaving every theme-block click a silent no-op.
  //
  // Both `QuoteBuilder` and `QuoteApp` aliases point at the same
  // object so block code that uses either name keeps working.
  const api = {
    version: VERSION,
    // Surfaced so theme blocks (cart-page button, etc.) can hit our
    // public endpoints from inline onclick handlers without baking in
    // a hardcoded host.
    apiOrigin: API_ORIGIN,
    shopSlug: SHOP_SLUG,
    open: (kind: "modal" | "drawer" = "modal") => openOverlay(kind),
    openRequest: (opts?: {
      productId?: string | number;
      variantId?: string | number;
      lineItems?: PrefillLineItem[];
      formMode?: FormMode | "quick";
      displayMode?: "drawer" | "modal";
      /** Form appearance from the launcher theme block (#4 color,
       *  #3 size). The Request/cart/floating button blocks pass their
       *  own "form color" + "form size" settings here so the opened
       *  overlay matches what the merchant configured on that block,
       *  independent of the shop's brand accent. */
      formColor?: string;
      formScale?: number | string;
      /** Multi-form (docs/MULTI_FORM.md §6): which named form to load.
       *  Phase 2's theme-block `data-qa-form-id` setting passes this;
       *  this lane plumbs it end-to-end (→ `&form=` fetch → `form_id`
       *  POST). Unset → the shop's default form (unchanged). */
      formId?: string;
      /** Explicit line-item properties for the seeded product/variant —
       *  e.g. a manual snippet that already knows its own custom
       *  options, or the auto-place button's own click-time capture
       *  (product-button.ts) threaded straight through. Always wins
       *  untouched over a fresh page capture (see resolveOpenRequestProperties
       *  below) — this is also what keeps auto-place's own capture from
       *  being captured a second time here. Absent → captured fresh from
       *  the page (APO v2 selections + `properties[...]` form inputs)
       *  whenever a product/variant id is present, covering doors that
       *  never build their own properties array (app block, manual
       *  `openRequest()` snippets). */
      properties?: Array<{ name: string; value: string }>;
    }) => {
      // Theme app extension's product-page button passes the product
      // the customer was viewing; we plumb it through openOverlay →
      // new QuoteBuilder({ prefill, formMode }) → applyPrefill() so
      // the form lands with that variant as the first line item.
      // variantId wins over productId (more specific); both may be
      // empty for the floating CTA / non-product-page calls — those
      // just open the overlay empty.
      //
      // formMode "quick_expandable" (or its block-facing alias
      // "quick") gates on quick_form.enabled + a prefilled product
      // before rendering the single-screen form; anything else
      // (including unset) falls through to the full 4-step wizard.
      //
      // displayMode chooses between the slide-in drawer (default)
      // and the centered modal — same form content either way, only
      // the outer panel sizing/animation differs.
      // Prefill resolution: lineItems (multi-item, cart block) wins
      // over single-item (productId/variantId). Either may be empty
      // for non-product / non-cart callers — those just open empty.
      //
      // Single-product path only: resolve the LIVE selected variant at
      // click time and prefer it over the stale id the theme button
      // stamped at Liquid render time (see variant-resolver.ts). The
      // lineItems (cart) path and the no-product floating-CTA path
      // (neither id passed → undefined) are left untouched.
      const isLineItemsPrefill = Array.isArray(opts?.lineItems) && opts!.lineItems!.length > 0;
      // Option capture (APO v2 + `properties[...]` form inputs) — one
      // shared read for every door that reaches openRequest (app block,
      // manual snippets, auto-place's form mode). Skipped entirely for
      // the lineItems (cart) path — those items already carry their own
      // per-line properties from /cart.js — and for a bare no-product
      // open, matching "no product context → no capture".
      const properties = isLineItemsPrefill ? [] : resolveOpenRequestProperties(opts);
      const prefill: PrefillOpts | undefined =
        isLineItemsPrefill
          ? { lineItems: opts!.lineItems }
          : (opts?.variantId || opts?.productId)
            ? {
                variantId: readLiveSelectedVariantId(opts?.variantId),
                productId: opts?.productId,
                ...(properties.length ? { properties } : {}),
              }
            : undefined;
      const rawMode = opts?.formMode;
      const explicitFormMode: FormMode | null =
        rawMode === "quick" || rawMode === "quick_expandable"
          ? "quick_expandable"
          : rawMode === "full"
            ? "full"
            : null;
      const displayMode: "drawer" | "modal" =
        opts?.displayMode === "modal" ? "modal" : "drawer";
      // Precedence: explicit call > script tag data-form-mode > per-kind default
      const formMode: FormMode =
        explicitFormMode || SCRIPT_FORM_MODE || defaultFormModeFor(displayMode);
      const accentColor = typeof opts?.formColor === "string" ? opts.formColor.trim() : "";
      const formScale = opts?.formScale != null && String(opts.formScale).trim() !== ""
        ? clampFormScale(opts.formScale)
        : undefined;
      // Precedence: explicit call formId > script-tag data-form-id >
      // shop default. Only a slug-shaped value threads through; the
      // server's resolveForm() is the final authority (unknown → default).
      const rawFormId = typeof opts?.formId === "string" ? opts.formId.trim().toLowerCase() : "";
      const formId =
        (/^[a-z0-9][a-z0-9-]*$/.test(rawFormId) && rawFormId !== "default" ? rawFormId : "")
        || SCRIPT_FORM_ID
        || undefined;
      openOverlay(displayMode, { prefill, formMode, accentColor: accentColor || undefined, formScale, formId });
    },
    // Public surface for the theme app block's "Add to quote basket"
    // click_action (and any other on-page caller). Reuses the exact
    // internal path the auto-place button's `add_to_quote` mode uses:
    // resolve the LIVE selected variant (same precedence as
    // openRequest), enrich it via handleAddToQuote's product-info
    // fetch, then basket.add. Degrades to openRequest (the form path)
    // when the basket isn't enabled — mirrors performInject's
    // fallback for a misconfigured ATQ button, so a block left
    // pointed at "Add to quote basket" on a basket-disabled shop
    // still does something useful instead of a silent no-op.
    addToQuote: (opts?: {
      productId?: string | number;
      variantId?: string | number;
      properties?: Array<{ name: string; value: string }>;
    }) => {
      const variantId = readLiveSelectedVariantId(opts?.variantId);
      const productId = opts?.productId != null ? String(opts.productId).trim() : "";
      if (!_basketEnabled) {
        // Degrading to the form path must not drop an explicit
        // properties array the caller already passed — openRequest()
        // treats it exactly the same as its own opts.properties.
        api.openRequest({ productId: opts?.productId, variantId: opts?.variantId, properties: opts?.properties });
        return;
      }
      void handleAddToQuote({
        variantId: variantId || undefined,
        productId: productId || undefined,
        properties: opts?.properties,
      });
    },
    /** Admin-only: render the real form inline from an injected config. See
     *  renderPreview() above. Never used by a storefront. */
    renderPreview,
  };
  (window as any).QuoteBuilder = api;
  (window as any).QuoteApp = api;

  // Admin form designer (`data-preview="1"`): the global API above is all it
  // wants. Everything below is storefront-only — config + catalog-rule
  // fetches, the auto-place button, basket activation, the auto-mount scan,
  // and a body-wide MutationObserver. None of it belongs in the admin.
  if (PREVIEW_ONLY) return;

  // ── Catalog Rules activation (Hide Price + Hide ATC) ────────────
  // Two-phase activation so neither stale-cache nor cold-cache
  // drops the rule on the floor:
  //
  //   Phase 1 (sync): apply from cache if present — fast paint,
  //   minimizes the price-flash on warm sessions.
  //
  //   Phase 2 (async, ALWAYS runs): re-fetch from the network +
  //   re-apply. The cache is for speed; the network response is
  //   authoritative. Without phase 2, a merchant who changed
  //   their settings within the 5-min cache window would see no
  //   effect until the TTL expired — and the original
  //   reported-bug was a stale-cache miss where Phase 1's
  //   v1.0-shaped cached config no-op'd while v1.1 was already
  //   saved server-side. The cache-key prefix in
  //   src/lib/embed-shop-config.ts was also bumped v1→v2 to
  //   force one fresh fetch on every customer's first post-deploy
  //   visit; Phase 2 covers everything after.
  //
  // The activator runs both Hide Price + Hide ATC independently
  // via the per-feature rule blocks in shop_config (see
  // src/lib/storefront-product-matcher.ts for the AND-combined
  // vendor + tag matching logic).
  //
  // Fire-and-forget — failure leaves the storefront in its default
  // (prices visible) state, which is the safe fallback. Wrapped in
  // try/catch so a config-fetch glitch can't kill the rest of the
  // embed boot.
  try {
    const cached = readCachedShopConfig(SHOP_SLUG);
    if (cached?.config) {
      applyCatalogRules(cached.config as any);
      // Product-page auto-place button + Quote Basket. Both gated in
      // config (auto_place_enabled / basket.enabled — off by default) so
      // an un-opted-in merchant sees zero storefront change. Idempotent +
      // never-throws; the re-assert observer survives Swym / variant
      // swaps. See src/embed/product-button.ts + src/embed/basket.ts.
      activateBasketAndButton(cached.config as any);
    }
    // Phase 2 always — even on cache hit. Both activators are
    // idempotent (catalog: style-tag replace + html-attr no-op;
    // product button: RECONCILES in place — Lane 2/C3 — rather than
    // skipping when one already exists in the form), and the network
    // response is authoritative — a merchant who just flipped a style
    // toggle sees it apply without waiting for the cache TTL (this
    // phase-2 call is the actual staleness fix; no cache invalidation
    // needed), and a toggle-OFF tears the button back down.
    void fetchEmbedShopConfigWithVersion({ apiOrigin: API_ORIGIN, shopSlug: SHOP_SLUG })
      .then(r => {
        if (r?.config) {
          applyCatalogRules(r.config as any);
          activateBasketAndButton(r.config as any);
        }
      })
      .catch(() => { /* leave catalog + button as-is on fetch error */ });
  } catch { /* never let catalog-rule / auto-place errors break the embed */ }

  const target = document.querySelector(TARGET_SELECTOR) as HTMLElement | null;
  // Drawer mode is target-optional — the floating trigger button
  // auto-mounts to document.body regardless. Inline + modal still
  // need a target (inline IS the target, modal needs a place for
  // its CTA button). When neither auto-mount applies, we still
  // exposed the API above so programmatic callers work — AND we
  // still scan for theme-block-emitted mount markers below.
  if (target) {
    if (MODE === "modal") mountModal(target);
    else if (MODE === "drawer") mountDrawer(target);
    else if (MODE === "builder") mountBuilder(target);
    else mountInline(target);
  } else if (MODE === "drawer") {
    mountDrawer(null);
  }

  // Theme-block-emitted mount targets. The inline-quote-builder
  // app block (and any future inline section block) emits a marker
  // div carrying its own `data-quotesense-mount` mode — the bundle
  // mounts each one independently of the script-tag MODE.
  //
  // Why this is its own pass:
  //   1. The script-tag MODE comes from whichever loads embed.js
  //      first (app-embed.liquid in the canonical install). Boot
  //      guard short-circuits any later loads, so a per-block
  //      script tag can't override MODE.
  //   2. Multiple inline blocks on one page each need their own
  //      mount (a single TARGET_SELECTOR can't address them all).
  //   3. The theme editor re-injects sections on save without a
  //      full page reload — MutationObserver below picks those up
  //      so the merchant sees their changes immediately.
  scanInlineMountTargets();
  observeInlineMountTargets();
}

const MOUNT_MARKER_ATTR = "data-quotesense-mount";

/** Find every `[data-quotesense-mount]` element on the page and
 *  mount a QuoteBuilder into each one. Idempotent — sets
 *  `data-qb-mounted` on the host so subsequent scans skip
 *  already-mounted nodes. */
function scanInlineMountTargets() {
  if (typeof document === "undefined") return;
  const nodes = document.querySelectorAll<HTMLElement>(`[${MOUNT_MARKER_ATTR}]`);
  nodes.forEach(el => {
    if (el.hasAttribute("data-qb-mounted")) return;
    el.setAttribute("data-qb-mounted", "1");
    const mode = (el.getAttribute(MOUNT_MARKER_ATTR) || "inline").toLowerCase();
    if (mode === "builder") mountBuilder(el);
    else mountInline(el);
  });
}

let _inlineMountObserver: MutationObserver | null = null;
/** Re-scan when the DOM changes so theme-editor section reloads +
 *  late-arriving sections pick up the embed. Single shared observer;
 *  idempotent setup. */
function observeInlineMountTargets() {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
  if (_inlineMountObserver) return;
  _inlineMountObserver = new MutationObserver(() => scanInlineMountTargets());
  _inlineMountObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
